// frontend/src/services/api.ts

import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';

// ==================== TYPES ====================
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  phone?: string;
  avatar?: string;
  profile_photo?: string;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  newsletter_subscribed?: boolean;
  language?: string;
  currency?: string;
  designation?: string;
  department?: string;
  wallet?: Wallet;
  employee_code?: string;
  must_change_password?: boolean;
}

export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone: string;
}

export interface RegisterResponse {
  token: string;
  user: User;
}

export interface Address {
  id: number;
  customer_id: number;
  name: string;
  phone: string;
  address_line1: string;
  address_line2?: string;
  landmark?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  address_type: 'home' | 'work' | 'other';
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductImage {
  id: number;
  product_id: number;
  image_url: string;
  is_primary: boolean;
  sort_order: number;
  created_at: string;
}

export interface ProductVideo {
  id: number;
  product_id: number;
  video_url: string;
  thumbnail_url?: string;
  sort_order: number;
  created_at: string;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  image_url: string;
  is_active: boolean;
  product_count?: number;
  created_at: string;
  updated_at: string;
}

export interface AgeGroup {
  id: number;
  name: string;
  slug: string;
  min_age: number;
  max_age: number;
  range: string;
  description: string;
  is_active: boolean;
  product_count?: number;
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: number;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  sku: string;
  mrp: number;
  selling_price: number;
  dealer_price: number;
  purchase_cost?: number;
  packing_cost?: number;
  stock: number;
  low_stock_alert: number;
  age_group: string | AgeGroup;
  weight: number;
  dimensions: string;
  is_featured: boolean;
  is_festive?: boolean;
  is_cod_available?: boolean;
  is_active: boolean;
  images: ProductImage[];
  videos: ProductVideo[];
  categories: Category[];
  created_at: string;
  updated_at: string;
  sales_count?: number;
  cgst_percent: number;
  sgst_percent: number;
  igst_percent: number;
  return_exchange_condition: string;
  size_chart?: string;
  warehouse_allocations?: Array<{
    id: number;
    product_id: number;
    warehouse_id: string;
    quantity: number;
    priority: number;
    created_at: string;
    updated_at: string;
  }>;
}

export interface CartItem {
  id: number;
  customer_id: number;
  product_id: number;
  product: Product;
  quantity: number;
  created_at: string;
  updated_at: string;
}

export interface Cart {
  id: number;
  user_id: number;
  items: CartItem[];
  subtotal: number;
  cgst: number;
  sgst: number;
  total: number;
}

export interface OrderItem {
  id: number;
  order_id?: number;
  product_id: number;
  product_name?: string;
  product_sku?: string;
  product: Product;
  quantity: number;
  unit_price: number;
  price?: number;
  total_price: number;
  return_exchange_condition?: string;
  created_at?: string;
}

export interface OrderStatusHistory {
  id: number;
  order_id: number;
  status: string;
  notes: string;
  created_by: number;
  created_at: string;
}

export interface OrderReturn {
  id: number;
  order_id: number;
  order_item_id: number;
  product_id: number;
  quantity: number;
  refund_amount: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  requested_at: string;
  processed_at?: string;
  processed_by?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ReplacementItem {
  id: number;
  order_id: number;
  original_item_id: number;
  original_product_name: string;
  original_product_image?: string;
  original_quantity: number;
  replacement_product_id: number;
  replacement_product_name: string;
  replacement_product_image?: string;
  replacement_quantity: number;
  price_difference: number;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'in_transit';
  requested_at?: string;
  approved_at?: string;
  completed_at?: string;
  tracking_id?: string;
  reason: string;
  size_variant?: string;
  color_variant?: string;
  admin_notes?: string;
}

export interface OrderFreeGift {
  id: number;
  order_id: number;
  coupon_id: number;
  coupon_code: string;
  product_id: string;
  product_name: string;
  product_image: string;
  product_price: number;
  quantity: number;
  created_at: string;
}

export interface Order {
  id: number;
  order_number: string;
  customer_id: number;
  status: string;
  payment_status: string;
  payment_method: string;
  subtotal_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount?: number;
  total_amount: number;
  cod_charges?: number;
  handling_charges?: number;
  small_cart_charges?: number;
  payment_discount?: number;
  coupon_discount?: number;
  wallet_deducted?: number;
  // Partial COD
  is_partial_cod?: boolean;
  advance_amount?: number;
  advance_paid?: number;
  advance_paid_at?: string;
  cod_amount_due?: number;
  shipping_name: string;
  shipping_phone: string;
  shipping_address: string;
  shipping_city: string;
  shipping_state: string;
  shipping_pincode: string;
  shipping_country: string;
  delivery_start_date?: string;
  delivery_end_date?: string;
  tracking_id?: string;
  courier_name?: string;
  awb_number?: string;
  shipped_at?: string;
  delivered_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  return_requested_at?: string;
  return_approved_at?: string;
  return_start_date?: string;
  return_end_date?: string;
  return_reason?: string;
  return_video_url?: string;
  return_admin_notes?: string;
  return_tracking_id?: string;
  return_courier_name?: string;
  return_awb_number?: string;
  return_received_at?: string;
  return_completed_at?: string;
  refund_amount?: number;
  refund_method?: string;
  refund_transaction_id?: string;
  refund_processed_at?: string;
  returned_at?: string;
  delivery_instructions?: string;
  preferred_delivery_date?: string;
  alternative_phone?: string;
  admin_notes?: string;
  commission_amount?: number;
  referred_by_employee_id?: number;
  items: OrderItem[];
  status_history?: OrderStatusHistory[];
  returns?: OrderReturn[];
  replacements?: ReplacementItem[];
  free_gifts?: OrderFreeGift[];
  created_at: string;
  updated_at: string;
  customer?: {
    id: number;
    user_id: number;
    user?: {
      id: number;
      email: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
    }
  };
  total?: number;
  
  order_via?: string;
  place_of_supply?: string;
}

export interface ExtendedOrder extends Order {
  status_label?: string;
  payment_status_label?: string;
  return_status?: string;
  refund_status?: string;
  shiprocket_order_id?: number;
  shiprocket_shipment_id?: number;
  shiprocket_return_order_id?: number;
  shiprocket_return_awb?: string;
  shiprocket_return_pickup_date?: string;
  shiprocket_return_courier?: string;
  pickup_location?: string;
  warehouse_name?: string;
  warehouse_address?: string;
  warehouse_city?: string;
  warehouse_state?: string;
  warehouse_pincode?: string;
  warehouse_phone?: string;
  shipping_amount?: number;
  courier_charge?: number; // Shiprocket courier charge saved at ShipNow
  discount_amount?: number;
  tracking_url?: string;
  call_status?: string;
  call_notes?: string;
  call_attempts?: number;
  last_call_at?: string;
  courier_id?: number;
  free_gift_coupon_code?: string;
  free_gifts?: OrderFreeGift[];
  order_via?: string;
  place_of_supply?: string;
}

export interface ExtendedApiOrder {
  id: number;
  order_number: string;
  customer_id: number;
  status: string;
  payment_status: string;
  payment_method: string;
  subtotal_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount?: number;
  total_amount: number;
  cod_charges?: number;
  handling_charges?: number;
  small_cart_charges?: number;
  payment_discount?: number;
  coupon_discount?: number;
  wallet_deducted?: number;
  // Partial COD
  is_partial_cod?: boolean;
  advance_amount?: number;
  advance_paid?: number;
  advance_paid_at?: string;
  cod_amount_due?: number;
  shipping_name: string;
  shipping_phone: string;
  shipping_address: string;
  shipping_city: string;
  shipping_state: string;
  shipping_pincode: string;
  shipping_country: string;
  billing_name?: string;
  billing_phone?: string;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_pincode?: string;
  billing_country?: string;
  created_at: string;
  updated_at: string;
  items: any[];
  discount_amount?: number;
  notes?: string;
  admin_notes?: string;
  awb_number?: string;
  courier_name?: string;
  tracking_url?: string;
  tracking_id?: string;
  shipped_at?: string;
  delivered_at?: string;
  delivery_start_date?: string;
  delivery_end_date?: string;
  estimated_delivery?: string;
  cancellation_reason?: string;
  cancelled_at?: string;
  return_requested_at?: string;
  return_approved_at?: string;
  return_rejected_at?: string;
  return_processing_at?: string;
  return_in_transit_at?: string;
  return_received_at?: string;
  return_completed_at?: string;
  return_start_date?: string;
  return_end_date?: string;
  return_reason?: string;
  return_video_url?: string;
  return_tracking_id?: string;
  return_courier_name?: string;
  return_awb_number?: string;
  refund_amount?: number;
  refund_method?: string;
  refund_transaction_id?: string;
  refund_processed_at?: string;
  status_history?: OrderStatusHistory[];
  returns?: ExtendedOrderReturn[];
  replacements?: ReplacementItem[];
  shiprocket_order_id?: number;
  shiprocket_shipment_id?: number;
  pickup_location?: string;
  warehouse_name?: string;
  warehouse_address?: string;
  free_gifts?: ApiFreeGift[];
  free_gift_coupon_code?: string;
  invoice_url?: string;
  
  order_via?: string;
  place_of_supply?: string;
}

export interface ExtendedOrderReturn extends OrderReturn {
  return_requested_at?: string;
  return_approved_at?: string;
  return_rejected_at?: string;
  return_processing_at?: string;
  return_in_transit_at?: string;
  return_received_at?: string;
  return_completed_at?: string;
  return_start_date?: string;
  return_end_date?: string;
  return_tracking_id?: string;
  return_courier_name?: string;
  return_awb_number?: string;
  refund_transaction_id?: string;
  refund_processed_at?: string;
  admin_notes?: string;
  return_period?: number;
}

export interface ApiFreeGift {
  id: number;
  product_name: string;
  product_image: string | null;
  product_price: number;
  quantity: number;
  coupon_code?: string;
}

export interface Slide {
  id: number;
  title: string;
  description: string;
  image_url: string;
  order: number;
  is_active: boolean;
  button_text: string;
  button_link: string;
  created_at: string;
  updated_at: string;
}

export interface About {
  id: number;
  title: string;
  company_name: string;
  brand_name: string;
  tagline: string;
  founder_name: string;
  description: string;
  about_brand: string;
  highlights: string;
  udyam_registration: string;
  gst_cg: string;
  gst_odisha: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaymentCharges {
  id: number;
  codCharges: number;
  showCodToast: boolean;
  deliveryCharges: number;
  minCartValue: number;
  handlingCharges: number;
  smallCartCharges: number;
  smallCartLimit: number;
  onlineDiscountType: 'Percentage' | 'Flat';
  onlineDiscountValue: number;
  onlineMaxDiscount: number;
  partialCodDiscountType: 'Percentage' | 'Flat';
  partialCodDiscountValue: number;
  partialCodMaxDiscount: number;
  partialCodEnabled: boolean;
  partialCodAdvanceType: 'Percentage' | 'Flat';
  partialCodAdvanceValue: number;
  partialCodGateway?: 'razorpay' | 'payu' | 'phonepe' | 'both';
  razorpayEnabled?: boolean;
  payuEnabled?: boolean;
  phonepeEnabled?: boolean;
  codEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RepeatUserCoupon {
  id: number;
  coupon_code: string;
  discount_type: 'Percentage' | 'Flat';
  discount_value: number;
  max_discount: number;
  expiry_days: number;
  whatsapp_sent: boolean;
  enabled: boolean;
  created_by: number;
  created_at: string;
  expires_at: string;
  used_count: number;
}

export interface RepeatUserCouponUsage {
  id: number;
  coupon_id: number;
  user_id: number;
  order_id: number;
  discount: number;
  used_at: string;
}

export interface GenericCoupon {
  id: number;
  uuid: string;
  title: string;
  subtitle: string;
  coupon_code: string;
  discount_type: 'Flat' | 'Percentage';
  flat_price: number;
  percentage_discount: number;
  max_discount: number;
  min_order_value: number;
  min_quantity: string;
  expiry_date: string;
  max_usage: number;
  per_user_limit: number;
  fake_timer: boolean;
  platform: string;
  only_new_customers: boolean;
  global_visibility: boolean;
  pre_applied_ad_link: boolean;
  status: 'active' | 'expired' | 'disabled';
  total_used: number;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface GenericCouponUsage {
  id: number;
  coupon_id: number;
  user_id: number;
  order_id: number;
  discount_amount: number;
  used_at: string;
}

export interface FreeGiftProduct {
  id: number;
  coupon_id: number;
  product_id: string;
  product_name: string;
  product_image: string;
  product_price: number;
}

export interface FreeGiftCoupon {
  id: number;
  title: string;
  subtitle: string;
  coupon_code: string;
  expiry_date: string;
  per_user_limit: number;
  max_usage: number;
  number_of_free_gift: number;
  number_of_items: number | null;
  applicability: string;
  min_order_value: number;
  platform: string;
  only_new_customers: boolean;
  global_visibility: boolean;
  fake_timer: boolean;
  usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  selected_gift_products: Array<{
    id: number;
    product_id: string;
    product_name: string;
    product_image: string;
    product_price: number;
  }>;
}

export interface CreateFreeGiftCouponRequest {
  title: string;
  subtitle?: string;
  couponCode: string;
  expiryDate: string;
  perUserLimit: number;
  maxUsage: number;
  numberOfFreeGift: number;
  numberOfItems: number | null;
  applicability: string;
  minOrderValue: number | null;
  platform: string;
  onlyNewCustomers: boolean;
  globalVisibility: boolean;
  fakeTimer: boolean;
  selectedGiftProducts: Array<{
    id: string;
    name: string;
    image: string;
    price: number;
  }>;
}

export type BXGYPaymentMode = 'all' | 'cod' | 'partial_cod' | 'online';

export interface BXGYCoupon {
  id: number;
  title: string;
  subtitle: string;
  offerCode: string;
  minOrderValue: number;
  expiryDate: string;
  maxUsage: number;
  perUserLimit: number;
  fakeTimer: boolean;
  onlyNewCustomers: boolean;
  globalVisibility: boolean;
  platform: string;
  paymentMode?: BXGYPaymentMode;
  productSelection: 'all' | 'custom';
  selectedProducts: number[];
  /** ON: free product(s) on buying ANY website product. OFF: only when a Buy (X) product is in cart */
  applyOnAllProducts?: boolean;
  buyProducts: number[];
  freeProducts: number[];
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BXGYCouponStats {
  total_usage: number;
  unique_users: number;
  remaining: number;
  daily_usage: Array<{
    date: string;
    count: number;
  }>;
  couponId: number;
  couponCode: string;
  couponTitle: string;
  maxUsage: number;
  currentUsage: number;
  isActive: boolean;
  isExpired: boolean;
}

export interface ValidateBXGYCouponRequest {
  offerCode: string;
  userId: number;
  cartTotal: number;
  cartItems: Array<{
    productId: number;
    quantity: number;
  }>;
  platform: string;
  /** Checkout payment method: 'cod' | 'partial_cod' | 'razorpay' | 'payu' ... */
  paymentMethod?: string;
}

export interface ValidateBXGYCouponResponse {
  success: boolean;
  data: {
    coupon: BXGYCoupon;
    freeProducts: Product[];
    message: string;
  };
}

export interface RecordBXGYCouponUsageRequest {
  offerCode: string;
  userId: number;
  orderId: number;
  freeProductId: number;
}

export interface CreateBXGYCouponRequest {
  title: string;
  subtitle: string;
  /** Optional - generated automatically by the server when omitted */
  offerCode?: string;
  minOrderValue: number;
  expiryDate: string;
  maxUsage: number;
  perUserLimit: number;
  fakeTimer: boolean;
  onlyNewCustomers: boolean;
  globalVisibility: boolean;
  platform: string;
  paymentMode?: BXGYPaymentMode;
  productSelection: 'all' | 'custom';
  selectedProducts: number[];
  applyOnAllProducts?: boolean;
  buyProducts: number[];
  freeProducts: number[];
}

export interface UpdateBXGYCouponRequest extends Partial<CreateBXGYCouponRequest> {
  isActive?: boolean;
}

export interface GetAllBXGYCouponsParams {
  status?: 'active' | 'inactive';
  search?: string;
  platform?: string;
  page?: number;
  limit?: number;
}

export interface OfficeLocation {
  id?: number;
  name: string;
  address: string;
  phone: string;
}


export interface Contact {
  id: number;
  email: string;
  phone: string;
  address: string;
  business_hours: string;
  website: string;
  additional_info: string;
  hero_image: string;
  map_embed_url: string;
  facebook: string;
  twitter: string;
  instagram: string;
  youtube: string;   
  is_active: boolean;      
  created_at: string;
  updated_at: string;
  offices?: OfficeLocation[];
}

export interface ContactForm {
  id: number;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'pending' | 'replied' | 'resolved';
  user_id?: number;
  created_at: string;
  updated_at: string;
}

export interface InventoryPurchase {
  id: number;
  product_id: number;
  product: Product;
  invoiceNumber: string;
  purchaseDate: string;
  purchaseQuantity: number;
  damageQuantity: number;
  invoiceFile?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseStats {
  totalPurchased: number;
  totalDamaged: number;
  totalNetAdded: number;
  totalPurchases: number;
  averagePurchase: number;
}

export interface TrackingEvent {
  timestamp: string;
  status: string;
  location: string;
  description: string;
  completed?: boolean;
}

export interface TrackingInfo {
  tracking_number: string;
  courier_name: string;
  estimated_delivery?: string;
  current_status: string;
  events: TrackingEvent[];
}

export interface CustomerPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  whatsapp_notifications: boolean;
  newsletter_subscribed: boolean;
  language: string;
  currency: string;
  theme: 'light' | 'dark' | 'system';
}

export interface Wallet {
  id: number;
  employee_id: number;
  balance: number;
  pending_balance: number;
  total_earned: number;
  total_withdrawn: number;
  created_at: string;
  updated_at: string;
}

export interface Transaction {
  id: number;
  wallet_id: number;
  order_id?: number;
  transaction_type: 'commission_credit' | 'commission_debit' | 'withdrawal' | 'adjustment';
  type: 'credit' | 'debit';
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  reference_id: string;
  note: string;
  description: string;
  balance_before: number;
  balance_after: number;
  created_at: string;
}

export interface Commission {
  id: number;
  order_id: number;
  employee_id: number;
  employee_name: string;
  employee_email: string;
  order_number: string;
  level_no: number;
  commission_amount: number;
  amount: number;
  rate: number;
  status: 'pending' | 'upcoming' | 'credited' | 'cancelled' | 'reversed';
  credited_to_wallet: boolean;
  eligible_at: string;
  credited_at: string;
  remarks: string;
  created_at: string;
  updated_at: string;
  order?: Order;
  employee?: Employee;
}

export interface CommissionsPaginatedResponse {
  success: boolean;
  data: Commission[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  totals: {
    pending_total: number;
    approved_total: number;
    rejected_total: number;
  };
  message?: string;
  error?: string;
}

export interface WithdrawalRequest {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_email: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid' | 'completed';
  approval_required: boolean;
  approved_by?: number;
  approved_by_name?: string;
  approved_at?: string;
  processed_at?: string;
  rejection_reason?: string;
  payout_method?: string;
  payout_reference?: string;
  transaction_ref?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  employee?: Employee;
}

export interface Employee {
  id: number;
  user_id: number;
  created_at: string;
  updated_at: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  gender: 'male' | 'female' | 'other';
  father_name?: string;
  mother_name?: string;
  emergency_contact?: string;
  email: string;
  phone: string;
  mobile_num?: string;
  whatsapp_num?: string;
  designation: string;
  employee_type: 'permanent' | 'contract' | 'probation' | 'intern';
  department: string;
  commission_rate: number;
  commission: number;
  experience: number;
  join_date?: string;
  joined_at: string;
  referral_code: string;
  total_referrals: number;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  district?: string;
  pincode?: string;
  country?: string;
  aadhar_number?: string;
  pan_number?: string;
  account_number?: string;
  ifsc_code?: string;
  bank_name?: string;
  bank_branch?: string;
  education_qualification?: string;
  role: 'employee' | 'manager' | 'admin';
  is_active: boolean;
  parent_employee_id?: number;
  user?: User;
  wallet?: Wallet;
  parent_employee?: Employee;
  can_create_sub_employees: boolean;
  max_referrals_allowed: number | null;
  can_view_reports: boolean;
  can_manage_team: boolean;
  profile_photo?: string;
  wallet_balance?: number;
  total_earnings?: number;
  pending_earnings?: number;
  upcoming_earnings?: number;
  direct_referrals?: number;
  level2_referrals?: number;
  level3_referrals?: number;
  employee_code?: string;
}

// ==================== NOTIFICATION TYPES ====================

export interface Notification {
  id: number;
  employee_id: number;
  title: string;
  message: string;
  type: 'commission' | 'order' | 'referral' | 'system' | 'withdrawal' | 'general';
  is_read: boolean;
  link?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferences {
  email_enabled: boolean;
  push_enabled: boolean;
  commission_alerts: boolean;
  order_alerts: boolean;
  referral_alerts: boolean;
  withdrawal_alerts: boolean;
  system_alerts: boolean;
}

export interface NotificationStats {
  total_notifications: number;
  unread_count: number;
  read_count: number;
  by_type: {
    commission: number;
    order: number;
    referral: number;
    withdrawal: number;
    system: number;
    general: number;
  };
  last_7_days: Array<{ date: string; count: number }>;
}

// ==================== ModulePermission Interface ====================
export interface ModulePermission {
  module: string;
  module_name: string;
  permissions: {
    view: boolean;
    add: boolean;
    edit: boolean;
    delete: boolean;
    reset_password: boolean;
    active: boolean;
    track_order: boolean;
    return_exchange: boolean;
    ndr: boolean;
    rto_return: boolean;
    can_track_order?: boolean;
    can_return_exchange?: boolean;
    can_ndr?: boolean;
    can_rto?: boolean;
    can_reset_password?: boolean;
    can_toggle_active?: boolean;
  };
  icon?: string;
}

export interface EmployeeFullPermissions {
  employee_id: number;
  employee_code?: string;
  employee_name: string;
  permissions: ModulePermission[];
  last_updated?: string;
  updated_by?: string;
}

export interface EmployeePermissionsResponse {
  can_create_sub_employees: boolean;
  max_referrals_allowed: number | null;
  can_view_reports: boolean;
  can_manage_team: boolean;
}

export interface DashboardStats {
  employee_code?: string;
  referral_code: string;
  referral_link: string;
  wallet_balance: number;
  total_earnings: number;
  pending_earnings: number;
  upcoming_earnings: number;
  total_referrals: number;
  successful_referrals: number;
  referral_team_size: number;
  level1_referrals: number;
  level2_referrals: number;
  level3_referrals: number;
  level1_commission: number;
  level2_commission: number;
  level3_commission: number;
  max_referrals_allowed: number | null;
  can_create_sub_employees: boolean;
  can_manage_team: boolean;
  can_view_reports: boolean;
}

export interface Review {
  id: number;
  product_id: number;
  customer_id: number;
  order_id?: number;
  rating: number;
  title: string;
  review: string;
  is_verified: boolean;
  is_approved: boolean;
  helpful_count: number;
  user_name: string;
  user_avatar: string;
  images: string[];
  created_at: string;
  updated_at: string;
  product?: {
    id: number;
    name: string;
    image?: string;
  };
}

export interface ReviewSummary {
  total_reviews: number;
  average_rating: number;
  star_5: number;
  star_4: number;
  star_3: number;
  star_2: number;
  star_1: number;
}

export interface EmployeeReferralStats {
  employee_id: number;
  employee_code?: string;
  employee_name: string;
  total_referrals: number;
  successful_referrals: number;
  total_commission: number;
  pending_commission: number;
  level1_count: number;
  level2_count: number;
  level3_count: number;
  level1_commission: number;
  level2_commission: number;
  level3_commission: number;
  referral_code: string;
  referral_link: string;
}

// 🔗 Admin product share links (list shown on the Product Share page)
export interface ProductShareLinkProduct {
  id: number;
  name: string;
  sku: string;
  selling_price: number;
  is_active: boolean;
}

export interface ProductShareLinkItem {
  id: number;
  code: string;
  path: string;
  product_count: number;
  preview: Array<{ id: number; name: string }>;
  created_by: number;
  created_at: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  pagination?: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  totals?: any;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface ApiPaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  message?: string;
  error?: string;
}

// ==================== ✨ SKU AVAILABILITY TYPES (NEW) ====================
export interface SkuAvailabilityResponse {
  success: boolean;
  available: boolean;
  product_id?: number;
  product_name?: string;
  message?: string;
}
// =========================================================================

export interface ReferralValidationResponse {
  valid: boolean;
  employee_name?: string;
  discount_percent?: number;
  discount_type?: string;
  discount_value?: number;
  message?: string;
}

export interface BankAccount {
  id?: number;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch_name?: string;
  upi_id?: string;
}

export interface CustomerWallet {
  id: number;
  customer_id: number;
  balance: number;
  total_added: number;
  total_spent: number;
  total_cashback: number;
  created_at: string;
  updated_at: string;
}

export interface CustomerTransaction {
  id: number;
  wallet_id: number;
  order_id?: number;
  amount: number;
  type: 'credit' | 'debit';
  description: string;
  status: 'success' | 'pending' | 'failed';
  reference_id?: string;
  balance_before: number;
  balance_after: number;
  created_at: string;
}

export interface CustomerWithdrawalRequest {
  id: number;
  customer_id: number;
  wallet_id: number;
  amount: number;
  bank_details: {
    accountNumber: string;
    ifscCode: string;
    accountHolderName: string;
    bankName?: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed';
  rejection_reason?: string;
  transaction_ref?: string;
  created_at: string;
  processed_at?: string;
}

export interface CustomerSavedBank {
  id: number;
  customer_id: number;
  account_number: string;
  ifsc_code: string;
  account_holder_name: string;
  bank_name?: string;
  is_default: boolean;
  created_at: string;
}

export interface ReportRevenueData {
  label: string;
  revenue: number;
  orders: number;
}

export interface ReportKPIData {
  revenue: number;
  revenue_change: number;
  orders: number;
  orders_change: number;
  customers: number;
  customers_change: number;
  avg_order_value: number;
  avg_order_change: number;
  return_rate: number;
  commissions: number;
  commissions_change: number;
}

export interface ReportCategoryData {
  name: string;
  value: number;
  color: string;
}

export interface ReportWeeklyOrderData {
  week: string;
  orders: number;
  returns: number;
}

export interface ReportTopProduct {
  name: string;
  emoji: string;
  bgColor: string;
  sales: number;
  revenue: string;
  stock: 'In stock' | 'Low stock' | 'Out of stock';
}

export interface ReportActivityItem {
  id: number;
  type: 'order' | 'coupon' | 'return' | 'wallet';
  title: string;
  sub: string;
  amount: string;
  positive: boolean;
}

export interface ReportResponse {
  revenueData: ReportRevenueData[];
  kpi: ReportKPIData;
  categoryData: ReportCategoryData[];
  weeklyOrders: ReportWeeklyOrderData[];
  topProducts: ReportTopProduct[];
  activityItems: ReportActivityItem[];
}

export interface PickupAddress {
  id: string;
  addressLine: string;
  landmark: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
  operationalDays: string;
  openingTime: string;
  closingTime: string;
  primaryContact: ContactPerson;
  alternateContact: AlternateContact;
  addAsSupplier: boolean;
  useDifferentRTO: boolean;
  rtoAddress: string;
  isActive: boolean;
  createdAt: string;
}

export interface ContactPerson {
  name: string;
  contactNumber: string;
  email: string;
  role: string;
}

export interface AlternateContact {
  name: string;
  alternatePhone: string;
  email: string;
  role: string;
}

export interface WarehousePreferences {
  preference: 'Fastest' | 'Cheapest' | 'Custom';
  customOrder?: PickupAddress[];
}

export interface RTOSettings {
  sameAsPickup: boolean;
}

export interface Announcement {
  id: number;
  text: string;
  emoji: string;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

// ==================== RTO TYPES ====================

export type RtoStatus = 'RTO' | 'Return' | 'Exchange';
export type RtoTab = 'intransit' | 'delivered' | 'disputes';

export interface RtoOrder {
  id: number;
  initiated_at: string;
  order_number: string;
  order_placed_at: string;
  product_image?: string;
  type: RtoStatus;
  payment_mode: string;
  order_value: number;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  awb_number: string;
  track_url: string;
  courier_name: string;
  ndr_attempt: number;
  reason: string;
  expected_delivery: string;
  status: RtoTab;
  delivery_status: 'RTO Failed' | 'Out for Delivery' | 'In Transit' | 'Delivered' | 'Disputed';
}

export interface RtoFilters {
  page?: number;
  page_size?: number;
  status?: RtoTab;
  search?: string;
  type?: RtoStatus;
  courier_name?: string;
  delivery_status?: string;
  start_date?: string;
  end_date?: string;
}

export interface RtoStats {
  intransit: number;
  delivered: number;
  disputes: number;
}

// ==================== NDR TYPES ====================

export type NdrTab = 'action' | 'reattempt' | 'delivered' | 'rto';

export interface NdrOrder {
  id: number;
  latest_ndr_time: string;
  order_number: string;
  order_placed_at: string;
  awb_number: string;
  track_url: string;
  shipping_charge: number;
  courier_name: string;
  payment_mode: string;
  order_value: number;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  ndr_attempt: number;
  ndr_reason: string;
  whatsapp_status: string;
  sms_status: string;
  call_status: string;
  total_whatsapp: number;
  total_sms: number;
  total_calls: number;
  status: NdrTab;
}

export interface NdrFilters {
  page?: number;
  page_size?: number;
  status?: NdrTab;
  search?: string;
  ndr_reason?: string;
  ndr_attempt?: number;
  start_date?: string;
  end_date?: string;
}

export interface NdrStats {
  total: number;
  pending: number;
  delivered: number;
  rto: number;
}

// ==================== CUSTOMER TYPES ====================

export interface AdminCustomer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  total_orders?: number;
  total_spent?: number;
  call_status?: string; // pending / connected / not_reached / busy / callback / ...
}

export interface AdminCustomerDetails extends AdminCustomer {
  addresses?: Address[];
  orders?: Order[];
  wallet?: CustomerWallet;
  wallet_transactions?: CustomerTransaction[];
}

// ==================== POST CHECKOUT OFFER TYPES ====================

export interface PostCheckoutOffer {
  id: number;
  couponCode: string;
  discountType: 'Percentage' | 'Flat';
  discount: number;
  maxDiscount: number;
  timeValid: number;
  perUserLimit: number;
  whatsapp: boolean;
  messageType: 'Normal' | 'Promotional' | 'Transactional';
  useDefaultAccent: boolean;
  accentColor: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePostCheckoutOfferData {
  couponCode: string;
  discountType: string;
  discount: number;
  maxDiscount: number;
  timeValid: number;
  perUserLimit: number;
  whatsapp: boolean;
  messageType: string;
  useDefaultAccent: boolean;
  accentColor: string;
  enabled: boolean;
}

export interface UpdatePostCheckoutOfferData extends Partial<CreatePostCheckoutOfferData> {}

// ==================== OFFERS TYPES ====================

export interface Offer {
  id: string;
  title: string;
  description: string;
  discount_percentage: number;
  code: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  min_order_value?: number;
  max_discount?: number;
  emoji?: string;
}

// ==================== API CONFIGURATION ====================
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const BASE_URL = API_URL.replace('/api', '');

/** Turn a stored relative upload path (e.g. /uploads/returns/x.mp4) into a full URL. */
export const resolveMediaUrl = (path?: string): string => {
  if (!path) return '';
  return /^https?:\/\//i.test(path) ? path : `${BASE_URL}${path}`;
};

console.log('🔧 API Base URL:', API_URL);
console.log('🔧 Static Base URL:', BASE_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Guest cart helpers
const GUEST_CART_KEY = 'guestCart';
const GUEST_CART_ID_KEY = 'guestCartId';

// Helper to generate unique guest cart ID
const generateGuestCartId = (): string => {
  return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create guest cart ID
const getGuestCartId = (): string => {
  let guestId = localStorage.getItem(GUEST_CART_ID_KEY);
  if (!guestId) {
    guestId = generateGuestCartId();
    localStorage.setItem(GUEST_CART_ID_KEY, guestId);
  }
  return guestId;
};

// Save guest cart to localStorage
const saveGuestCart = (cart: any) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
};

// Get guest cart from localStorage
const getGuestCart = (): any => {
  const stored = localStorage.getItem(GUEST_CART_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Clear guest cart
const clearGuestCart = () => {
  localStorage.removeItem(GUEST_CART_KEY);
};

// Helper to create empty cart object matching Cart interface
const createEmptyCart = (): Cart => ({
  id: 0,
  user_id: 0,
  items: [],
  subtotal: 0,
  cgst: 0,
  sgst: 0,
  total: 0
});

// Helper to convert guest cart to proper Cart type with stock validation - FIXED
const normalizeGuestCart = (guestCart: any): Cart => {
  if (!guestCart || !guestCart.items) {
    return createEmptyCart();
  }
  
  // Validate each product has required fields
  const validItems = guestCart.items.filter((item: any) => {
    if (!item.product) return false;
    
    // Ensure selling_price is a valid number
    if (typeof item.product.selling_price !== 'number' || isNaN(item.product.selling_price)) {
      console.warn(`⚠️ Product ${item.product.id} has invalid selling_price:`, item.product.selling_price);
      item.product.selling_price = item.product.mrp || 0;
    }
    
    if (typeof item.product.mrp !== 'number' || isNaN(item.product.mrp)) {
      item.product.mrp = item.product.selling_price;
    }
    
    // Ensure stock is a valid number
    if (typeof item.product.stock === 'undefined' || isNaN(item.product.stock)) {
      console.warn(`⚠️ Product ${item.product.id} has no stock info, defaulting to 0`);
      item.product.stock = 0;
    }
    
    return true;
  });
  
  const subtotal = validItems.reduce((sum: number, item: any) => {
    const price = item.product?.selling_price || 0;
    const qty = item.quantity || 0;
    return sum + (price * qty);
  }, 0);
  
  console.log('📊 normalizeGuestCart calculated:', {
    itemsCount: validItems.length,
    subtotal: subtotal,
    firstItem: validItems[0]?.product
  });
  
  return {
    id: guestCart.id || 0,
    user_id: guestCart.user_id || 0,
    items: validItems,
    subtotal: subtotal,
    cgst: guestCart.cgst || 0,
    sgst: guestCart.sgst || 0,
    total: subtotal + (guestCart.cgst || 0) + (guestCart.sgst || 0)
  };
};

// ==================== REQUEST INTERCEPTOR ====================
api.interceptors.request.use(
  (config) => {
    console.log(`🚀 ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    // Endpoints that must NEVER receive a token (login / register / OTP / shared-link login).
    // NOTE: '/auth/protected/*' and '/auth/force-change-password' DO need the token.
    const url = config.url || '';
    const isNoTokenEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/send-login-otp') ||
      url.includes('/auth/verify-login-otp') ||
      url.includes('/auth/register') ||
      url.includes('/register-with-otp') ||
      url.includes('/forgot-password') ||
      url.includes('/reset-password-direct') ||
      url.includes('/send-otp') ||
      url.includes('/verify-otp') ||
      url.includes('/product-share/');

    // Attach the token whenever the user is logged in.
    // (Previously every URL containing '/products' was treated as "public" and got NO token,
    //  so auth-only calls like GET /products/:id/reviews/my returned 401 after a page reload
    //  and the 401 handler kicked the user to /login. Public endpoints simply ignore the header.)
    if (token && !isNoTokenEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔐 Added auth token for ${config.url}`);
    }
    
    // Handle guest cart only when there's NO token
    const isCartOperation = config.url?.includes('/cart') || config.url?.includes('/add-to-cart');
    if (isCartOperation && !token) {
      const guestId = getGuestCartId();
      if (config.method === 'get') {
        config.params = { ...config.params, guest_id: guestId };
      } else if (config.data) {
        config.data.guest_id = guestId;
      } else {
        config.data = { guest_id: guestId };
      }
      console.log(`🛒 Guest cart operation with ID: ${guestId}`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// ==================== RESPONSE INTERCEPTOR ====================
api.interceptors.response.use(
  (response) => {
    console.log(`✅ ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      console.error('❌ Response Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        data: error.response.data
      });

      const PUBLIC_AUTH_PATHS = [
        '/auth/login',
        '/auth/send-login-otp',
        '/auth/verify-login-otp',
        '/send-otp',
        '/verify-otp',
        '/register-with-otp',
        '/reset-password-direct',
        '/forgot-password',
      ];
      const isPublicAuthRequest = PUBLIC_AUTH_PATHS.some((path) => error.config?.url?.includes(path));

      // Optional "am I logged in?" lookups on public pages (product detail): a 401 here must
      // never throw the visitor to the Login page. Just treat them as logged-out for that call.
      const isSoftAuthRequest = ['/reviews/my', '/wishlist/check-status'].some((path) => error.config?.url?.includes(path));

      if (error.response.status === 401 && !isPublicAuthRequest && !isSoftAuthRequest && !error.config?.url?.includes('/cart')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
      }
    } else if (error.request) {
      console.error('❌ No response received:', error.request);
    } else {
      console.error('❌ Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export const getEmployeeCode = (id: number): string => {
  return `EMP${String(id).padStart(6, '0')}`;
};

// ==================== API SERVICE ====================
// ---- Warehouse URL helpers: employees use /employee/warehouse/*, admins use /admin/* ----
const isEmployeePortal = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/employee');
const whBase = () => (isEmployeePortal() ? '/employee/warehouse' : '/admin');
const whPrefBase = () => (isEmployeePortal() ? '/employee/warehouse' : '/admin/warehouse');
const whRtoUrl = () => (isEmployeePortal() ? '/employee/warehouse/rto-settings' : '/admin/rto/settings');
// ---- Product Share URL helper: employees use /employee/product-share/*, admins use /admin/* ----
const shareUrl = (adminPath: string, employeePath: string) =>
  (isEmployeePortal() ? `/employee/product-share${employeePath}` : `/admin${adminPath}`);
// ---- COD URL helpers: employees use /employee/cod-configuration|cod-remittance/*, admins use /admin/* ----
const codCfgUrl = (p: string) => (isEmployeePortal() ? `/employee/cod-configuration${p}` : `/admin/cod-blocks${p}`);
const codRemUrl = (p: string) => (isEmployeePortal() ? `/employee/cod-remittance${p}` : `/admin/cod-remittance${p}`);

const apiService = {
  // ==================== OTP REGISTRATION METHODS ====================
  
  sendOTP: async (phone: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`📱 Sending registration OTP to ${phone}...`);
      const res = await api.post('/send-otp', { phone });
      console.log('📱 Send OTP response:', res.data);
      return { success: true, message: res.data.message };
    } catch (error: any) {
      console.error('❌ Send OTP error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to send OTP' 
      };
    }
  },

  verifyOTP: async (phone: string, otp: string): Promise<{ 
    success: boolean; 
    verified?: boolean; 
    temp_token?: string; 
    error?: string 
  }> => {
    try {
      console.log(`📱 Verifying registration OTP for ${phone}...`);
      const res = await api.post('/verify-otp', { phone, otp });
      console.log('📱 Verify OTP response:', res.data);
      
      if (res.data.success && res.data.data) {
        return { 
          success: true, 
          verified: res.data.data.verified,
          temp_token: res.data.data.temp_token 
        };
      }
      return { success: false, error: res.data.message || 'Verification failed' };
    } catch (error: any) {
      console.error('❌ Verify OTP error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid OTP' 
      };
    }
  },

  registerWithOTP: async (data: {
    phone: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    temp_token: string;
  }): Promise<{ success: boolean; data?: { token: string; user: User }; error?: string }> => {
    try {
      console.log('📝 Registering with OTP verification...');
      const res = await api.post('/register-with-otp', data);
      console.log('📝 Registration response:', res.data);
      
      if (res.data.success && res.data.data) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Registration failed' };
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Registration failed' 
      };
    }
  },

  sendLoginOTP: async (phone: string, channel: 'sms' | 'whatsapp' = 'sms'): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`📱 Sending login OTP via ${channel} to ${phone}...`);
      const res = await api.post('/auth/send-login-otp', { phone, channel });
      console.log('📱 Send login OTP response:', res.data);
      return { success: true, message: res.data.message };
    } catch (error: any) {
      console.error('❌ Send login OTP error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || `Failed to send OTP via ${channel}` 
      };
    }
  },

  verifyLoginOTP: async (phone: string, otp: string, rememberMe: boolean = false, channel: 'sms' | 'whatsapp' = 'sms'): Promise<{ 
    success: boolean; 
    data?: { token: string; user: User; must_change_password?: boolean }; 
    error?: string 
  }> => {
    try {
      console.log(`📱 Verifying login OTP via ${channel} for ${phone}...`);
      const res = await api.post('/auth/verify-login-otp', { phone, otp, remember_me: rememberMe, channel });
      console.log('📱 Verify login OTP response:', res.data);
      
      if (res.data.success && res.data.data) {
        const { token, user, must_change_password } = res.data.data;
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        if (user.role === 'employee' && user.id) {
          user.employee_code = getEmployeeCode(user.id);
        }
        
        return { 
          success: true, 
          data: { token, user, must_change_password }
        };
      }
      return { success: false, error: res.data.message || 'Verification failed' };
    } catch (error: any) {
      console.error('❌ Verify login OTP error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid OTP' 
      };
    }
  },

  // ==================== AUTH ====================
  login: async (username: string, password: string, rememberMe: boolean = false): Promise<{ success: boolean; data: { token: string; user: User; must_change_password?: boolean } | null; error?: string }> => {
    try {
      console.log(`🔐 Login attempt with username: ${username}`);
      const payload = { username, password };
      const res = await api.post('/auth/login', payload);
      
      console.log('📦 Full API Response:', JSON.stringify(res.data, null, 2));
      
      if (res.data && res.data.success === false) {
        return {
          success: false,
          data: null,
          error: res.data.error || res.data.message || 'Invalid credentials'
        };
      }
      
      if (res.data && res.data.error) {
        return {
          success: false,
          data: null,
          error: res.data.error
        };
      }
      
      if (res.data && res.data.message && !res.data.token && !res.data.data?.token) {
        return {
          success: false,
          data: null,
          error: res.data.message
        };
      }
      
      let token: string | null = null;
      let user: User | null = null;
      let must_change_password: boolean | undefined = false;
      
      if (res.data?.data?.token) {
        token = res.data.data.token;
        user = res.data.data.user;
        must_change_password = res.data.data.must_change_password;
      }
      else if (res.data?.token) {
        token = res.data.token;
        user = res.data.user;
        must_change_password = res.data.must_change_password;
      }
      else if (res.data?.user && res.data?.token) {
        token = res.data.token;
        user = res.data.user;
        must_change_password = res.data.must_change_password;
      }
      
      if (!token || !user) {
        console.log('❌ No token found in response');
        return {
          success: false,
          data: null,
          error: res.data?.message || res.data?.error || 'Invalid credentials'
        };
      }
      
      if (user.role === 'employee' && user.id) {
        user.employee_code = getEmployeeCode(user.id);
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      console.log(`✅ Login successful, saved to localStorage`);
      
      return {
        success: true,
        data: { token, user, must_change_password }
      };
      
    } catch (error: any) {
      console.error('❌ Login error:', error);
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      
      let errorMessage = 'Login failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  },
  
  forceChangePassword: async (currentPassword: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log('🔐 Force changing password...');
      const res = await api.post('/auth/force-change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return res.data;
    } catch (error) {
      console.error('❌ Force change password error:', error);
      throw error;
    }
  },
  
  register: async (data: any): Promise<ApiResponse<{ token: string; user: User }>> => {
    try {
      let requestData = { ...data };
      if (data.name) {
        const nameParts = data.name.trim().split(' ');
        requestData = {
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' ') || '',
          email: data.email,
          password: data.password,
          phone: data.phone,
        };
      }
      console.log('📤 Register API call with data:', requestData);
      const res = await api.post('/auth/register', requestData);
      return res.data;
    } catch (error) {
      console.error('❌ Register API error:', error);
      throw error;
    }
  },
  
  getProfile: async (): Promise<ApiResponse<User>> => {
    try {
      const res = await api.get('/auth/protected/profile');
      if (res.data.success && res.data.data && res.data.data.role === 'employee' && res.data.data.id) {
        res.data.data.employee_code = getEmployeeCode(res.data.data.id);
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  
  updateProfile: async (data: Partial<User>): Promise<ApiResponse<User>> => {
    try {
      console.log('📝 Updating profile:', data);
      const res = await api.put('/auth/protected/profile', data);
      return res.data;
    } catch (error) {
      console.error('❌ Profile update failed:', error);
      throw error;
    }
  },
  
  changePassword: async (oldPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.post('/auth/protected/change-password', { 
        current_password: oldPassword, 
        new_password: newPassword 
      });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  uploadProfilePhoto: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('profile_photo', file);
    try {
      console.log('📸 Uploading profile photo:', file.name);
      const res = await api.post('/auth/protected/upload-profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      
      console.log('📸 Upload response:', res.data);
      
      if (res.data.success) {
        let imageUrl = res.data.url || res.data.data?.url;
        if (imageUrl) {
          const fullUrl = imageUrl.startsWith('http') ? imageUrl : `http://localhost:8080${imageUrl}`;
          console.log('📸 Final URL:', fullUrl);
          return { success: true, url: fullUrl };
        }
      }
      
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  deleteProfilePhoto: async (): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log('🗑️ Deleting profile photo...');
      const res = await api.delete('/auth/protected/profile-photo');
      return res.data;
    } catch (error) {
      console.error('❌ Error deleting profile photo:', error);
      throw error;
    }
  },

  getProfilePhoto: async (): Promise<ApiResponse<{ profile_photo_url: string | null }>> => {
    try {
      console.log('📸 Fetching profile photo...');
      const res = await api.get('/auth/protected/profile-photo');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching profile photo:', error);
      throw error;
    }
  },
  
  logout: async (): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.post('/auth/protected/logout');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== ADDRESS METHODS ====================
  getAddresses: async (): Promise<ApiResponse<Address[]>> => {
    try {
      const res = await api.get('/addresses');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getDefaultAddress: async (): Promise<ApiResponse<Address>> => {
    try {
      const res = await api.get('/addresses/default');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  saveAddress: async (addressData: Partial<Address>): Promise<ApiResponse<Address>> => {
    try {
      console.log('🏠 Saving new address:', addressData);
      const res = await api.post('/addresses', addressData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getAddress: async (id: number): Promise<ApiResponse<Address>> => {
    try {
      const res = await api.get(`/addresses/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateAddress: async (id: number, addressData: Partial<Address>): Promise<ApiResponse<Address>> => {
    try {
      console.log('🏠 Updating address:', id, addressData);
      const res = await api.put(`/addresses/${id}`, addressData);
      return res.data;
    } catch (error) {
      console.error('❌ Address update failed:', error);
      throw error;
    }
  },

  deleteAddress: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/addresses/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  setDefaultAddress: async (id: number): Promise<ApiResponse<Address>> => {
    try {
      const res = await api.patch(`/addresses/${id}/default`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== GENERIC COUPON METHODS ====================
  
  createGenericCoupon: async (data: {
    title: string;
    subtitle?: string;
    coupon_code: string;
    discount_type: string;
    flat_price?: number;
    percentage_discount?: number;
    max_discount?: number;
    min_order_value?: number;
    min_quantity?: string;
    expiry_date: string;
    max_usage?: number;
    per_user_limit?: number;
    fake_timer?: boolean;
    platform: string;
    only_new_customers?: boolean;
    global_visibility?: boolean;
    pre_applied_ad_link?: boolean;
  }): Promise<ApiResponse<GenericCoupon>> => {
    try {
      console.log('🎫 Creating generic coupon:', data);
      const res = await api.post('/admin/generic-coupons', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error creating generic coupon:', error);
      throw error;
    }
  },

  getAllGenericCoupons: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<GenericCoupon[]>> => {
    try {
      console.log('🎫 Fetching all generic coupons with params:', params);
      const res = await api.get('/admin/generic-coupons', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching generic coupons:', error);
      throw error;
    }
  },

  getGenericCouponById: async (id: number): Promise<ApiResponse<GenericCoupon>> => {
    try {
      console.log(`🎫 Fetching generic coupon ${id}...`);
      const res = await api.get(`/admin/generic-coupons/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching generic coupon:', error);
      throw error;
    }
  },

  getGenericCouponByCode: async (code: string): Promise<ApiResponse<GenericCoupon>> => {
    try {
      console.log(`🎫 Fetching generic coupon by code: ${code}`);
      const res = await api.get(`/generic-coupons/validate/${code}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching generic coupon by code:', error);
      throw error;
    }
  },

  updateGenericCoupon: async (id: number, data: {
    title?: string;
    subtitle?: string;
    discount_type?: string;
    flat_price?: number;
    percentage_discount?: number;
    max_discount?: number;
    min_order_value?: number;
    min_quantity?: string;
    expiry_date?: string;
    max_usage?: number;
    per_user_limit?: number;
    fake_timer?: boolean;
    platform?: string;
    only_new_customers?: boolean;
    global_visibility?: boolean;
    pre_applied_ad_link?: boolean;
    status?: string;
  }): Promise<ApiResponse<GenericCoupon>> => {
    try {
      console.log(`🎫 Updating generic coupon ${id}:`, data);
      const res = await api.put(`/admin/generic-coupons/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating generic coupon:', error);
      throw error;
    }
  },

  deleteGenericCoupon: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🎫 Deleting generic coupon ${id}...`);
      const res = await api.delete(`/admin/generic-coupons/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error deleting generic coupon:', error);
      throw error;
    }
  },

  toggleGenericCouponStatus: async (id: number): Promise<ApiResponse<GenericCoupon>> => {
    try {
      console.log(`🎫 Toggling generic coupon ${id} status...`);
      const res = await api.patch(`/admin/generic-coupons/${id}/toggle-status`);
      return res.data;
    } catch (error) {
      console.error('❌ Error toggling generic coupon status:', error);
      throw error;
    }
  },

  validateGenericCoupon: async (data: {
    coupon_code: string;
    user_id: number;
    order_amount: number;
    quantity: number;
  }): Promise<ApiResponse<{
    discount_amount: number;
    final_amount: number;
    coupon: GenericCoupon;
  }>> => {
    try {
      console.log(`🎫 Validating generic coupon ${data.coupon_code} for user ${data.user_id}...`);
      const res = await api.post('/generic-coupons/validate', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error validating generic coupon:', error);
      throw error;
    }
  },

  recordGenericCouponUsage: async (data: {
    coupon_id: number;
    user_id: number;
    order_id: number;
    discount_amount: number;
  }): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🎫 Recording generic coupon usage:`, data);
      const res = await api.post('/generic-coupons/usage', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error recording generic coupon usage:', error);
      throw error;
    }
  },

  getGenericCouponStats: async (id: number): Promise<ApiResponse<{
    total_users: number;
    total_discount: number;
    average_discount: number;
  }>> => {
    try {
      console.log(`🎫 Fetching generic coupon stats for ${id}...`);
      const res = await api.get(`/admin/generic-coupons/${id}/stats`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching generic coupon stats:', error);
      throw error;
    }
  },

  getActiveCoupons: async (platform?: string): Promise<ApiResponse<Array<{
    id: number;
    coupon_code: string;
    title: string;
    subtitle: string;
    discount_type: string;
    flat_price: number;
    percentage_discount: number;
    max_discount: number;
    min_order_value: number;
    min_quantity: string;
    expiry_date: string;
    fake_timer: boolean;
  }>>> => {
    try {
      const params = platform ? { platform } : {};
      console.log('🎫 Fetching active coupons for display...');
      const res = await api.get('/coupons/active', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching active coupons:', error);
      throw error;
    }
  },

  // ==================== FREE GIFT COUPON PUBLIC METHODS ====================

  getActiveFreeGiftCoupons: async (): Promise<{ 
    success: boolean; 
    data?: FreeGiftCoupon[]; 
    count?: number; 
    error?: string 
  }> => {
    try {
      console.log('🎁 Fetching active free gift coupons for customers...');
      const res = await api.get('/coupons/free-gift/active');
      console.log('🎁 Active free gift coupons response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching active free gift coupons:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to fetch coupons' 
      };
    }
  },

  createFreeGiftCoupon: async (data: CreateFreeGiftCouponRequest): Promise<ApiResponse<FreeGiftCoupon>> => {
    try {
      console.log('🎁 Creating free gift coupon:', data);
      const res = await api.post('/admin/coupons/free-gift', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error creating free gift coupon:', error);
      throw error;
    }
  },

  getAllFreeGiftCoupons: async (params?: { active?: boolean; page?: number; page_size?: number }): Promise<ApiResponse<FreeGiftCoupon[]>> => {
    try {
      console.log('🎁 Fetching all free gift coupons with params:', params);
      const res = await api.get('/admin/coupons/free-gift', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching free gift coupons:', error);
      throw error;
    }
  },

  getFreeGiftCouponById: async (id: number): Promise<ApiResponse<FreeGiftCoupon>> => {
    try {
      console.log(`🎁 Fetching free gift coupon ${id}...`);
      const res = await api.get(`/admin/coupons/free-gift/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching free gift coupon:', error);
      throw error;
    }
  },

  updateFreeGiftCoupon: async (id: number, data: Partial<CreateFreeGiftCouponRequest>): Promise<ApiResponse<FreeGiftCoupon>> => {
    try {
      console.log(`🎁 Updating free gift coupon ${id}:`, data);
      const res = await api.put(`/admin/coupons/free-gift/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating free gift coupon:', error);
      throw error;
    }
  },

  deleteFreeGiftCoupon: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🎁 Deleting free gift coupon ${id}...`);
      const res = await api.delete(`/admin/coupons/free-gift/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error deleting free gift coupon:', error);
      throw error;
    }
  },

  toggleFreeGiftCouponStatus: async (id: number): Promise<ApiResponse<FreeGiftCoupon>> => {
    try {
      console.log(`🎁 Toggling free gift coupon ${id} status...`);
      const res = await api.patch(`/admin/coupons/free-gift/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Error toggling free gift coupon status:', error);
      throw error;
    }
  },

  validateFreeGiftCoupon: async (data: {
    coupon_code: string;
    user_id: number;
    cart_total: number;
    item_count: number;
  }): Promise<ApiResponse<FreeGiftCoupon & { message?: string }>> => {
    try {
      console.log(`🎁 Validating free gift coupon ${data.coupon_code}...`);
      const res = await api.post('/coupons/free-gift/validate', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error validating free gift coupon:', error);
      throw error;
    }
  },

  recordFreeGiftCouponUsage: async (data: {
    coupon_id: number;
    user_id: number;
    order_id: number;
  }): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🎁 Recording free gift coupon usage:`, data);
      const res = await api.post('/coupons/free-gift/usage', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error recording free gift coupon usage:', error);
      throw error;
    }
  },

  // ==================== BXGY COUPON METHODS ====================

  createBXGYCoupon: async (data: CreateBXGYCouponRequest): Promise<ApiResponse<BXGYCoupon>> => {
    try {
      console.log('🎁 Creating BXGY coupon:', data);
      const res = await api.post('/admin/bxgy-coupons', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error creating BXGY coupon:', error);
      throw error;
    }
  },

  getAllBXGYCoupons: async (params?: GetAllBXGYCouponsParams): Promise<ApiResponse<{
    coupons: BXGYCoupon[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>> => {
    try {
      console.log('🎁 Fetching all BXGY coupons with params:', params);
      const res = await api.get('/admin/bxgy-coupons', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching BXGY coupons:', error);
      throw error;
    }
  },

  getBXGYCouponById: async (id: number): Promise<ApiResponse<BXGYCoupon>> => {
    try {
      console.log(`🎁 Fetching BXGY coupon ${id}...`);
      const res = await api.get(`/admin/bxgy-coupons/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching BXGY coupon:', error);
      throw error;
    }
  },

  updateBXGYCoupon: async (id: number, data: UpdateBXGYCouponRequest): Promise<ApiResponse<BXGYCoupon>> => {
    try {
      console.log(`🎁 Updating BXGY coupon ${id}:`, data);
      const res = await api.put(`/admin/bxgy-coupons/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating BXGY coupon:', error);
      throw error;
    }
  },

  deleteBXGYCoupon: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🎁 Deleting BXGY coupon ${id}...`);
      const res = await api.delete(`/admin/bxgy-coupons/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error deleting BXGY coupon:', error);
      throw error;
    }
  },

  toggleBXGYCouponStatus: async (id: number): Promise<ApiResponse<BXGYCoupon>> => {
    try {
      console.log(`🎁 Toggling BXGY coupon ${id} status...`);
      const res = await api.patch(`/admin/bxgy-coupons/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Error toggling BXGY coupon status:', error);
      throw error;
    }
  },

  getBXGYCouponStats: async (id: number): Promise<ApiResponse<BXGYCouponStats>> => {
    try {
      console.log(`🎁 Fetching BXGY coupon stats for ${id}...`);
      const res = await api.get(`/admin/bxgy-coupons/${id}/stats`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching BXGY coupon stats:', error);
      throw error;
    }
  },

  validateBXGYCoupon: async (data: ValidateBXGYCouponRequest): Promise<ValidateBXGYCouponResponse> => {
    try {
      console.log(`🎁 Validating BXGY coupon ${data.offerCode}...`);
      const res = await api.post('/bxgy-coupons/validate', data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error validating BXGY coupon:', error);
      return {
        success: false,
        data: {
          coupon: {} as BXGYCoupon,
          freeProducts: [],
          message: error.response?.data?.error || error.message || 'Failed to validate coupon'
        }
      };
    }
  },

  recordBXGYCouponUsage: async (data: RecordBXGYCouponUsageRequest): Promise<ApiResponse<{
    usageId: number;
    usageCount: number;
    remaining: number;
  }>> => {
    try {
      console.log(`🎁 Recording BXGY coupon usage:`, data);
      const res = await api.post('/bxgy-coupons/usage', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error recording BXGY coupon usage:', error);
      throw error;
    }
  },

  getActiveBXGYCoupons: async (platform?: string): Promise<ApiResponse<BXGYCoupon[]>> => {
    try {
      console.log('🎁 Fetching active BXGY coupons...');
      const params = platform ? { platform } : {};
      const res = await api.get('/bxgy-coupons/active', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching active BXGY coupons:', error);
      throw error;
    }
  },

  getBXGYCouponByCode: async (code: string): Promise<ApiResponse<{
    id: number;
    title: string;
    subtitle: string;
    offerCode: string;
    minOrderValue: number;
    expiryDate: string;
    fakeTimer: boolean;
    productSelection: string;
    buyProducts: number[];
    freeProducts: number[];
    isActive: boolean;
  }>> => {
    try {
      console.log(`🎁 Fetching BXGY coupon by code: ${code}`);
      const res = await api.get(`/bxgy-coupons/code/${code}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching BXGY coupon by code:', error);
      throw error;
    }
  },

  // ==================== PARTICULAR CUSTOMER COUPON METHODS ====================

  getCustomers: async (): Promise<{ success: boolean; data?: Array<{ id: number; name: string; email: string; phone: string; totalOrders: number; totalSpent: number }>; error?: string }> => {
    try {
      console.log('👥 Fetching customers for particular customer coupon...');
      const res = await api.get('/admin/particular-customer-coupons/customers');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching customers:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch customers' };
    }
  },

  assignParticularCustomerCoupon: async (data: {
    code: string;
    customerId: number;
    discountType: string;
    discountValue: number;
    validUntil: string;
    minOrderAmount: number;
    maxDiscount: number;
    usageLimit: number;
  }): Promise<{ success: boolean; data?: any; message?: string; error?: string }> => {
    try {
      console.log('🎫 Assigning particular customer coupon:', data);
      const res = await api.post('/admin/particular-customer-coupons/assign', data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error assigning coupon:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to assign coupon' };
    }
  },

  getParticularCustomerCoupons: async (): Promise<{ success: boolean; data?: Array<{
    id: string;
    customerId: number;
    customerName: string;
    customerEmail: string;
    couponCode: string;
    discountType: string;
    discountValue: number;
    validUntil: string;
    minOrderAmount: number;
    maxDiscount: number;
    usageLimit: number;
    usedCount: number;
    isRedeemed: boolean;
    redeemedAt?: string;
    createdAt: string;
  }>; error?: string }> => {
    try {
      console.log('🎫 Fetching assigned particular customer coupons...');
      const res = await api.get('/admin/particular-customer-coupons');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching coupons:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch coupons' };
    }
  },

  deleteParticularCustomerCoupon: async (id: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`🗑️ Deleting particular customer coupon ${id}...`);
      const res = await api.delete(`/admin/particular-customer-coupons/${id}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error deleting coupon:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to delete coupon' };
    }
  },

  redeemParticularCustomerCoupon: async (id: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`✅ Redeeming particular customer coupon ${id}...`);
      const res = await api.post(`/customer/coupons/${id}/redeem`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error redeeming coupon:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to redeem coupon' };
    }
  },

  getParticularCustomerCouponStats: async (): Promise<{ success: boolean; data?: { totalCoupons: number; redeemedCount: number; activeCount: number }; error?: string }> => {
    try {
      console.log('📊 Fetching particular customer coupon stats...');
      const res = await api.get('/admin/particular-customer-coupons/stats');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching stats:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch stats' };
    }
  },

  // ==================== POST CHECKOUT OFFER METHODS ====================
  
  getAllPostCheckoutOffers: async (enabled?: boolean): Promise<{
    success: boolean;
    data?: PostCheckoutOffer[];
    error?: string;
  }> => {
    try {
      console.log('🎫 Fetching all post-checkout offers...');
      const params = enabled !== undefined ? { enabled: enabled.toString() } : {};
      const res = await api.get('/admin/post-checkout-offers', { params });
      console.log('🎫 Post-checkout offers response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch offers' };
    } catch (error: any) {
      console.error('❌ Error fetching post-checkout offers:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch offers' 
      };
    }
  },

  getPostCheckoutOfferById: async (id: number): Promise<{
    success: boolean;
    data?: PostCheckoutOffer;
    error?: string;
  }> => {
    try {
      console.log(`🎫 Fetching post-checkout offer ${id}...`);
      const res = await api.get(`/admin/post-checkout-offers/${id}`);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch offer' };
    } catch (error: any) {
      console.error('❌ Error fetching post-checkout offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch offer' 
      };
    }
  },

  createPostCheckoutOffer: async (data: CreatePostCheckoutOfferData): Promise<{
    success: boolean;
    data?: PostCheckoutOffer;
    error?: string;
  }> => {
    try {
      console.log('🎫 Creating post-checkout offer:', data);
      const res = await api.post('/admin/post-checkout-offers', data);
      console.log('🎫 Create offer response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to create offer' };
    } catch (error: any) {
      console.error('❌ Error creating post-checkout offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to create offer' 
      };
    }
  },

  updatePostCheckoutOffer: async (id: number, data: UpdatePostCheckoutOfferData): Promise<{
    success: boolean;
    data?: PostCheckoutOffer;
    error?: string;
  }> => {
    try {
      console.log(`🎫 Updating post-checkout offer ${id}:`, data);
      const res = await api.put(`/admin/post-checkout-offers/${id}`, data);
      console.log('🎫 Update offer response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to update offer' };
    } catch (error: any) {
      console.error('❌ Error updating post-checkout offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update offer' 
      };
    }
  },

  deletePostCheckoutOffer: async (id: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`🎫 Deleting post-checkout offer ${id}...`);
      const res = await api.delete(`/admin/post-checkout-offers/${id}`);
      console.log('🎫 Delete offer response:', res.data);
      
      if (res.data.success) {
        return { success: true, message: res.data.message || 'Offer deleted successfully' };
      }
      return { success: false, error: res.data.message || 'Failed to delete offer' };
    } catch (error: any) {
      console.error('❌ Error deleting post-checkout offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete offer' 
      };
    }
  },

  togglePostCheckoutOfferStatus: async (id: number): Promise<{
    success: boolean;
    data?: PostCheckoutOffer;
    error?: string;
  }> => {
    try {
      console.log(`🎫 Toggling post-checkout offer ${id} status...`);
      const res = await api.patch(`/admin/post-checkout-offers/${id}/toggle`);
      console.log('🎫 Toggle offer response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to toggle offer status' };
    } catch (error: any) {
      console.error('❌ Error toggling post-checkout offer status:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to toggle offer status' 
      };
    }
  },

  // ==================== CUSTOMER POST-CHECKOUT OFFER METHODS ====================
  
  getActivePostCheckoutOffer: async (): Promise<{
    success: boolean;
    data?: PostCheckoutOffer;
    error?: string;
  }> => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await api.get('/post-checkout-offer/active');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error fetching active offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to fetch offer' 
      };
    }
  },

  claimPostCheckoutOffer: async (offerId: number, orderId: number): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> => {
    try {
      const response = await api.post('/post-checkout-offer/claim', { offerId, orderId });
      return response.data;
    } catch (error: any) {
      console.error('❌ Error claiming offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to claim offer' 
      };
    }
  },

  trackPostCheckoutOfferUsage: async (couponCode: string): Promise<{
    success: boolean;
    data?: {
      valid: boolean;
      couponCode: string;
      discountType: string;
      discount: number;
      discountAmount: number;
      maxDiscount: number;
      message: string;
    };
    error?: string;
  }> => {
    try {
      const response = await api.post('/post-checkout-offer/track-usage', { couponCode });
      return response.data;
    } catch (error: any) {
      console.error('❌ Error tracking offer usage:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to validate coupon' 
      };
    }
  },

  getUserPostCheckoutOfferStatus: async (): Promise<{
    success: boolean;
    data?: Array<{
      offer: PostCheckoutOffer;
      usedCount: number;
      remainingUses: number;
      canUse: boolean;
    }>;
    error?: string;
  }> => {
    try {
      const response = await api.get('/post-checkout-offer/my-status');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error fetching user offer status:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to fetch offer status' 
      };
    }
  },

  getPostCheckoutOfferUsageHistory: async (offerId: number): Promise<{
    success: boolean;
    data?: {
      usages: any[];
      totalUses: number;
      uniqueUsers: number;
    };
    error?: string;
  }> => {
    try {
      const response = await api.get(`/admin/post-checkout-offers/${offerId}/usage`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error fetching usage history:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to fetch usage history' 
      };
    }
  },

  // ==================== OFFERS API ====================

  getAllOffers: async (): Promise<{ 
    success: boolean; 
    data?: Offer[]; 
    error?: string 
  }> => {
    try {
      console.log('🎫 Fetching all offers...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get('/customer/offers');
      console.log('🎫 Offers response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch offers' };
    } catch (error: any) {
      console.error('❌ Error fetching offers:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to load offers from server'
      };
    }
  },

  getActiveOffers: async (): Promise<{ 
    success: boolean; 
    data?: Offer[]; 
    error?: string 
  }> => {
    try {
      console.log('🎫 Fetching active offers...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get('/customer/offers/active');
      console.log('🎫 Active offers response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch active offers' };
    } catch (error: any) {
      console.error('❌ Error fetching active offers:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch active offers'
      };
    }
  },

  validateOfferCode: async (data: {
    code: string;
    order_total: number;
  }): Promise<{ 
    success: boolean; 
    data?: { 
      valid: boolean;
      discount_amount: number;
      final_amount: number;
      message: string;
      coupon: Offer;
    }; 
    error?: string 
  }> => {
    try {
      console.log(`🎫 Validating offer code ${data.code}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.post('/customer/offers/validate', data);
      console.log('🎫 Validate offer response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error validating offer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid offer code' 
      };
    }
  },

  // ==================== WAREHOUSE SELECTION FOR ORDERS ====================

  getAvailableWarehousesForOrder: async (orderId: number, pincode: string): Promise<{
    success: boolean;
    data?: {
      warehouses: Array<{
        id: string;
        addressLine: string;
        landmark: string;
        pincode: string;
        city: string;
        state: string;
        country: string;
        operationalDays: string;
        openingTime: string;
        closingTime: string;
        primaryContact: {
          name: string;
          contactNumber: string;
          email: string;
          role: string;
        };
        alternateContact: {
          name: string;
          alternatePhone: string;
          email: string;
          role: string;
        };
        addAsSupplier: boolean;
        useDifferentRTO: boolean;
        rtoAddress: string;
        isActive: boolean;
        shiprocketPickupLocation?: string;
        hasStock: boolean;
        missingItems: string[];
        distance: number;
        deliveryDays: number;
        shippingCost: number;
        isCurrent: boolean;
      }>;
      order: {
        id: number;
        order_number: string;
        current_warehouse_id: string | null;
      };
    };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`🏭 Fetching available warehouses for order ${orderId} with pincode ${pincode}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get(`/admin/orders/${orderId}/available-warehouses`, {
        params: { pincode }
      });
      console.log('🏭 Available warehouses response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching available warehouses:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch warehouses' 
      };
    }
  },

  assignWarehouseToOrder: async (orderId: number, warehouseId: string, reassignCourier: boolean = false): Promise<{
    success: boolean;
    data?: {
      order: Order;
      warehouse: {
        id: string;
        name: string;
        city: string;
        contact_person: string;
        contact_number: string;
        operational_days: string;
        timings: string;
      };
      reassign_courier: boolean;
    };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`🏭 Assigning warehouse ${warehouseId} to order ${orderId}, reassign courier: ${reassignCourier}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.post(`/admin/orders/${orderId}/assign-warehouse`, {
        warehouse_id: warehouseId,
        reassign_courier: reassignCourier
      });
      console.log('🏭 Assign warehouse response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error assigning warehouse:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to assign warehouse' 
      };
    }
  },

  // ==================== CART METHODS WITH GUEST SUPPORT ====================
  
  getCart: async (): Promise<ApiResponse<Cart>> => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('🛒 Guest mode: Fetching cart from localStorage');
        const guestCart = getGuestCart();
        if (guestCart && guestCart.items && guestCart.items.length > 0) {
          const normalizedCart = normalizeGuestCart(guestCart);
          return { success: true, data: normalizedCart };
        }
        return { success: true, data: createEmptyCart() };
      }
      
      console.log('🛒 Logged in mode: Fetching cart from API');
      const res = await api.get('/cart');
      
      if (res.data.success && res.data.data) {
        saveGuestCart(res.data.data);
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching cart:', error);
      const guestCart = getGuestCart();
      if (guestCart && guestCart.items && guestCart.items.length > 0) {
        const normalizedCart = normalizeGuestCart(guestCart);
        return { success: true, data: normalizedCart };
      }
      throw error;
    }
  },
  
  addToCart: async (productId: number, quantity: number): Promise<ApiResponse<Cart>> => {
    try {
      const token = localStorage.getItem('token');
      const payload: any = { product_id: productId, quantity };
      
      if (!token) {
        console.log('🛒 Guest mode: Adding to cart in localStorage');
        
        let guestCart = getGuestCart();
        if (!guestCart || !guestCart.items) {
          guestCart = { items: [] };
        }
        
        const existingItemIndex = guestCart.items.findIndex(
          (item: any) => item.product_id === productId || item.product?.id === productId
        );
        
        if (existingItemIndex > -1) {
          guestCart.items[existingItemIndex].quantity += quantity;
        } else {
          // Fetch complete product data
          let productData = null;
          
          try {
            const productResponse = await apiService.getProduct(productId);
            console.log('📦 Product fetch response:', productResponse);
            
            if (productResponse.success && productResponse.data) {
              productData = productResponse.data;
            } else {
              throw new Error('Product fetch failed');
            }
          } catch (firstError) {
            console.error('First attempt to fetch product failed:', firstError);
            // Retry once with direct API call to handle nested response
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              const directResponse = await api.get(`/products/${productId}`);
              console.log('📦 Direct product fetch response:', directResponse.data);
              
              // Handle nested response structure from Go backend
              if (directResponse.data && directResponse.data.success && directResponse.data.data) {
                if (directResponse.data.data.product) {
                  productData = directResponse.data.data.product;
                } else {
                  productData = directResponse.data.data;
                }
              } else if (directResponse.data && directResponse.data.product) {
                productData = directResponse.data.product;
              } else if (directResponse.data && directResponse.data.id) {
                productData = directResponse.data;
              }
            } catch (retryError) {
              console.error('Retry also failed:', retryError);
            }
          }
          
          if (productData) {
            // CRITICAL FIX: Ensure selling_price is properly set
            const sellingPrice = productData.selling_price || productData.SellingPrice || 0;
            const mrp = productData.mrp || productData.MRP || 0;
            const stock = typeof productData.stock === 'number' ? productData.stock : Number(productData.stock ?? 0);
            
            console.log(`✅ Adding product to cart:`, {
              id: productId,
              name: productData.name,
              selling_price: sellingPrice,
              mrp: mrp,
              stock: stock
            });
            
            guestCart.items.push({
              id: Date.now() + Math.random(),
              product_id: productId,
              product: {
                id: productData.id,
                name: productData.name,
                selling_price: sellingPrice,
                mrp: mrp,
                stock: stock,
                sku: productData.sku || '',
                description: productData.description || '',
                short_description: productData.short_description || '',
                images: productData.images || [],
                categories: productData.categories || [],
                cgst_percent: productData.cgst_percent || 0,
                sgst_percent: productData.sgst_percent || 0,
                igst_percent: productData.igst_percent || 0,
              },
              quantity: quantity,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              customer_id: 0
            });
          } else {
            // Last resort - show error to user
            toast.error('Unable to add product. Please try again.');
            return { success: false, data: createEmptyCart(), error: 'Failed to fetch product details' };
          }
        }
        
        saveGuestCart(guestCart);
        const normalizedCart = normalizeGuestCart(guestCart);
        return { success: true, data: normalizedCart };
      }
      
      console.log('🛒 Adding to cart via API for logged-in user');
      const res = await api.post('/cart/add', payload);
      
      if (res.data.success && res.data.data) {
        saveGuestCart(res.data.data);
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Add to cart error:', error);
      throw error;
    }
  },
  
  updateCartItem: async (itemId: number, quantity: number): Promise<ApiResponse<Cart>> => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('🛒 Guest mode: Updating cart item in localStorage');
        const guestCart = getGuestCart();
        
        if (guestCart && guestCart.items) {
          const itemIndex = guestCart.items.findIndex((item: any) => item.id === itemId);
          if (itemIndex > -1) {
            guestCart.items[itemIndex].quantity = quantity;
            saveGuestCart(guestCart);
            const normalizedCart = normalizeGuestCart(guestCart);
            return { success: true, data: normalizedCart };
          }
        }
        return { success: false, data: createEmptyCart(), error: 'Item not found' };
      }
      
      console.log('🛒 Updating cart item via API');
      const res = await api.put(`/cart/update/${itemId}`, { quantity });
      
      if (res.data.success && res.data.data) {
        saveGuestCart(res.data.data);
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Update cart item error:', error);
      throw error;
    }
  },
  
  removeFromCart: async (itemId: number): Promise<ApiResponse<Cart>> => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('🛒 Guest mode: Removing cart item from localStorage');
        const guestCart = getGuestCart();
        
        if (guestCart && guestCart.items) {
          guestCart.items = guestCart.items.filter((item: any) => item.id !== itemId);
          saveGuestCart(guestCart);
          const normalizedCart = normalizeGuestCart(guestCart);
          return { success: true, data: normalizedCart };
        }
        return { success: true, data: createEmptyCart() };
      }
      
      console.log('🛒 Removing cart item via API');
      const res = await api.delete(`/cart/remove/${itemId}`);
      
      if (res.data.success && res.data.data) {
        saveGuestCart(res.data.data);
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Remove from cart error:', error);
      throw error;
    }
  },
  
  clearCart: async (): Promise<ApiResponse<{ message: string }>> => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('🛒 Guest mode: Clearing cart from localStorage');
        clearGuestCart();
        return { success: true, data: { message: 'Cart cleared successfully' } };
      }
      
      console.log('🛒 Clearing cart via API');
      const res = await api.delete('/cart/clear');
      
      if (res.data.success) {
        clearGuestCart();
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Clear cart error:', error);
      throw error;
    }
  },
  
  mergeGuestCart: async (): Promise<ApiResponse<Cart>> => {
    try {
      const guestCart = getGuestCart();
      if (!guestCart || !guestCart.items || guestCart.items.length === 0) {
        console.log('🛒 No guest cart to merge');
        clearGuestCart();
        return { success: true, data: createEmptyCart() };
      }
      
      console.log(`🛒 Merging guest cart with ${guestCart.items.length} items to user cart`);
      const res = await api.post('/cart/merge', { guest_cart: guestCart });
      
      if (res.data.success) {
        clearGuestCart();
        localStorage.removeItem(GUEST_CART_ID_KEY);
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Merge cart error:', error);
      return { success: false, data: createEmptyCart(), error: 'Failed to merge cart' };
    }
  },

  // ==================== ADMIN CUSTOMERS ====================

  getAllCustomers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    call_status?: string;
    date_from?: string; // YYYY-MM-DD (inclusive)
    date_to?: string; // YYYY-MM-DD (inclusive)
    date_field?: 'joined' | 'rejoined' | 'any';
    min_orders?: number;
    max_orders?: number;
    sort?: string; // latest | oldest | orders_desc | orders_asc
  }): Promise<{
    success: boolean;
    data?: AdminCustomer[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
    error?: string;
  }> => {
    try {
      console.log('👥 Fetching all customers with params:', params);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get('/admin/customers', { params });
      console.log('👥 Customers response:', res.data);
      
      if (res.data.success) {
        return {
          success: true,
          data: res.data.data,
          pagination: res.data.pagination
        };
      }
      return { success: false, error: res.data.message || 'Failed to fetch customers' };
    } catch (error: any) {
      console.error('❌ Error fetching customers:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch customers' 
      };
    }
  },

  getCustomerById: async (id: number): Promise<{
    success: boolean;
    data?: AdminCustomerDetails;
    error?: string;
  }> => {
    try {
      console.log(`👥 Fetching customer ${id} details...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get(`/admin/customers/${id}`);
      console.log(`👥 Customer ${id} response:`, res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch customer' };
    } catch (error: any) {
      console.error('❌ Error fetching customer:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch customer' 
      };
    }
  },

  // ==================== NEW: GET CUSTOMER ADDRESSES ====================

  getCustomerAddresses: async (customerId: number): Promise<ApiResponse<Address[]>> => {
    try {
      console.log(`🏠 Fetching addresses for customer ${customerId}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, data: [], error: 'Not authenticated' };
      }
      
      const res = await api.get(`/admin/customers/${customerId}/addresses`);
      console.log(`🏠 Customer ${customerId} addresses response:`, res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching customer addresses:', error);
      return { 
        success: false, 
        data: [], 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch addresses' 
      };
    }
  },

  updateCustomerStatus: async (id: number, isActive: boolean): Promise<{
    success: boolean;
    data?: any;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`👥 Updating customer ${id} status to ${isActive ? 'active' : 'inactive'}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.patch(`/admin/customers/${id}/status`, { is_active: isActive });
      console.log('👥 Update status response:', res.data);
      
      return res.data;
    } catch (error: any) {
      console.error('❌ Error updating customer status:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update customer status' 
      };
    }
  },

  updateCustomerCallStatus: async (id: number, callStatus: string): Promise<{
    success: boolean;
    data?: any;
    message?: string;
    error?: string;
  }> => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const res = await api.patch(`/admin/customers/${id}/call-status`, { call_status: callStatus });
      return res.data;
    } catch (error: any) {
      console.error('❌ Error updating customer call status:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update call status'
      };
    }
  },

  // ==================== EMPLOYEE CUSTOMERS (needs "customers" module permission) ====================
  employeeGetCustomers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    call_status?: string;
  }): Promise<{
    success: boolean;
    data: any[];
    pagination?: { page: number; limit: number; total: number; total_pages: number };
    error?: string;
  }> => {
    try {
      const res = await api.get('/employee/customers', { params });
      // Backend wraps the list as { data: { data: [...], pagination } }
      const payload = res.data?.data;
      const list = Array.isArray(payload) ? payload : (payload?.data ?? []);
      const pagination = res.data?.pagination ?? payload?.pagination;
      return { success: !!res.data?.success, data: list, pagination };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch customers',
      };
    }
  },

  employeeUpdateCustomerStatus: async (id: number, isActive: boolean): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    try {
      const res = await api.patch(`/employee/customers/${id}/status`, { is_active: isActive });
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update customer status',
      };
    }
  },

  employeeUpdateCustomerCallStatus: async (id: number, callStatus: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    try {
      const res = await api.patch(`/employee/customers/${id}/call-status`, { call_status: callStatus });
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update call status',
      };
    }
  },

  // ==================== PHONE BLOCKLIST (admin) ====================
  uploadBlockedPhones: async (payload: { text: string; reason?: string; source?: 'upload' | 'manual' }): Promise<{
    success: boolean;
    data?: {
      added: number;
      already_blocked: number;
      invalid_count: number;
      invalid_numbers: string[];
      customer_accounts_blocked: number;
    };
    error?: string;
  }> => {
    try {
      const res = await api.post('/admin/blocked-phones/upload', payload);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to upload blocked numbers'
      };
    }
  },

  getBlockedPhones: async (params?: { page?: number; limit?: number; search?: string }): Promise<{
    success: boolean;
    data?: { data: any[]; pagination?: { page: number; limit: number; total: number; total_pages: number } };
    error?: string;
  }> => {
    try {
      const res = await api.get('/admin/blocked-phones', { params });
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blocked numbers'
      };
    }
  },

  deleteBlockedPhone: async (id: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await api.delete(`/admin/blocked-phones/${id}`);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to unblock number'
      };
    }
  },

  // ==================== COD CONFIGURATION (block COD by state / district / pincode) ====================
  getCodBlocks: async (params?: { type?: 'STATE' | 'DISTRICT' | 'PINCODE'; search?: string; page?: number; limit?: number }): Promise<{
    success: boolean;
    data?: { data: any[]; pagination?: { page: number; limit: number; total: number; total_pages: number } };
    error?: string;
  }> => {
    try {
      const res = await api.get(codCfgUrl(''), { params });
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch COD rules' };
    }
  },

  addCodBlocks: async (payload: { type: 'STATE' | 'DISTRICT' | 'PINCODE'; text: string; reason?: string }): Promise<{
    success: boolean;
    data?: { added: number; already_listed: number; invalid_count: number; invalid_values: string[] };
    message?: string;
    error?: string;
  }> => {
    try {
      const res = await api.post(codCfgUrl(''), payload);
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Failed to add COD rules' };
    }
  },

  updateCodBlock: async (id: number, payload: { is_blocked?: boolean; reason?: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await api.patch(codCfgUrl(`/${id}`), payload);
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Failed to update COD rule' };
    }
  },

  deleteCodBlock: async (id: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await api.delete(codCfgUrl(`/${id}`));
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete COD rule' };
    }
  },

  bulkDeleteCodBlocks: async (ids: number[]): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await api.post(codCfgUrl('/bulk-delete'), { ids });
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete COD rules' };
    }
  },

  // Public: checkout asks whether COD is allowed for this location (fails open on error)
  checkCodEligibility: async (loc: { state?: string; district?: string; pincode?: string }): Promise<{
    cod_available: boolean; failed_at?: string; message?: string;
  }> => {
    try {
      const res = await api.post('/cod/eligibility', loc);
      return { cod_available: res.data?.cod_available !== false, failed_at: res.data?.failed_at, message: res.data?.message };
    } catch {
      return { cod_available: true };
    }
  },

  getCustomerOrders: async (id: number, params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data?: Order[];
    pagination?: any;
    error?: string;
  }> => {
    try {
      console.log(`📦 Fetching orders for customer ${id}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get(`/admin/customers/${id}/orders`, { params });
      console.log(`📦 Customer ${id} orders response:`, res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data, pagination: res.data.pagination };
      }
      return { success: false, error: res.data.message || 'Failed to fetch customer orders' };
    } catch (error: any) {
      console.error('❌ Error fetching customer orders:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch customer orders' 
      };
    }
  },

  getCustomerWalletTransactions: async (id: number, params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data?: CustomerTransaction[];
    pagination?: any;
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching wallet transactions for customer ${id}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get(`/admin/customers/${id}/wallet-transactions`, { params });
      console.log(`💰 Customer ${id} wallet transactions response:`, res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data, pagination: res.data.pagination };
      }
      return { success: false, error: res.data.message || 'Failed to fetch wallet transactions' };
    } catch (error: any) {
      console.error('❌ Error fetching wallet transactions:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch wallet transactions' 
      };
    }
  },

  // ==================== PUBLIC ABOUT ====================
  getAboutContent: async (): Promise<ApiResponse<About | null>> => {
    try {
      const res = await api.get('/about');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== PUBLIC CONTACT ====================
  getActiveContact: async (): Promise<ApiResponse<Contact | null>> => {
    try {
      const res = await api.get('/contact');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  submitContactForm: async (data: {
    name: string;
    email: string;
    phone?: string;
    subject: string;
    message: string;
  }): Promise<ApiResponse<ContactForm>> => {
    try {
      const res = await api.post('/contact/submit', data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== PUBLIC SLIDES ====================
  getActiveSlides: async (): Promise<ApiResponse<Slide[]>> => {
    try {
      const res = await api.get('/slides/active');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== PRODUCTS ====================
  getProducts: async (params?: any): Promise<ApiPaginatedResponse<Product>> => {
    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let endpoint = '/products';
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.role === 'employee') {
            endpoint = '/employee/products';
            console.log('📦 Employee user - using /employee/products endpoint for warehouse data');
          } else if (user.role === 'admin') {
            endpoint = '/admin/products';
            console.log('📦 Admin user - using /admin/products endpoint');
          }
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      // 🔗 Shared product link: always use the public endpoint so every visitor
      // (even a logged-in admin/employee) sees exactly the shared products
      if (params?.share) endpoint = '/products';

      console.log(`📦 Fetching products from ${endpoint} with params:`, params);
      const res = await api.get(endpoint, { params });
      
      if (res.data?.data && res.data.data.length > 0) {
        console.log('📦 Sample product warehouse_allocations:', 
          res.data.data[0]?.warehouse_allocations || 'No warehouse data');
      }
      
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      throw error;
    }
  },
  
  getProduct: async (id: number): Promise<ApiResponse<Product>> => {
    try {
      const res = await api.get(`/products/${id}`);
      console.log(`📦 Product ${id} API response:`, res.data);
      
      // Handle different response structures from Go backend
      if (res.data.success && res.data.data) {
        // Backend returns { success: true, data: { product: {...}, average_rating, review_count } }
        if (res.data.data.product) {
          return { success: true, data: res.data.data.product };
        }
        return { success: true, data: res.data.data };
      }
      
      // Fallback for direct product response
      if (res.data.id && res.data.name) {
        return { success: true, data: res.data };
      }
      
      return res.data;
    } catch (error) {
      console.error(`❌ Error fetching product ${id}:`, error);
      throw error;
    }
  },
  
  getFeaturedProducts: async (limit = 8): Promise<ApiResponse<Product[]>> => {
    try {
      const res = await api.get('/products/featured', { params: { limit } });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getFestiveProducts: async (limit = 8): Promise<ApiResponse<Product[]>> => {
    try {
      const res = await api.get('/products/festive', { params: { limit } });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getNewArrivals: async (limit = 10): Promise<ApiResponse<Product[]>> => {
    try {
      const res = await api.get('/products/new-arrivals', { params: { limit } });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  searchProducts: async (query: string, params?: any): Promise<ApiPaginatedResponse<Product>> => {
    try {
      const res = await api.get('/products/search', { params: { q: query, ...params } });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getProductsByCategory: async (categoryId: number, params?: any): Promise<ApiPaginatedResponse<Product>> => {
    try {
      const res = await api.get(`/products/category/${categoryId}`, { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== REVIEWS ====================
  getProductReviews: async (
    productId: number,
    params?: { page?: number; page_size?: number; rating?: number; sort?: 'recent' | 'helpful' | 'highest' | 'lowest'; }
  ): Promise<ApiResponse<{ summary: ReviewSummary; reviews: Review[]; }>> => {
    try {
      console.log(`📝 Fetching reviews for product ${productId} with params:`, params);
      const res = await api.get(`/products/${productId}/reviews`, { params });
      return res.data;
    } catch (error) {
      console.error('❌ Get product reviews error:', error);
      throw error;
    }
  },

  createReview: async (productId: number, data: { rating: number; title: string; review: string; order_id?: number; images?: string[]; }): Promise<ApiResponse<Review>> => {
    try {
      console.log(`📝 Creating review for product ${productId}:`, data);
      const res = await api.post(`/products/${productId}/reviews`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Create review error:', error);
      throw error;
    }
  },

  getMyReview: async (productId: number): Promise<ApiResponse<Review | null>> => {
    try {
      console.log(`📝 Fetching my review for product ${productId}`);
      const res = await api.get(`/products/${productId}/reviews/my`);
      return res.data;
    } catch (error) {
      console.error('❌ Get my review error:', error);
      throw error;
    }
  },

  updateReview: async (productId: number, reviewId: number, data: { rating: number; title: string; review: string; images?: string[]; }): Promise<ApiResponse<Review>> => {
    try {
      console.log(`📝 Updating review ${reviewId} for product ${productId}:`, data);
      const res = await api.put(`/products/${productId}/reviews/${reviewId}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Update review error:', error);
      throw error;
    }
  },

  deleteReview: async (productId: number, reviewId: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`📝 Deleting review ${reviewId} for product ${productId}`);
      const res = await api.delete(`/products/${productId}/reviews/${reviewId}`);
      return res.data;
    } catch (error) {
      console.error('❌ Delete review error:', error);
      throw error;
    }
  },

  markReviewHelpful: async (productId: number, reviewId: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`📝 Marking review ${reviewId} as helpful for product ${productId}`);
      const res = await api.post(`/products/${productId}/reviews/${reviewId}/helpful`);
      return res.data;
    } catch (error) {
      console.error('❌ Mark review helpful error:', error);
      throw error;
    }
  },

  uploadReviewImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      console.log('📸 Uploading review image:', file.name);
      const res = await api.post('/products/upload-review-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `${BASE_URL}${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  // ==================== ADMIN REVIEWS ====================
  adminGetAllReviews: async (params?: { page?: number; page_size?: number; product_id?: number; is_approved?: boolean; }): Promise<ApiPaginatedResponse<Review>> => {
    try {
      console.log('📝 Admin fetching all reviews with params:', params);
      const res = await api.get('/admin/reviews', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Admin get all reviews error:', error);
      throw error;
    }
  },

  adminToggleReviewApproval: async (reviewId: number): Promise<ApiResponse<Review>> => {
    try {
      console.log(`📝 Admin toggling approval for review ${reviewId}`);
      const res = await api.patch(`/admin/reviews/${reviewId}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Admin toggle review approval error:', error);
      throw error;
    }
  },

  // ==================== CATEGORIES (PUBLIC) ====================
  getCategories: async (): Promise<ApiResponse<Category[]>> => {
    try {
      const res = await api.get('/categories');
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  
  getCategoryById: async (id: number): Promise<ApiResponse<Category>> => {
    try {
      const res = await api.get(`/categories/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== AGE GROUPS (PUBLIC) ====================
  getAgeGroups: async (): Promise<ApiResponse<AgeGroup[]>> => {
    try {
      const res = await api.get('/age-groups');
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  
  getAgeGroupById: async (id: number): Promise<ApiResponse<AgeGroup>> => {
    try {
      const res = await api.get(`/age-groups/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== WISHLIST ====================
  getWishlist: async (): Promise<ApiResponse<Product[]>> => {
    try {
      const res = await api.get('/wishlist');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  addToWishlist: async (productId: number): Promise<ApiResponse<{ action: string }>> => {
    try {
      const res = await api.post('/wishlist/add', { product_id: productId });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  removeFromWishlist: async (productId: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/wishlist/remove/${productId}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  checkWishlistStatus: async (productIds: number[]): Promise<ApiResponse<Record<string, boolean>>> => {
    try {
      console.log('🔍 Checking wishlist status for products:', productIds);
      const res = await api.post('/wishlist/check-status', { product_ids: productIds });
      return res.data;
    } catch (error) {
      console.error('❌ Check wishlist status error:', error);
      throw error;
    }
  },

  // ==================== ORDERS ====================
  createOrder: async (orderData: any): Promise<ApiResponse<Order>> => { 
    try {
      console.log('📦 Creating order with data:', orderData);
      const res = await api.post('/orders', orderData);
      return res.data; 
    } catch (error) {
      console.error('❌ Create order error:', error);
      throw error;
    }
  },
  
  getOrders: async (): Promise<ApiResponse<Order[]>> => { 
    try {
      const res = await api.get('/orders'); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  getOrder: async (id: number): Promise<ApiResponse<Order>> => { 
    try {
      const res = await api.get(`/orders/${id}`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  cancelOrder: async (id: number): Promise<ApiResponse<Order>> => { 
    try {
      const res = await api.put(`/orders/${id}/cancel`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },

  uploadReturnVideo: async (
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('video', file);
    try {
      const res = await api.post('/orders/return-video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0, // large video on slow mobile network
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      const url = res.data?.data?.url || res.data?.url;
      if (res.data?.success && url) return { success: true, url };
      return { success: false, url: '', error: res.data?.message || res.data?.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const d = error.response?.data as any;
        return { success: false, url: '', error: d?.message || d?.error || 'Failed to upload video' };
      }
      return { success: false, url: '', error: 'Failed to upload video' };
    }
  },

  requestReturn: async (orderId: number, data: { items: number[]; reason: string; video_url?: string }): Promise<ApiResponse<Order>> => {
    try {
      console.log('🔄 Requesting return for order:', orderId, data);
      const res = await api.post(`/orders/${orderId}/return`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Return request error:', error);
      throw error;
    }
  },

  getReturnStatus: async (orderId: number): Promise<ApiResponse<OrderReturn[]>> => {
    try {
      console.log('🔄 Fetching return status for order:', orderId);
      const res = await api.get(`/orders/${orderId}/returns`);
      return res.data;
    } catch (error) {
      console.error('❌ Get return status error:', error);
      throw error;
    }
  },

  // ==================== INVOICE METHODS ====================
  downloadInvoice: async (orderId: number): Promise<any> => {
    try {
      console.log('📄 Fetching invoice for order:', orderId);
      const response = await api.get(`/admin/orders/${orderId}/invoice`);
      
      console.log('📄 Invoice API response:', response.data);
      
      if (response.data && response.data.success) {
        const invoiceData = response.data.data;
        
        if (!invoiceData) {
          console.error('❌ Invoice data is empty/missing');
          return { success: false, data: null, error: 'Invoice data missing from response' };
        }
        
        console.log('📄 Invoice data received:', invoiceData);
        return { success: true, data: invoiceData };
      }
      
      return { success: false, data: null, error: response.data?.message || 'Failed to fetch invoice data' };
    } catch (error: any) {
      console.error('❌ Invoice fetch error:', error);
      throw error;
    }
  },

  // ==================== REPLACEMENT METHODS ====================
  getReplacements: async (orderId: number): Promise<ApiResponse<ReplacementItem[]>> => {
    try {
      console.log('🔄 Fetching replacements for order:', orderId);
      const res = await api.get(`/orders/${orderId}/replacements`);
      return res.data;
    } catch (error) {
      console.error('❌ Get replacements error:', error);
      throw error;
    }
  },

  requestReplacement: async (orderId: number, data: {
    original_item_id: number;
    replacement_product_id: number;
    quantity: number;
    reason: string;
  }): Promise<ApiResponse<ReplacementItem>> => {
    try {
      console.log('🔄 Requesting replacement for order:', orderId, data);
      const res = await api.post(`/orders/${orderId}/replacements`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Replacement request error:', error);
      throw error;
    }
  },

  // ── Admin: replacement requests ──
  adminGetReplacements: async (params: { status?: string; page?: number; page_size?: number }): Promise<any> => {
    const res = await api.get('/admin/replacements', { params });
    return res.data;
  },

  adminUpdateReplacementStatus: async (replacementId: number, data: {
    status: 'approved' | 'rejected' | 'in_transit' | 'completed';
    admin_notes?: string;
    tracking_id?: string;
  }): Promise<ApiResponse<ReplacementItem>> => {
    const res = await api.put(`/admin/replacements/${replacementId}/status`, data);
    return res.data;
  },

  // Products allowed as replacement for one order item (same price only) + same-product stock info
  getReplacementOptions: async (orderId: number, itemId: number, search?: string): Promise<ApiResponse<{
    item_id: number;
    allowed_price: number;
    max_quantity: number;
    same_product: { id: number; name: string; in_stock: boolean };
    different_options: { id: number; name: string; sku: string; price: number; mrp: number; image: string; stock: number; in_stock: boolean }[];
  }>> => {
    const res = await api.get(`/orders/${orderId}/replacement-options`, {
      params: { item_id: itemId, ...(search ? { search } : {}) },
    });
    return res.data;
  },

  getAvailableProducts: async (params?: { search?: string; category?: string; exclude_product_id?: number }): Promise<ApiPaginatedResponse<Product>> => {
    try {
      console.log('📦 Fetching available products for replacement:', params);
      const res = await api.get('/products', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Get available products error:', error);
      throw error;
    }
  },

  getTrackingInfo: async (trackingNumber: string): Promise<ApiResponse<TrackingEvent[]>> => {
    try {
      console.log('📦 Fetching tracking info for:', trackingNumber);
      const res = await api.get(`/tracking/${trackingNumber}`);
      return res.data;
    } catch (error) {
      console.error('❌ Get tracking info error:', error);
      throw error;
    }
  },

  // ==================== EMPLOYEE ORDERS ====================
  getEmployeeOrders: async (): Promise<ApiResponse<{ orders: Order[]; summary: any }>> => {
    try {
      console.log('📦 Fetching employee referred orders...');
      const res = await api.get('/employee/orders');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee orders:', error);
      throw error;
    }
  },

  getEmployeeAllOrders: async (params?: { page?: number; page_size?: number; status?: string; search?: string }): Promise<ApiResponse<Order[]> & { summary?: any; pagination?: any }> => {
    try {
      console.log('📦 Fetching all orders for employee (with permission)...');
      const res = await api.get('/employee/all-orders', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching all orders:', error);
      throw error;
    }
  },

  // ==================== PAYMENTS ====================
  createRazorpayOrder: async (amount: number, currency = 'INR'): Promise<ApiResponse<any>> => { 
    try {
      const res = await api.post('/payments/razorpay/create', { amount, currency }); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  verifyRazorpayPayment: async (paymentData: any): Promise<ApiResponse<any>> => { 
    try {
      const res = await api.post('/payments/razorpay/verify', paymentData); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },

  // ==================== PHONEPE PAYMENT METHODS ====================
  initiatePhonePePayment: async (orderId: number): Promise<{ success: boolean; redirectUrl?: string; merchantOrderId?: string; error?: string }> => {
    try {
      const res = await api.post('/payment/phonepe/initiate', { order_id: orderId });
      if (res.data.success && res.data.data?.redirect_url) {
        return { success: true, redirectUrl: res.data.data.redirect_url, merchantOrderId: res.data.data.merchant_order_id };
      }
      return { success: false, error: res.data.message || 'Failed to start PhonePe payment' };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || error.response?.data?.error || 'Failed to start PhonePe payment' };
    }
  },

  getPhonePeStatus: async (merchantOrderId: string): Promise<{ state: 'paid' | 'failed' | 'pending' | 'mismatch'; orderId?: number }> => {
    try {
      const res = await api.get(`/payment/phonepe/status/${encodeURIComponent(merchantOrderId)}`);
      return { state: res.data?.data?.state || 'pending', orderId: res.data?.data?.order_id };
    } catch {
      return { state: 'pending' };
    }
  },

  // ==================== ✅ PAYU PAYMENT METHODS ====================
  
  /**
   * Generate PayU Hash (SHA-512)
   * ⚠️ IMPORTANT: Hash MUST be generated on backend for security.
   * The salt should NEVER be exposed in frontend code in production.
   */
  generatePayUHash: async (data: {
    txnid: string;
    amount: string;
    productinfo: string;
    firstname: string;
    email: string;
    udf1?: string;
    udf2?: string;
    udf3?: string;
    udf4?: string;
    udf5?: string;
  }): Promise<{ success: boolean; hash?: string; key?: string; error?: string }> => {
    try {
      console.log('🔐 Generating PayU hash for txnid:', data.txnid);
      const res = await api.post('/payment/payu/generate-hash', data);
      console.log('🔐 PayU hash response:', res.data);
      
      if (res.data.success && res.data.hash) {
        return { success: true, hash: res.data.hash, key: res.data.key };
      }
      return { success: false, error: res.data.message || 'Failed to generate hash' };
    } catch (error: any) {
      console.error('❌ Error generating PayU hash:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to generate hash'
      };
    }
  },

  /**
   * Verify PayU Payment after redirect
   * Called when user returns from PayU payment page
   */
  verifyPayUPayment: async (data: {
    txnid: string;
    status: string;
    hash: string;
    order_id: number;
    mihpayid?: string;
    mode?: string;
    amount?: string;
    firstname?: string;
    email?: string;
    productinfo?: string;
    udf1?: string;
    udf2?: string;
    udf3?: string;
    udf4?: string;
    udf5?: string;
  }): Promise<{ success: boolean; verified?: boolean; message?: string; error?: string }> => {
    try {
      console.log('🔐 Verifying PayU payment for order:', data.order_id);
      const res = await api.post('/payment/payu/verify', data);
      console.log('🔐 PayU verification response:', res.data);
      
      if (res.data.success && res.data.verified) {
        return { success: true, verified: true, message: res.data.message };
      }
      return {
        success: false,
        verified: false,
        message: res.data.message || 'Payment verification failed'
      };
    } catch (error: any) {
      console.error('❌ Error verifying PayU payment:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Payment verification failed'
      };
    }
  },

  // ==================== SHIPROCKET METHODS ====================
  createShiprocketShipment: async (orderId: number, data: { admin_note?: string; skip_awb?: boolean }): Promise<ApiResponse<{
    awb_code?: string;
    courier_name?: string;
    estimated_delivery_date?: string;
    shipment_id?: number;
    shiprocket_order_id?: number;
  }>> => {
    try {
      console.log(`📦 Creating ShipRocket shipment for order ${orderId}`);
      const res = await api.post(`/admin/orders/${orderId}/shiprocket/create-shipment`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Create ShipRocket shipment error:', error);
      throw error;
    }
  },

  cancelShiprocketOrder: async (orderId: number, data: { ids: number[] }): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🚫 Cancelling ShipRocket order ${orderId}`);
      const res = await api.post(`/admin/orders/${orderId}/shiprocket/cancel`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Cancel ShipRocket order error:', error);
      throw error;
    }
  },

  createShiprocketReturn: async (orderId: number, data: { admin_note?: string; return_reason?: string }): Promise<ApiResponse<{
    awb_code?: string;
    courier_name?: string;
    pickup_date?: string;
    return_order_id?: number;
  }>> => {
    try {
      console.log(`🔄 Creating ShipRocket return for order ${orderId}`);
      const res = await api.post(`/admin/orders/${orderId}/shiprocket/create-return`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Create ShipRocket return error:', error);
      throw error;
    }
  },

  trackShiprocketOrder: async (orderId: number, awbNumber: string): Promise<ApiResponse<{
    tracking_data: {
      shipment_track_activities: Array<{
        date: string;
        status: string;
        activity: string;
        location: string;
        'sr-status-label': string;
      }>;
      shipment_track: Array<{
        edd: string;
      }>;
    };
  }>> => {
    try {
      console.log(`📡 Tracking ShipRocket order ${orderId} with AWB ${awbNumber}`);
      const res = await api.get(`/admin/orders/${orderId}/shiprocket/track`, { 
        params: { awb: awbNumber } 
      });
      return res.data;
    } catch (error) {
      console.error('❌ Track ShipRocket order error:', error);
      throw error;
    }
  },

  // ==================== MANUAL SHIPNOW METHOD ====================
  manualShipNow: async (orderId: number, data: { courier_id: number; courier_name: string; courier_charge?: number }): Promise<ApiResponse<{
    awb_code: string;
    courier_name: string;
    shipment_id: number;
    order_number: string;
    message: string;
  }>> => {
    try {
      console.log(`🚚 Manual ShipNow for order ${orderId} with courier ${data.courier_name}`);
      const res = await api.post(`/admin/orders/${orderId}/shipnow`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Manual ShipNow error:', error);
      throw error;
    }
  },

  // ==================== SHIPROCKET COURIER REASSIGNMENT METHODS ====================
  
  getAvailableCouriersForOrder: async (orderId: number): Promise<ApiResponse<{
    couriers: Array<{
      courier_id: number;
      courier_name: string;
      shipping_charge: number;
      estimated_days: number;
      delivery_date: string;
    }>;
    weight: number;
    cod: boolean;
    pickup_pincode: string;
    delivery_pincode: string;
    current_courier_id?: number;
    current_courier_name?: string;
  }>> => {
    try {
      console.log(`🔍 Fetching available couriers for order ${orderId} for reassignment`);
      const res = await api.get(`/admin/orders/${orderId}/shiprocket/available-couriers`);
      return res.data;
    } catch (error) {
      console.error('❌ Get available couriers error:', error);
      throw error;
    }
  },

  reassignShiprocketCourier: async (orderId: number, data: {
    courier_id: number;
    courier_name: string;
    reason?: string;
  }): Promise<ApiResponse<{
    order_id: number;
    shipment_id: number;
    awb_code: string;
    courier_name: string;
    courier_id: number;
    pickup_date: string;
    pickup_location: string;
    order_number: string;
    status: string;
    old_awb?: string;
    old_courier?: string;
  }>> => {
    try {
      console.log(`🔄 Reassigning order ${orderId} to courier ${data.courier_name} (ID: ${data.courier_id})`);
      const res = await api.post(`/admin/orders/${orderId}/shiprocket/reassign`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Reassign courier error:', error);
      throw error;
    }
  },

  // ==================== RETURN COURIER SELECTION (Return dashboard → Ongoing) ====================

  getReturnAvailableCouriers: async (orderId: number): Promise<ApiResponse<{
    couriers: Array<{
      courier_id: number;
      courier_name: string;
      shipping_charge: number;
      estimated_days: number;
      delivery_date: string;
    }>;
    weight: number;
    pickup_pincode: string;
    delivery_pincode: string;
    current_courier_name?: string;
  }>> => {
    const res = await api.get(`/admin/orders/${orderId}/return/available-couriers`);
    return res.data;
  },

  assignReturnCourier: async (orderId: number, data: {
    courier_id: number;
    courier_name: string;
  }): Promise<ApiResponse<{
    return_order_id: number;
    awb_code: string;
    courier_name: string;
    pickup_date?: string;
    old_awb?: string;
    old_courier?: string;
  }>> => {
    const res = await api.post(`/admin/orders/${orderId}/return/assign-courier`, data);
    return res.data;
  },

  // ==================== ORDER CALL STATUS METHODS ====================
  updateOrderCallStatus: async (orderId: number, data: {
    call_status: string;
    call_notes: string;
    call_attempts: number;
    last_call_at: string;
  }): Promise<ApiResponse<ExtendedOrder>> => {
    try {
      console.log(`📞 Updating call status for order ${orderId}:`, data);
      const res = await api.patch(`/admin/orders/${orderId}/call-status`, data);
      console.log('📞 Call status response:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Update order call status error:', error);
      throw error;
    }
  },

  // ==================== ADMIN PACK ORDER ====================
  packOrder: async (orderId: number, data?: { courier_id?: number; courier_name?: string; admin_note?: string }): Promise<ApiResponse<Order>> => {
    try {
      console.log(`📦 Packing order ${orderId}...`);
      const res = await api.post(`/admin/orders/${orderId}/pack`, data || {});
      return res.data;
    } catch (error) {
      console.error('❌ Pack order error:', error);
      throw error;
    }
  },

  // ==================== ADMIN HANDOVER (schedule Shiprocket pickup, Pack -> Handover) ====================
  handoverOrder: async (orderId: number, data: { pickup_date: string; admin_note?: string }): Promise<ApiResponse<Order>> => {
    try {
      const res = await api.post(`/admin/orders/${orderId}/handover`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Handover order error:', error);
      throw error;
    }
  },

  // ==================== ADMIN ABOUT ====================
  adminGetAboutContent: async (): Promise<ApiResponse<About | null>> => {
    try {
      const res = await api.get('/admin/about');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminCreateAboutContent: async (data: Omit<About, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<About>> => {
    try {
      const res = await api.post('/admin/about', data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminUpdateAboutContent: async (data: Partial<About>): Promise<ApiResponse<About>> => {
    try {
      const res = await api.put('/admin/about', data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminUploadAboutImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/admin/about/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `${BASE_URL}${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  // ==================== EMPLOYEE ABOUT METHODS ====================
  employeeGetAboutContent: async (): Promise<ApiResponse<About | null>> => {
    try {
      console.log('👤 Employee fetching about content...');
      const res = await api.get('/employee/about');
      return res.data;
    } catch (error) {
      console.error('❌ Employee get about content error:', error);
      throw error;
    }
  },

  employeeCreateAboutContent: async (data: Omit<About, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<About>> => {
    try {
      console.log('👤 Employee creating about content:', data);
      const res = await api.post('/employee/about', data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee create about content error:', error);
      throw error;
    }
  },

  employeeUpdateAboutContent: async (data: Partial<About>): Promise<ApiResponse<About>> => {
    try {
      console.log('👤 Employee updating about content:', data);
      const res = await api.put('/employee/about', data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee update about content error:', error);
      throw error;
    }
  },

  employeeUploadAboutImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      console.log('👤 Employee uploading about image:', file.name);
      const res = await api.post('/employee/about/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `${BASE_URL}${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  // ==================== EMPLOYEE CONTACT METHODS ====================
  employeeGetAllContactInfo: async (params?: { page?: number; page_size?: number }): Promise<ApiPaginatedResponse<Contact>> => {
    try {
      console.log('👤 Employee fetching contact info...');
      const res = await api.get('/employee/contact', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Employee get contact info error:', error);
      throw error;
    }
  },

  employeeGetContactInfo: async (id: number): Promise<ApiResponse<Contact>> => {
    try {
      console.log(`👤 Employee fetching contact info for ID: ${id}`);
      const res = await api.get(`/employee/contact/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee get contact by ID error:', error);
      throw error;
    }
  },

  employeeCreateContactInfo: async (data: any): Promise<ApiResponse<Contact>> => {
    try {
      console.log('👤 Employee creating contact info:', data);
      const res = await api.post('/employee/contact', data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee create contact info error:', error);
      throw error;
    }
  },

  employeeUpdateContactInfo: async (id: number, data: any): Promise<ApiResponse<Contact>> => {
    try {
      console.log(`👤 Employee updating contact info ${id}:`, data);
      const res = await api.put(`/employee/contact/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee update contact info error:', error);
      throw error;
    }
  },

  employeeDeleteContactInfo: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting contact info ${id}`);
      const res = await api.delete(`/employee/contact/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete contact info error:', error);
      throw error;
    }
  },

  employeeUploadContactImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      console.log('👤 Employee uploading contact image:', file.name);
      const res = await api.post('/employee/contact/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `${BASE_URL}${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  employeeDeleteContactImage: async (imageUrl: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      if (!filename) {
        return { success: false, error: 'Invalid image URL' };
      }
      const res = await api.delete(`/employee/contact/delete-image/${filename}`);
      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, error: (error.response?.data as any)?.error || 'Failed to delete image' };
      }
      return { success: false, error: 'Failed to delete image' };
    }
  },

  // ==================== ADMIN CONTACT ====================
  adminGetAllContactInfo: async (params?: any): Promise<ApiPaginatedResponse<Contact>> => {
    try {
      const res = await api.get('/admin/contact', { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminGetContactInfo: async (id: number): Promise<ApiResponse<Contact>> => {
    try {
      const res = await api.get(`/admin/contact/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminCreateContactInfo: async (data: any): Promise<ApiResponse<Contact>> => {
    try {
      const res = await api.post('/admin/contact', data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminUpdateContactInfo: async (id: number, data: any): Promise<ApiResponse<Contact>> => {
    try {
      const res = await api.put(`/admin/contact/${id}`, data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminDeleteContactInfo: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/admin/contact/${id}`);
      return res.data;
    } catch (error) {
      console.error(`❌ Admin delete contact info ${id} failed:`, error);
      throw error;
    }
  },

  adminUploadContactHeroImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/admin/contact/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `${BASE_URL}${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Invalid response from server' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  adminDeleteContactImage: async (imageUrl: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      if (!filename) {
        return { success: false, error: 'Invalid image URL' };
      }
      const res = await api.delete(`/admin/contact/delete-image/${filename}`);
      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, error: (error.response?.data as any)?.error || 'Failed to delete image' };
      }
      return { success: false, error: 'Failed to delete image' };
    }
  },

  // ==================== ADMIN CONTACT FORMS ====================
  adminGetAllContactForms: async (params?: any): Promise<ApiPaginatedResponse<ContactForm>> => {
    try {
      const res = await api.get('/admin/contact-forms', { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminGetContactForm: async (id: number): Promise<ApiResponse<ContactForm>> => {
    try {
      const res = await api.get(`/admin/contact-forms/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminUpdateContactFormStatus: async (id: number, status: 'pending' | 'replied' | 'resolved'): Promise<ApiResponse<ContactForm>> => {
    try {
      const res = await api.patch(`/admin/contact-forms/${id}/status`, { status });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminDeleteContactForm: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/admin/contact-forms/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== ADMIN SLIDES ====================
  getAllSlidesAdmin: async (params?: any): Promise<ApiPaginatedResponse<Slide>> => {
    try {
      const res = await api.get('/admin/slides', { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  createSlide: async (data: any): Promise<ApiResponse<Slide>> => {
    try {
      const res = await api.post('/admin/slides', data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateSlide: async (id: number, data: any): Promise<ApiResponse<Slide>> => {
    try {
      const res = await api.put(`/admin/slides/${id}`, data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSlide: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/admin/slides/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  toggleSlideStatus: async (id: number): Promise<ApiResponse<Slide>> => {
    try {
      const res = await api.patch(`/admin/slides/${id}/toggle`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateSlidesOrder: async (slides: Array<{id: number, order: number}>): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.put('/admin/slides/order', { slides });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  uploadSlideImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/admin/slides/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `${BASE_URL}${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  // ==================== ADMIN CATEGORIES ====================
  getAllCategoriesAdmin: async (params?: any): Promise<ApiPaginatedResponse<Category>> => { 
    try {
      const res = await api.get('/admin/categories', { params }); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  createCategory: async (formData: FormData): Promise<ApiResponse<Category>> => { 
    try {
      const res = await api.post('/admin/categories', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      }); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  updateCategory: async (id: number, formData: FormData): Promise<ApiResponse<Category>> => { 
    try {
      const res = await api.put(`/admin/categories/${id}`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      }); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  deleteCategory: async (id: number): Promise<ApiResponse<{ message: string }>> => { 
    try {
      const res = await api.delete(`/admin/categories/${id}`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  toggleCategoryStatus: async (id: number): Promise<ApiResponse<Category>> => { 
    try {
      const res = await api.patch(`/admin/categories/${id}/toggle`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },

  // ==================== ADMIN AGE GROUPS ====================
  getAllAgeGroupsAdmin: async (params?: any): Promise<ApiPaginatedResponse<AgeGroup>> => { 
    try {
      const res = await api.get('/admin/age-groups', { params }); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  createAgeGroup: async (data: any): Promise<ApiResponse<AgeGroup>> => { 
    try {
      const res = await api.post('/admin/age-groups', data); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  updateAgeGroup: async (id: number, data: any): Promise<ApiResponse<AgeGroup>> => { 
    try {
      const res = await api.put(`/admin/age-groups/${id}`, data); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  deleteAgeGroup: async (id: number): Promise<ApiResponse<{ message: string }>> => { 
    try {
      const res = await api.delete(`/admin/age-groups/${id}`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  toggleAgeGroupStatus: async (id: number): Promise<ApiResponse<AgeGroup>> => { 
    try {
      const res = await api.patch(`/admin/age-groups/${id}/toggle`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },

  // ==================== ADMIN EMPLOYEES ====================
  getAllEmployees: async (params?: any): Promise<ApiPaginatedResponse<Employee>> => {
    try {
      const res = await api.get('/admin/employees', { params });
      if (res.data.success && res.data.data) {
        res.data.data = res.data.data.map((emp: Employee) => ({
          ...emp,
          employee_code: getEmployeeCode(emp.id)
        }));
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getEmployeeById: async (id: number): Promise<ApiResponse<Employee>> => {
    try {
      const res = await api.get(`/admin/employees/${id}`);
      if (res.data.success && res.data.data) {
        if (res.data.data.employee) {
          res.data.data.employee.employee_code = getEmployeeCode(res.data.data.employee.id);
        } else if (res.data.data.id) {
          res.data.data.employee_code = getEmployeeCode(res.data.data.id);
        }
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  createEmployee: async (data: any): Promise<ApiResponse<Employee>> => {
    try {
      const res = await api.post('/admin/employees', data);
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateEmployee: async (id: number, data: any): Promise<ApiResponse<Employee>> => {
    try {
      const res = await api.put(`/admin/employees/${id}`, data);
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  resetEmployeePassword: async (id: number, password: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🔐 Resetting password for employee ID: ${id}`);
      const res = await api.post(`/admin/employees/${id}/reset-password`, { password });
      return res.data;
    } catch (error) {
      console.error('❌ Error resetting password:', error);
      throw error;
    }
  },

  adminResetEmployeePassword: async (employeeId: number): Promise<ApiResponse<{ message: string; new_password?: string }>> => {
    try {
      console.log(`🔐 Admin resetting password for employee ${employeeId} (send email)`);
      const res = await api.post(`/admin/employees/${employeeId}/reset-password-email`);
      return res.data;
    } catch (error) {
      console.error('❌ Error sending password reset email:', error);
      throw error;
    }
  },

  adminToggleEmployeeStatus: async (employeeId: number, isActive: boolean): Promise<ApiResponse<Employee>> => {
    try {
      console.log(`👤 Admin toggling employee ${employeeId} status to ${isActive ? 'active' : 'inactive'}`);
      const res = await api.patch(`/admin/employees/${employeeId}/toggle-status`, { is_active: isActive });
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      console.error('❌ Error toggling employee status:', error);
      throw error;
    }
  },

  deleteEmployee: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/admin/employees/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  toggleEmployeeStatus: async (id: number): Promise<ApiResponse<Employee>> => {
    try {
      const res = await api.patch(`/admin/employees/${id}/toggle`);
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getEmployeeStats: async (): Promise<ApiResponse<any>> => {
    try {
      const res = await api.get('/admin/employees/stats');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getEmployeePermissions: async (id: number): Promise<ApiResponse<EmployeePermissionsResponse>> => {
    try {
      const res = await api.get(`/admin/employees/${id}/permissions`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateEmployeePermissions: async (id: number, permissions: EmployeePermissionsResponse): Promise<ApiResponse<Employee>> => {
    try {
      console.log(`🔐 Updating permissions for employee ${id}:`, permissions);
      const res = await api.patch(`/admin/employees/${id}/permissions`, permissions);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== FULL PERMISSIONS SYSTEM (ADMIN ONLY) ====================
  getEmployeeFullPermissions: async (employeeId: number): Promise<ApiResponse<EmployeeFullPermissions>> => {
    try {
      console.log(`🔍 Admin fetching full permissions for employee ${employeeId}...`);
      const res = await api.get(`/admin/employees/${employeeId}/full-permissions`);
      if (res.data.success && res.data.data && !res.data.data.employee_code && res.data.data.employee_id) {
        res.data.data.employee_code = getEmployeeCode(res.data.data.employee_id);
      }
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee full permissions:', error);
      throw error;
    }
  },

  updateEmployeeFullPermissions: async (employeeId: number, permissions: ModulePermission[]): Promise<ApiResponse<EmployeeFullPermissions>> => {
    try {
      const res = await api.put(`/admin/employees/${employeeId}/full-permissions`, { permissions });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getAvailableModules: async (): Promise<ApiResponse<ModulePermission[]>> => {
    try {
      const res = await api.get('/admin/permissions/modules');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== EMPLOYEE PERMISSIONS (SELF SERVICE) ====================
  getMyPermissions: async (): Promise<ApiResponse<EmployeeFullPermissions>> => {
    try {
      console.log('🔍 Fetching employee permissions from /employee/permissions...');
      const res = await api.get('/employee/permissions');
      console.log('📦 Permissions API Response:', res.data);
      if (res.data.success && res.data.data && !res.data.data.employee_code && res.data.data.employee_id) {
        res.data.data.employee_code = getEmployeeCode(res.data.data.employee_id);
      }
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching permissions:', error);
      throw error;
    }
  },

  getMyModules: async (): Promise<ApiResponse<ModulePermission[]>> => {
    try {
      const res = await api.get('/employee/permissions/modules');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== EMPLOYEE AGE GROUP METHODS ====================
  employeeCreateAgeGroup: async (data: any): Promise<ApiResponse<AgeGroup>> => {
    try {
      console.log(`👤 Employee creating age group:`, data);
      const res = await api.post('/employee/age-groups', data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee create age group error:', error);
      throw error;
    }
  },

  employeeUpdateAgeGroup: async (id: number, data: any): Promise<ApiResponse<AgeGroup>> => {
    try {
      console.log(`👤 Employee updating age group ${id}:`, data);
      const res = await api.put(`/employee/age-groups/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee update age group error:', error);
      throw error;
    }
  },

  employeeToggleAgeGroupStatus: async (id: number): Promise<ApiResponse<AgeGroup>> => {
    try {
      console.log(`👤 Employee toggling age group status ${id}`);
      const res = await api.patch(`/employee/age-groups/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee toggle age group status error:', error);
      throw error;
    }
  },

  employeeDeleteAgeGroup: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting age group ${id}`);
      const res = await api.delete(`/employee/age-groups/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete age group error:', error);
      throw error;
    }
  },

  // ==================== EMPLOYEE CATEGORY METHODS ====================
  employeeCreateCategory: async (formData: FormData): Promise<ApiResponse<Category>> => {
    try {
      console.log(`👤 Employee creating category`);
      const res = await api.post('/employee/categories', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    } catch (error) {
      console.error('❌ Employee create category error:', error);
      throw error;
    }
  },

  employeeUpdateCategory: async (id: number, formData: FormData): Promise<ApiResponse<Category>> => {
    try {
      console.log(`👤 Employee updating category ${id}`);
      const res = await api.put(`/employee/categories/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    } catch (error) {
      console.error('❌ Employee update category error:', error);
      throw error;
    }
  },

  employeeDeleteCategory: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting category ${id}`);
      const res = await api.delete(`/employee/categories/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete category error:', error);
      throw error;
    }
  },

  employeeToggleCategoryStatus: async (id: number): Promise<ApiResponse<Category>> => {
    try {
      console.log(`👤 Employee toggling category status ${id}`);
      const res = await api.patch(`/employee/categories/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee toggle category status error:', error);
      throw error;
    }
  },

  // ==================== EMPLOYEE PRODUCT METHODS ====================
  employeeCreateProduct: async (data: any): Promise<ApiResponse<Product>> => {
    try {
      console.log(`👤 Employee creating product:`, data);
      const res = await api.post('/employee/products', data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee create product error:', error);
      throw error;
    }
  },

  employeeUpdateProduct: async (id: number, data: any): Promise<ApiResponse<Product>> => {
    try {
      console.log(`👤 Employee updating product ${id}:`, data);
      const res = await api.put(`/employee/products/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee update product error:', error);
      throw error;
    }
  },

  employeeDeleteProduct: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting product ${id}`);
      const res = await api.delete(`/employee/products/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete product error:', error);
      throw error;
    }
  },

  employeeToggleProductStatus: async (id: number): Promise<ApiResponse<Product>> => {
    try {
      console.log(`👤 Employee toggling product status ${id}`);
      const res = await api.patch(`/employee/products/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee toggle product status error:', error);
      throw error;
    }
  },

  employeeUpdateProductStock: async (id: number, stock: number): Promise<ApiResponse<Product>> => {
    try {
      console.log(`👤 Employee updating product stock ${id}: ${stock}`);
      const res = await api.patch(`/employee/products/${id}/stock`, { stock });
      return res.data;
    } catch (error) {
      console.error('❌ Employee update product stock error:', error);
      throw error;
    }
  },

  // ==================== EMPLOYEE PRODUCT MEDIA UPLOAD METHODS ====================
  
  employeeUploadProductImages: async (formData: FormData): Promise<{ success: boolean; urls: string[]; error?: string }> => {
    try {
      console.log('📸 Employee uploading product images...');
      const res = await api.post('/employee/products/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      return res.data;
    } catch (error: any) {
      console.error('❌ Employee upload product images error:', error);
      return { 
        success: false, 
        urls: [], 
        error: error.response?.data?.error || 'Failed to upload images' 
      };
    }
  },

  employeeUploadProductVideos: async (formData: FormData): Promise<{ success: boolean; urls: string[]; error?: string }> => {
    try {
      console.log('🎥 Employee uploading product videos...');
      const res = await api.post('/employee/products/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      return res.data;
    } catch (error: any) {
      console.error('❌ Employee upload product videos error:', error);
      return { 
        success: false, 
        urls: [], 
        error: error.response?.data?.error || 'Failed to upload videos' 
      };
    }
  },

  employeeUploadSizeChart: async (formData: FormData): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      console.log('📊 Employee uploading size chart...');
      const res = await api.post('/employee/products/size-chart', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      console.log('📊 Size chart upload response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Employee upload size chart error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to upload size chart' 
      };
    }
  },

  // ==================== EMPLOYEE SLIDE METHODS ====================
  employeeGetSlides: async (params?: any): Promise<ApiPaginatedResponse<Slide>> => {
    try {
      console.log(`👤 Employee fetching slides...`);
      const res = await api.get('/employee/slides', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Employee get slides error:', error);
      throw error;
    }
  },

  employeeCreateSlide: async (data: any): Promise<ApiResponse<Slide>> => {
    try {
      console.log(`👤 Employee creating slide:`, data);
      const res = await api.post('/employee/slides', data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee create slide error:', error);
      throw error;
    }
  },

  employeeUpdateSlide: async (id: number, data: any): Promise<ApiResponse<Slide>> => {
    try {
      console.log(`👤 Employee updating slide ${id}:`, data);
      const res = await api.put(`/employee/slides/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Employee update slide error:', error);
      throw error;
    }
  },

  employeeDeleteSlide: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting slide ${id}`);
      const res = await api.delete(`/employee/slides/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete slide error:', error);
      throw error;
    }
  },

  employeeToggleSlideStatus: async (id: number): Promise<ApiResponse<Slide>> => {
    try {
      console.log(`👤 Employee toggling slide status ${id}`);
      const res = await api.patch(`/employee/slides/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee toggle slide status error:', error);
      throw error;
    }
  },

  employeeUploadSlideImage: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      console.log(`👤 Employee uploading slide image: ${file.name}`);
      const res = await api.post('/employee/slides/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success && res.data.url) {
        const imageUrl = res.data.url.startsWith('http') ? res.data.url : `http://localhost:8080${res.data.url}`;
        return { success: true, url: imageUrl };
      }
      return { success: false, url: '', error: res.data.error || 'Upload failed' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, url: '', error: (error.response?.data as any)?.error || 'Failed to upload image' };
      }
      return { success: false, url: '', error: 'Failed to upload image' };
    }
  },

  // ==================== EMPLOYEE PROFILE & SUB-EMPLOYEES ====================
  getEmployeeProfile: async (): Promise<ApiResponse<Employee>> => {
    try {
      const res = await api.get('/employee/profile');
      if (res.data.success && res.data.data && res.data.data.id) {
        res.data.data.employee_code = getEmployeeCode(res.data.data.id);
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getEmployeeDashboardStats: async (): Promise<ApiResponse<DashboardStats>> => {
    try {
      const res = await api.get('/employee/dashboard-stats');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getAllEmployeesForEmployee: async (params?: { page?: number; page_size?: number; search?: string }): Promise<ApiPaginatedResponse<Employee>> => {
    try {
      console.log('👤 Employee fetching all employees (with permission check)...');
      const res = await api.get('/employee/all-employees', { params });
      if (res.data.success && res.data.data) {
        res.data.data = res.data.data.map((emp: Employee) => ({
          ...emp,
          employee_code: getEmployeeCode(emp.id)
        }));
      }
      console.log(`✅ Loaded ${res.data.data?.length || 0} employees`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employees for employee:', error);
      throw error;
    }
  },

  getEmployeeStatsForEmployee: async (): Promise<ApiResponse<any>> => {
    try {
      console.log('👤 Employee fetching employee stats (with permission check)...');
      const res = await api.get('/employee/employees/stats');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee stats for employee:', error);
      throw error;
    }
  },

  getMySubEmployees: async (params?: { page?: number; page_size?: number }): Promise<ApiPaginatedResponse<Employee>> => {
    try {
      const res = await api.get('/employee/sub-employees', { params });
      if (res.data.success && res.data.data) {
        res.data.data = res.data.data.map((emp: Employee) => ({
          ...emp,
          employee_code: getEmployeeCode(emp.id)
        }));
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  createSubEmployee: async (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
    designation: string;
    department: string;
    commission_rate?: number;
  }): Promise<ApiResponse<Employee>> => {
    try {
      console.log('📝 Creating sub-employee:', data);
      const res = await api.post('/employee/sub-employees', data);
      if (res.data.success && res.data.data && res.data.data.id) {
        res.data.data.employee_code = getEmployeeCode(res.data.data.id);
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSubEmployee: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/employee/sub-employees/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== EMPLOYEE EMPLOYEE MANAGEMENT (CRUD with permissions) ====================
  employeeGetEmployeeById: async (id: number): Promise<ApiResponse<Employee>> => {
    try {
      console.log(`👤 Employee fetching employee ${id} details...`);
      const res = await api.get(`/employee/employees/${id}`);
      if (res.data.success && res.data.data) {
        if (res.data.data.employee) {
          res.data.data.employee.employee_code = getEmployeeCode(res.data.data.employee.id);
        } else if (res.data.data.id) {
          res.data.data.employee_code = getEmployeeCode(res.data.data.id);
        }
      }
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee details:', error);
      throw error;
    }
  },

  employeeCreateEmployee: async (data: any): Promise<ApiResponse<Employee>> => {
    try {
      console.log(`👤 Employee creating new employee...`);
      const res = await api.post('/employee/employees', data);
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      console.error('❌ Employee create employee error:', error);
      throw error;
    }
  },

  employeeUpdateEmployee: async (id: number, data: any): Promise<ApiResponse<Employee>> => {
    try {
      console.log(`👤 Employee updating employee ${id}...`);
      const res = await api.put(`/employee/employees/${id}`, data);
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      console.error('❌ Employee update employee error:', error);
      throw error;
    }
  },

  employeeDeleteEmployee: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting employee ${id} (with permission check)`);
      const res = await api.delete(`/employee/employees/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete employee error:', error);
      throw error;
    }
  },

  employeeToggleEmployeeStatus: async (id: number, isActive: boolean): Promise<ApiResponse<Employee>> => {
    try {
      console.log(`👤 Employee toggling employee ${id} status to ${isActive ? 'active' : 'inactive'} (with permission check)`);
      const res = await api.patch(`/employee/employees/${id}/toggle-status`, { is_active: isActive });
      if (res.data.success && res.data.data) {
        const employee = res.data.data.employee || res.data.data;
        if (employee && employee.id) {
          employee.employee_code = getEmployeeCode(employee.id);
        }
      }
      return res.data;
    } catch (error) {
      console.error('❌ Employee toggle employee status error:', error);
      throw error;
    }
  },

  employeeResetEmployeePassword: async (id: number, password: string): Promise<ApiResponse<{ message: string; employee_id: number; employee_code: string; phone: string }>> => {
    try {
      console.log(`👤 Employee resetting password for employee ${id} (with permission check)`);
      const res = await api.post(`/employee/employees/${id}/reset-password`, { password });
      return res.data;
    } catch (error) {
      console.error('❌ Employee reset employee password error:', error);
      throw error;
    }
  },

  employeeResetPasswordAuto: async (id: number): Promise<ApiResponse<{ 
    message: string; 
    employee_id: number; 
    employee_code: string; 
    phone: string;
    new_password: string;
  }>> => {
    try {
      console.log(`🔐 Employee resetting password for employee ${id} (auto-generate from phone)`);
      const res = await api.post(`/employee/employees/${id}/reset-password-auto`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee reset password auto error:', error);
      throw error;
    }
  },

  // ==================== EMPLOYEE NOTIFICATIONS ====================
  
  getEmployeeNotifications: async (params?: { page?: number; page_size?: number; unread_only?: boolean }): Promise<{
    success: boolean;
    data?: Notification[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    unread_count?: number;
    error?: string;
  }> => {
    try {
      console.log('🔔 Fetching employee notifications...');
      const res = await api.get('/employee/notifications', { params });
      console.log('🔔 Notifications response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching notifications:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch notifications' 
      };
    }
  },

  markNotificationAsRead: async (notificationId: number): Promise<{
    success: boolean;
    data?: { id: number; is_read: boolean };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`🔔 Marking notification ${notificationId} as read...`);
      const res = await api.patch(`/employee/notifications/${notificationId}/read`);
      console.log('🔔 Mark read response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error marking notification as read:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to mark notification as read' 
      };
    }
  },

  markAllNotificationsAsRead: async (): Promise<{
    success: boolean;
    data?: { updated_count: number };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('🔔 Marking all notifications as read...');
      const res = await api.patch('/employee/notifications/mark-all-read');
      console.log('🔔 Mark all read response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error marking all notifications as read:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to mark all notifications as read' 
      };
    }
  },

  deleteNotification: async (notificationId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`🔔 Deleting notification ${notificationId}...`);
      const res = await api.delete(`/employee/notifications/${notificationId}`);
      console.log('🔔 Delete notification response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error deleting notification:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to delete notification' 
      };
    }
  },

  deleteAllNotifications: async (): Promise<{
    success: boolean;
    data?: { deleted_count: number };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('🔔 Deleting all notifications...');
      const res = await api.delete('/employee/notifications/delete-all');
      console.log('🔔 Delete all notifications response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error deleting all notifications:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to delete all notifications' 
      };
    }
  },

  getNotificationPreferences: async (): Promise<{
    success: boolean;
    data?: NotificationPreferences;
    error?: string;
  }> => {
    try {
      console.log('🔔 Fetching notification preferences...');
      const res = await api.get('/employee/notifications/preferences');
      console.log('🔔 Preferences response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching notification preferences:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch preferences' 
      };
    }
  },

  updateNotificationPreferences: async (preferences: Partial<NotificationPreferences>): Promise<{
    success: boolean;
    data?: NotificationPreferences;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('🔔 Updating notification preferences:', preferences);
      const res = await api.put('/employee/notifications/preferences', preferences);
      console.log('🔔 Update preferences response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error updating notification preferences:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update preferences' 
      };
    }
  },

  getNotificationStats: async (): Promise<{
    success: boolean;
    data?: NotificationStats;
    error?: string;
  }> => {
    try {
      console.log('🔔 Fetching notification stats...');
      const res = await api.get('/employee/notifications/stats');
      console.log('🔔 Stats response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching notification stats:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch notification stats' 
      };
    }
  },

  // ==================== EMPLOYEE REFERRAL MANAGEMENT ====================
  employeeGetReferralSettings: async (): Promise<ApiResponse<{
    id: number;
    referral_code_prefix: string;
    referral_code_length: number;
    referral_levels: number;
    commission_distribution: Record<number, number>;
    customer_discount_enabled: boolean;
    customer_discount_type: 'percentage' | 'fixed';
    customer_discount_value: number;
  }>> => {
    try {
      console.log('👤 Employee fetching referral settings...');
      const res = await api.get('/employee/referral/settings');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee referral settings:', error);
      throw error;
    }
  },

  employeeUpdateReferralSettings: async (data: {
    referral_code_prefix: string;
    referral_code_length: number;
    referral_levels: number;
    commission_distribution: Record<string, number>;
    customer_discount_enabled: boolean;
    customer_discount_type: string;
    customer_discount_value: number;
  }): Promise<ApiResponse<any>> => {
    try {
      console.log('📤 Employee updating referral settings:', data);
      const res = await api.put('/employee/referral/settings', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating employee referral settings:', error);
      throw error;
    }
  },

  employeeGetMyReferralStats: async (): Promise<ApiResponse<{
    employee_id: number;
    employee_code: string;
    employee_name: string;
    total_referrals: number;
    successful_referrals: number;
    total_commission: number;
    pending_commission: number;
    level1_count: number;
    level2_count: number;
    level3_count: number;
    level1_commission: number;
    level2_commission: number;
    level3_commission: number;
    referral_code: string;
    referral_link: string;
  }>> => {
    try {
      console.log('📊 Employee fetching referral stats...');
      const res = await api.get('/employee/referral/stats');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee referral stats:', error);
      throw error;
    }
  },

  employeeGetMyReferralTeam: async (params?: { page?: number; page_size?: number }): Promise<ApiResponse<{
    team_members: Array<{
      id: number;
      name: string;
      email: string;
      phone: string;
      designation: string;
      department: string;
      commission_rate: number;
      join_date: string;
      status: string;
      total_referrals: number;
      successful_referrals: number;
      total_earnings: number;
    }>;
    summary: {
      total_team_members: number;
      total_referrals: number;
      total_successful: number;
      total_earnings: number;
      level1_count: number;
      level2_count: number;
      level3_count: number;
    };
  }>> => {
    try {
      console.log('👥 Employee fetching referral team...');
      const res = await api.get('/employee/referral/team', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee referral team:', error);
      throw error;
    }
  },

  getMyReferralTeam: async (params?: { page?: number; page_size?: number }): Promise<ApiResponse<{
    team_members: Array<{
      id: number;
      name: string;
      email: string;
      phone: string;
      designation: string;
      department: string;
      commission_rate: number;
      join_date: string;
      status: string;
      total_referrals: number;
      successful_referrals: number;
    }>;
    summary: {
      total_team_members: number;
      total_referrals: number;
      total_earnings: number;
      level1_count: number;
      level2_count: number;
      level3_count: number;
    };
  }>> => {
    try {
      console.log('👥 Fetching my referral team...');
      const res = await api.get('/employee/referral/team', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching referral team:', error);
      return {
        success: false,
        data: { team_members: [], summary: { total_team_members: 0, total_referrals: 0, total_earnings: 0, level1_count: 0, level2_count: 0, level3_count: 0 } },
        error: 'Failed to fetch referral team'
      };
    }
  },

  getMyReferralStats: async (): Promise<ApiResponse<{
    total_referrals: number;
    successful_referrals: number;
    total_earnings: number;
    pending_earnings: number;
    level1_referrals: number;
    level2_referrals: number;
    level3_referrals: number;
    level1_commission: number;
    level2_commission: number;
    level3_commission: number;
    referral_code: string;
    referral_link: string;
  }>> => {
    try {
      console.log('📊 Fetching my referral stats...');
      const res = await api.get('/employee/referral/stats');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching referral stats:', error);
      return {
        success: false,
        data: {
          total_referrals: 0,
          successful_referrals: 0,
          total_earnings: 0,
          pending_earnings: 0,
          level1_referrals: 0,
          level2_referrals: 0,
          level3_referrals: 0,
          level1_commission: 0,
          level2_commission: 0,
          level3_commission: 0,
          referral_code: '',
          referral_link: ''
        },
        error: 'Failed to fetch referral stats'
      };
    }
  },

  // ==================== EMPLOYEE WALLET ====================
  getEmployeeWallet: async (): Promise<ApiResponse<Wallet>> => {
    try {
      const res = await api.get('/employee/wallet');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== EMPLOYEE WALLET METHODS ====================
  
  getWalletSummary: async (): Promise<{
    success: boolean;
    data?: {
      balance: number;
      total_earned: number;
      total_withdrawn: number;
      pending_earnings: number;
      upcoming_earnings: number;
    };
    error?: string;
  }> => {
    try {
      console.log('💰 Fetching wallet summary...');
      const res = await api.get('/employee/wallet/summary');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching wallet summary:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch wallet summary' };
    }
  },

  getWalletTransactions: async (page: number = 1, pageSize: number = 10): Promise<{
    success: boolean;
    data?: Transaction[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching wallet transactions (page ${page})...`);
      const res = await api.get(`/employee/wallet/transactions?page=${page}&page_size=${pageSize}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching wallet transactions:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch transactions' };
    }
  },

  getTransactions: async (page: number = 1, pageSize: number = 10): Promise<{
    success: boolean;
    data?: Transaction[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching transactions (page ${page})...`);
      const res = await api.get(`/employee/wallet/transactions?page=${page}&page_size=${pageSize}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching transactions:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch transactions' };
    }
  },

  getWithdrawals: async (page: number = 1, pageSize: number = 10): Promise<{
    success: boolean;
    data?: WithdrawalRequest[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching withdrawals (page ${page})...`);
      const res = await api.get(`/employee/wallet/withdrawals?page=${page}&page_size=${pageSize}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching withdrawals:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch withdrawals' };
    }
  },

  getMyWithdrawals: async (page: number = 1, pageSize: number = 10): Promise<{
    success: boolean;
    data?: WithdrawalRequest[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching my withdrawals (page ${page})...`);
      const res = await api.get(`/employee/wallet/withdrawals?page=${page}&page_size=${pageSize}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching withdrawals:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch withdrawals' };
    }
  },

  getCommissions: async (page: number = 1, pageSize: number = 10): Promise<{
    success: boolean;
    data?: Commission[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    totals?: { pending_total: number; approved_total: number; rejected_total: number };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching commissions (page ${page})...`);
      const res = await api.get(`/employee/wallet/commissions?page=${page}&page_size=${pageSize}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching commissions:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch commissions' 
      };
    }
  },

  getEmployeeCommissions: async (page: number = 1, pageSize: number = 10): Promise<{
    success: boolean;
    data?: Commission[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    totals?: { pending_total: number; approved_total: number; rejected_total: number };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`💰 Fetching employee commissions (page ${page})...`);
      const res = await api.get(`/employee/wallet/commissions?page=${page}&page_size=${pageSize}`);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching employee commissions:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch commissions' 
      };
    }
  },

  employeeGetAllWallets: async (params?: { page?: number; page_size?: number; search?: string }): Promise<{
    success: boolean;
    data?: Array<{
      id: number;
      employee_id: number;
      employee_name: string;
      employee_email: string;
      employee_code: string;
      department: string;
      balance: number;
      total_earned: number;
      total_withdrawn: number;
      pending_balance: number;
      created_at: string;
      updated_at: string;
    }>;
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
    summary?: { total_balance: number; total_earned: number; total_withdrawn: number };
    error?: string;
  }> => {
    try {
      console.log('💰 Employee fetching all wallets...');
      const res = await api.get('/employee/wallet/all-wallets', { params });
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching employee wallets:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch wallets' 
      };
    }
  },

  getWithdrawalSummary: async (): Promise<{
    success: boolean;
    data?: {
      balance: number;
      pending_withdrawals: number;
      total_withdrawn: number;
      min_withdrawal: number;
      auto_approval_limit: number;
      auto_approval_enabled: boolean;
    };
    error?: string;
  }> => {
    try {
      console.log('💰 Fetching withdrawal summary...');
      const res = await api.get('/employee/wallet/withdrawal-summary');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching withdrawal summary:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch withdrawal summary' };
    }
  },

  requestWithdrawal: async (amount: number, note: string = ''): Promise<{
    success: boolean;
    data?: { withdrawal: WithdrawalRequest; wallet: Wallet; auto_approved: boolean; message: string };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log(`💰 Requesting withdrawal of ₹${amount}...`);
      const res = await api.post('/employee/wallet/withdraw', { amount, note });
      return res.data;
    } catch (error: any) {
      console.error('❌ Error requesting withdrawal:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to request withdrawal' };
    }
  },

  // ==================== BANK ACCOUNT METHODS ====================

  getBankAccount: async (): Promise<{
    success: boolean;
    data?: BankAccount;
    error?: string;
  }> => {
    try {
      console.log('🏦 Fetching bank account...');
      const res = await api.get('/employee/bank-account');
      console.log('📥 Get bank account response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching bank account:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch bank account' 
      };
    }
  },

  saveBankAccount: async (bankData: Omit<BankAccount, 'id'>): Promise<{
    success: boolean;
    data?: BankAccount;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('💾 Saving bank account with data:', JSON.stringify(bankData, null, 2));
      const res = await api.post('/employee/bank-account', bankData);
      console.log('📥 Save bank response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error saving bank account:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        return { 
          success: false, 
          message: error.response.data?.message || error.response.data?.error,
          error: error.response.data?.error || 'Failed to save bank account' 
        };
      }
      return { 
        success: false, 
        error: error.message || 'Failed to save bank account' 
      };
    }
  },

  updateBankAccount: async (bankData: Partial<BankAccount>): Promise<{
    success: boolean;
    data?: BankAccount;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('✏️ Updating bank account with data:', JSON.stringify(bankData, null, 2));
      const res = await api.put('/employee/bank-account', bankData);
      console.log('📥 Update bank response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error updating bank account:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        return { 
          success: false, 
          message: error.response.data?.message || error.response.data?.error,
          error: error.response.data?.error || 'Failed to update bank account' 
        };
      }
      return { 
        success: false, 
        error: error.message || 'Failed to update bank account' 
      };
    }
  },

  deleteBankAccount: async (): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('🗑️ Deleting bank account...');
      const res = await api.delete('/employee/bank-account');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error deleting bank account:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to delete bank account' };
    }
  },

  // ==================== WITHDRAWAL OTP METHODS ====================

  sendWithdrawalOtp: async (): Promise<{
    success: boolean;
    data?: { message: string; expires_in: number; debug_otp?: string };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('📱 Sending withdrawal OTP...');
      const res = await api.post('/employee/wallet/send-otp');
      return res.data;
    } catch (error: any) {
      console.error('❌ Error sending OTP:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to send OTP' };
    }
  },

  verifyWithdrawalOtp: async (data: { withdrawal_id: number; otp: string }): Promise<{
    success: boolean;
    data?: { verified: boolean; message: string };
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('🔐 Verifying withdrawal OTP...');
      const res = await api.post('/employee/wallet/verify-otp', data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error verifying OTP:', error);
      return { success: false, error: error.response?.data?.error || 'Invalid OTP' };
    }
  },

  // ==================== CUSTOMER WALLET BALANCE ====================
  getWalletBalance: async (): Promise<{ success: boolean; balance?: number; total_added?: number; total_spent?: number; total_cashback?: number; error?: string }> => {
    try {
      console.log('💰 Fetching wallet balance for customer...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const res = await api.get('/customer/wallet/balance');
      console.log('💰 Wallet balance response:', res.data);
      
      if (res.data.success && res.data.data) {
        return { 
          success: true, 
          balance: res.data.data.balance,
          total_added: res.data.data.total_added,
          total_spent: res.data.data.total_spent,
          total_cashback: res.data.data.total_cashback
        };
      }
      return { success: false, error: 'Failed to fetch wallet balance' };
    } catch (error: any) {
      console.error('❌ Error fetching wallet balance:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch balance' };
    }
  },

  deductFromWallet: async (amount: number, orderId: number): Promise<{ success: boolean; message?: string; balance?: number }> => {
    try {
      console.log(`💰 Deducting ₹${amount} from customer wallet for order ${orderId}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }
      
      const res = await api.post('/customer/wallet/deduct', { amount, order_id: orderId });
      console.log('💰 Deduct from wallet response:', res.data);
      
      if (res.data.success) {
        return { 
          success: true, 
          message: res.data.message || 'Amount deducted successfully',
          balance: res.data.data?.balance
        };
      }
      return { success: false, message: res.data.message || 'Failed to deduct from wallet' };
    } catch (error: any) {
      console.error('❌ Error deducting from wallet:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to deduct from wallet' };
    }
  },

  getWalletTransactionsCustomer: async (): Promise<{ success: boolean; data?: CustomerTransaction[]; error?: string }> => {
    try {
      console.log('📊 Fetching wallet transactions for customer...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const res = await api.get('/customer/wallet/transactions');
      console.log('📊 Wallet transactions response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch transactions' };
    } catch (error: any) {
      console.error('❌ Error fetching wallet transactions:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch transactions' };
    }
  },

  addWalletBalance: async (amount: number, paymentId?: string): Promise<{ success: boolean; message?: string; balance?: number }> => {
    try {
      console.log(`💰 Adding ₹${amount} to customer wallet...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Please login first' };
      }
      
      const payload: any = { amount };
      if (paymentId) {
        payload.payment_id = paymentId;
      }
      
      const res = await api.post('/customer/wallet/add', payload);
      console.log('💰 Add money response:', res.data);
      
      if (res.data.success) {
        return { 
          success: true, 
          message: res.data.message || 'Money added successfully',
          balance: res.data.data?.balance
        };
      }
      return { success: false, message: res.data.message || 'Failed to add money' };
    } catch (error: any) {
      console.error('❌ Error adding money to wallet:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to add money' };
    }
  },

  // ==================== CUSTOMER WALLET WITHDRAWAL METHODS ====================
  requestCustomerWithdrawal: async (data: {
    amount: number;
    bankDetails: {
      accountNumber: string;
      ifscCode: string;
      accountHolderName: string;
      bankName?: string;
    };
  }): Promise<{ success: boolean; message?: string; data?: any; error?: string }> => {
    try {
      console.log(`💰 Withdrawing ₹${data.amount} from customer wallet...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }
      
      const res = await api.post('/customer/wallet/withdraw', data);
      console.log('💰 Withdrawal response:', res.data);
      
      if (res.data.success) {
        return { 
          success: true, 
          message: res.data.message || 'Withdrawal processed successfully',
          data: res.data.data
        };
      }
      return { success: false, message: res.data.message || 'Failed to process withdrawal' };
    } catch (error: any) {
      console.error('❌ Error processing withdrawal:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to process withdrawal',
        error: error.response?.data?.error
      };
    }
  },

  getCustomerWithdrawalHistory: async (): Promise<{ success: boolean; data?: CustomerWithdrawalRequest[]; error?: string }> => {
    try {
      console.log('📊 Fetching withdrawal history for customer...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get('/customer/wallet/withdrawals');
      console.log('📊 Withdrawal history response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch withdrawal history' };
    } catch (error: any) {
      console.error('❌ Error fetching withdrawal history:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch withdrawal history' };
    }
  },

  getCustomerWithdrawalLimits: async (): Promise<{ 
    success: boolean; 
    data?: { 
      available_balance: number;
      min_withdrawal: number;
      max_withdrawal_per_request: number;
      daily_withdrawal_limit: number;
      today_withdrawn: number;
      remaining_daily_limit: number;
      max_possible_withdrawal: number;
    }; 
    error?: string 
  }> => {
    try {
      console.log('💰 Fetching withdrawal limits for customer...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get('/customer/wallet/withdrawal/limits');
      console.log('💰 Withdrawal limits response:', res.data);
      
      if (res.data.success && res.data.data) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch withdrawal limits' };
    } catch (error: any) {
      console.error('❌ Error fetching withdrawal limits:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch withdrawal limits' };
    }
  },

  getCustomerSavedBanks: async (): Promise<{ success: boolean; data?: CustomerSavedBank[]; error?: string }> => {
    try {
      console.log('🏦 Fetching saved bank accounts for customer...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const res = await api.get('/customer/wallet/saved-banks');
      console.log('🏦 Saved banks response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to fetch saved banks' };
    } catch (error: any) {
      console.error('❌ Error fetching saved banks:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch saved banks' };
    }
  },

  saveCustomerBankDetails: async (bankDetails: {
    accountNumber: string;
    ifscCode: string;
    accountHolderName: string;
    bankName?: string;
  }): Promise<{ success: boolean; message?: string; data?: CustomerSavedBank; error?: string }> => {
    try {
      console.log('💾 Saving bank details for customer...', bankDetails);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }
      
      const res = await api.post('/customer/wallet/save-bank', bankDetails);
      console.log('💾 Save bank response:', res.data);
      
      if (res.data.success) {
        return { 
          success: true, 
          message: res.data.message || 'Bank details saved successfully',
          data: res.data.data
        };
      }
      return { success: false, message: res.data.message || 'Failed to save bank details' };
    } catch (error: any) {
      console.error('❌ Error saving bank details:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to save bank details' };
    }
  },

  deleteCustomerSavedBank: async (bankId: number): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`🗑️ Deleting saved bank ${bankId}...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }
      
      const res = await api.delete(`/customer/wallet/saved-banks/${bankId}`);
      console.log('🗑️ Delete bank response:', res.data);
      
      if (res.data.success) {
        return { success: true, message: res.data.message || 'Bank account deleted successfully' };
      }
      return { success: false, message: res.data.message || 'Failed to delete bank account' };
    } catch (error: any) {
      console.error('❌ Error deleting saved bank:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to delete bank account' };
    }
  },

  checkCustomerWithdrawalEligibility: async (amount?: number): Promise<{ 
    success: boolean; 
    data?: { 
      eligible: boolean; 
      available_balance: number;
      min_withdrawal: number;
      max_withdrawal: number;
      message?: string;
    }; 
    error?: string 
  }> => {
    try {
      console.log('🔍 Checking withdrawal eligibility...');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const params: any = {};
      if (amount) params.amount = amount;
      
      const res = await api.get('/customer/wallet/withdrawal/eligibility', { params });
      console.log('🔍 Eligibility response:', res.data);
      
      if (res.data.success) {
        return { success: true, data: res.data.data };
      }
      return { success: false, error: res.data.message || 'Failed to check eligibility' };
    } catch (error: any) {
      console.error('❌ Error checking withdrawal eligibility:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to check eligibility' };
    }
  },

  // ==================== ADMIN WALLET ====================
  adminGetCommissionSummary: async (): Promise<ApiResponse<{
    total_commission: number;
    pending_commission: number;
    upcoming_commission: number;
    credited_commission: number;
    cancelled_commission: number;
    total_orders_with_commission: number;
  }>> => {
    try {
      const res = await api.get('/admin/wallet/commissions/summary');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminGetAllCommissions: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    level?: string;
    search?: string;
  }): Promise<ApiPaginatedResponse<Commission>> => {
    try {
      const res = await api.get('/admin/wallet/commissions', { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminGetCommissionById: async (id: number): Promise<ApiResponse<Commission>> => {
    try {
      const res = await api.get(`/admin/wallet/commissions/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminUpdateCommissionStatus: async (id: number, status: string, notes?: string): Promise<ApiResponse<Commission>> => {
    try {
      const res = await api.patch(`/admin/wallet/commissions/${id}/status`, { status, notes });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  adminGetAllWithdrawals: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }): Promise<ApiPaginatedResponse<WithdrawalRequest>> => {
    try {
      const res = await api.get('/admin/wallet/withdrawals', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching withdrawals:', error);
      throw error;
    }
  },

  adminGetWithdrawalSummary: async (): Promise<ApiResponse<{
    total_pending: number;
    total_approved: number;
    total_rejected: number;
    total_paid: number;
    total_amount_pending: number;
    total_amount_approved: number;
    total_amount_paid: number;
  }>> => {
    try {
      const res = await api.get('/admin/wallet/withdrawals/summary');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching withdrawal summary:', error);
      throw error;
    }
  },

  adminUpdateWithdrawalStatus: async (
    id: number,
    status: string,
    transactionRef?: string,
    rejectionReason?: string
  ): Promise<ApiResponse<WithdrawalRequest>> => {
    try {
      console.log(`🔧 Admin updating withdrawal ${id} to status: ${status}`);
      const payload: any = { status };
      if (transactionRef) {
        payload.transaction_ref = transactionRef;
      }
      if (rejectionReason) {
        payload.rejection_reason = rejectionReason;
      }
      const res = await api.patch(`/admin/wallet/withdrawals/${id}/status`, payload);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating withdrawal status:', error);
      throw error;
    }
  },

  adminGetAllWallets: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<ApiPaginatedResponse<{
    id: number;
    employee_id: number;
    employee_name: string;
    employee_email: string;
    employee_code: string;
    department: string;
    balance: number;
    total_earned: number;
    total_withdrawn: number;
    pending_balance: number;
    created_at: string;
    updated_at: string;
  }> & { summary?: { total_balance: number; total_earned: number; total_withdrawn: number } }> => {
    try {
      const res = await api.get('/admin/wallets', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee wallets:', error);
      throw error;
    }
  },

  adminGetAllTransactions: async (params?: {
    page?: number;
    page_size?: number;
    employee_id?: number;
    type?: string;
    status?: string;
    search?: string;
  }): Promise<ApiPaginatedResponse<{
    id: number;
    employee_id: number;
    employee_name: string;
    employee_email: string;
    transaction_type: string;
    amount: number;
    status: string;
    reference_id: string;
    note: string;
    created_at: string;
    balance_before?: number;
    balance_after?: number;
  }>> => {
    try {
      const res = await api.get('/admin/transactions', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching transactions:', error);
      throw error;
    }
  },

  // ==================== ADMIN REFERRAL ====================
  adminGetReferralSettings: async (): Promise<ApiResponse<{
    id: number;
    referral_code_prefix: string;
    referral_code_length: number;
    referral_levels: number;
    commission_distribution: Record<number, number>;
    customer_discount_enabled: boolean;
    customer_discount_type: 'percentage' | 'fixed';
    customer_discount_value: number;
  }>> => {
    try {
      console.log('📊 Admin fetching referral settings...');
      const res = await api.get('/admin/referral/settings');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching referral settings:', error);
      throw error;
    }
  },

  adminUpdateReferralSettings: async (data: {
    referral_code_prefix: string;
    referral_code_length: number;
    referral_levels: number;
    commission_distribution: Record<string, number>;
    customer_discount_enabled: boolean;
    customer_discount_type: string;
    customer_discount_value: number;
  }): Promise<ApiResponse<any>> => {
    try {
      console.log('📤 Updating referral settings with data:', data);
      const res = await api.put('/admin/referral/settings', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating referral settings:', error);
      throw error;
    }
  },

  adminGetEmployeeReferrals: async (search?: string, page?: number, pageSize?: number): Promise<ApiResponse<{
    id: number;
    employee_code: string;
    employee_name: string;
    email: string;
    department: string;
    referral_code: string;
    referral_link: string;
    total_referrals: number;
    successful_referrals: number;
    total_earnings: number;
    join_date: string;
    status: string;
  }[]> & { totals?: {
    total_employees: number;
    total_referrals: number;
    total_successful: number;
    total_earnings: number;
    conversion_rate: number;
  }, pagination?: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  } }> => {
    try {
      console.log('📊 Admin fetching employee referrals...');
      const params: any = {};
      if (search) params.search = search;
      if (page) params.page = page;
      if (pageSize) params.page_size = pageSize;
      
      const res = await api.get('/admin/referral/employees', { params });
      console.log('📦 Admin referrals API response:', res.data);
      
      if (res.data.success && res.data.data) {
        res.data.data = res.data.data.map((emp: any) => ({
          ...emp,
          employee_code: emp.employee_code || getEmployeeCode(emp.id)
        }));
      }
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching employee referrals:', error);
      throw error;
    }
  },

  adminRegenerateReferralCode: async (employeeId: number): Promise<ApiResponse<{
    referral_code: string;
    referral_link: string;
  }>> => {
    try {
      console.log(`🔄 Admin regenerating referral code for employee ${employeeId}...`);
      const res = await api.post(`/admin/referral/employees/${employeeId}/regenerate-code`);
      return res.data;
    } catch (error) {
      console.error('❌ Error regenerating referral code:', error);
      throw error;
    }
  },

  // ==================== REFERRAL VALIDATION (PUBLIC) ====================
  validateReferralCode: async (code: string): Promise<ApiResponse<ReferralValidationResponse>> => {
    try {
      console.log('🔍 Validating referral code:', code);
      const res = await api.post('/referral/validate', { code });
      return res.data;
    } catch (error) {
      console.error('❌ Error validating referral code:', error);
      throw error;
    }
  },

  // ==================== ADMIN PRODUCTS ====================
  // Product picker for the Product Share page (admin: /admin/products, employee: needs product_share permission)
  getProductsForShare: async (params?: any): Promise<ApiPaginatedResponse<Product>> => {
    const res = await api.get(shareUrl('/products', '/products'), { params });
    return res.data;
  },

  getAllProductsAdmin: async (params?: any): Promise<ApiPaginatedResponse<Product>> => { 
    try {
      const res = await api.get('/admin/products', { params }); 
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== ✨ SKU AVAILABILITY CHECK (NEW) ====================
  // Lightweight endpoint for real-time SKU validation
  // Usage: checkSkuAvailability('PROD-123') or checkSkuAvailability('PROD-123', 5)
  checkSkuAvailability: async (sku: string, excludeId?: number): Promise<SkuAvailabilityResponse> => {
    try {
      const params: any = { sku };
      if (excludeId) {
        params.exclude_id = excludeId;
      }
      console.log(`🔍 Checking SKU availability: ${sku}${excludeId ? ` (excluding ID ${excludeId})` : ''}`);
      const res = await api.get('/admin/products/check-sku', { params });
      console.log('🔍 SKU check response:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('❌ Error checking SKU availability:', error);
      return {
        success: false,
        available: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to check SKU'
      };
    }
  },
  // =========================================================================
  
  createProduct: async (data: any): Promise<ApiResponse<Product>> => { 
    try {
      const res = await api.post('/admin/products', data); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  updateProduct: async (id: number, data: any): Promise<ApiResponse<Product>> => { 
    try {
      const res = await api.put(`/admin/products/${id}`, data); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  deleteProduct: async (id: number): Promise<ApiResponse<{ message: string }>> => { 
    try {
      const res = await api.delete(`/admin/products/${id}`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },
  
  toggleProductStatus: async (id: number): Promise<ApiResponse<Product>> => { 
    try {
      const res = await api.patch(`/admin/products/${id}/toggle`); 
      return res.data; 
    } catch (error) {
      throw error;
    }
  },

  getLowStockProducts: async (params?: any): Promise<ApiPaginatedResponse<Product>> => {
    try {
      const res = await api.get('/admin/products/low-stock', { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  bulkUpdateStock: async (updates: Array<{id: number, stock: number}>): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.post('/admin/products/bulk-stock', updates);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // 🔗 ADMIN: one shareable link for a set of selected products.
  // Same set of products => same code (backend is idempotent).
  createProductShareLink: async (
    productIds: number[],
    reorder: boolean = false
  ): Promise<ApiResponse<{ code: string; path: string; product_count: number; created: boolean; order_updated?: boolean }>> => {
    try {
      const res = await api.post(shareUrl('/products/share-link', '/share-link'), { product_ids: productIds, reorder });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // 🔗 ADMIN: list / load / edit / delete the share links that were created.
  getProductShareLinks: async (params?: { page?: number; limit?: number }): Promise<{
    success: boolean;
    data: ProductShareLinkItem[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
  }> => {
    const res = await api.get(shareUrl('/products/share-links', '/share-links'), { params });
    return res.data;
  },

  getProductShareLink: async (id: number): Promise<ApiResponse<ProductShareLinkItem & { products: ProductShareLinkProduct[] }>> => {
    const res = await api.get(shareUrl(`/products/share-links/${id}`, `/share-links/${id}`));
    return res.data;
  },

  updateProductShareLink: async (id: number, productIds: number[]): Promise<ApiResponse<ProductShareLinkItem>> => {
    const res = await api.put(shareUrl(`/products/share-links/${id}`, `/share-links/${id}`), { product_ids: productIds });
    return res.data;
  },

  deleteProductShareLink: async (id: number): Promise<ApiResponse<{ id: number }>> => {
    const res = await api.delete(shareUrl(`/products/share-links/${id}`, `/share-links/${id}`));
    return res.data;
  },

  // 📱 PUBLIC (shared product link): log in with ONLY the mobile number - no OTP.
  // New numbers get a phone-only customer account and are logged in at once. A number that
  // already belongs to a registered account is NOT logged in (otpRequired = true) - the caller
  // then falls back to the OTP step. The OTP for new numbers is asked later, on "Pay Later".
  loginWithShareLinkPhone: async (payload: {
    phone: string;
    product_id?: number;
    share_code?: string;
  }): Promise<{
    success: boolean;
    loggedIn?: boolean;
    otpRequired?: boolean;
    isNewAccount?: boolean;
    mustChangePassword?: boolean;
    user?: User;
    message?: string;
    error?: string;
  }> => {
    try {
      const res = await api.post('/product-share/login', payload);
      const d = res.data?.data;
      if (res.data?.success && d) {
        if (d.otp_required) {
          return { success: true, loggedIn: false, otpRequired: true, message: res.data.message };
        }
        if (d.logged_in && d.token && d.user) {
          localStorage.setItem('token', d.token);
          localStorage.setItem('user', JSON.stringify(d.user));
          api.defaults.headers.common['Authorization'] = `Bearer ${d.token}`;
          return { success: true, loggedIn: true, isNewAccount: !!d.is_new_account, user: d.user };
        }
        return { success: true, loggedIn: false, mustChangePassword: !!d.must_change_password, message: res.data.message };
      }
      return { success: false, error: res.data?.message || 'Login failed' };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Login failed. Please try again.',
      };
    }
  },

  // 💳 "Pay Later" (COD): sends an OTP to the customer's login number when that number has not
  // been verified yet (shared-link customers). required=false -> nothing sent, order can go ahead.
  sendCodOrderOTP: async (): Promise<{
    success: boolean;
    required?: boolean;
    phone?: string;
    phoneMasked?: string;
    message?: string;
    error?: string;
  }> => {
    try {
      const res = await api.post('/orders/cod-otp/send');
      const d = res.data?.data;
      if (res.data?.success) {
        return { success: true, required: !!d?.required, phone: d?.phone, phoneMasked: d?.phone_masked, message: res.data?.message };
      }
      return { success: false, error: res.data?.message || 'Failed to send confirmation code' };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to send OTP. Please try again.',
      };
    }
  },

  verifyCodOrderOTP: async (otp: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await api.post('/orders/cod-otp/verify', { otp });
      return { success: !!res.data?.success, error: res.data?.success ? undefined : (res.data?.message || 'Invalid confirmation code') };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid confirmation code',
      };
    }
  },

  // 📱 PUBLIC (shared product link): send an OTP to the visitor's mobile number.
  sendProductShareOTP: async (phone: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const res = await api.post('/product-share/send-otp', { phone });
      return { success: !!res.data?.success, message: res.data?.message };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to send OTP',
      };
    }
  },

  // 📱 PUBLIC (shared product link): verify the OTP and log the visitor in automatically.
  // New numbers get a phone-only customer account. On success the token + user are stored
  // exactly like a normal OTP login.
  verifyProductShareOTP: async (payload: {
    phone: string;
    otp: string;
    product_id?: number;
    share_code?: string;
  }): Promise<{
    success: boolean;
    loggedIn?: boolean;
    isNewAccount?: boolean;
    mustChangePassword?: boolean;
    user?: User;
    message?: string;
    error?: string;
  }> => {
    try {
      const res = await api.post('/product-share/verify-otp', payload);
      const d = res.data?.data;
      if (res.data?.success && d) {
        if (d.logged_in && d.token && d.user) {
          localStorage.setItem('token', d.token);
          localStorage.setItem('user', JSON.stringify(d.user));
          api.defaults.headers.common['Authorization'] = `Bearer ${d.token}`;
          return { success: true, loggedIn: true, isNewAccount: !!d.is_new_account, user: d.user };
        }
        return { success: true, loggedIn: false, mustChangePassword: !!d.must_change_password, message: res.data.message };
      }
      return { success: false, error: res.data?.message || 'Verification failed' };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid OTP',
      };
    }
  },

  // 📱 ADMIN: mobile numbers collected from shared links.
  getProductShareLeads: async (params?: { page?: number; limit?: number; search?: string }): Promise<{
    success: boolean;
    data: Array<{ id: number; phone: string; product_id?: number; product_name?: string; share_code?: string; new_account?: boolean; created_at: string }>;
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
  }> => {
    const res = await api.get(shareUrl('/product-share/leads', '/leads'), { params });
    return res.data;
  },

  uploadProductImages: async (formData: FormData): Promise<{ success: boolean; urls: string[]; error?: string }> => {
    try {
      const res = await api.post('/admin/products/upload-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, urls: [], error: (error.response?.data as any)?.error || 'Failed to upload images' };
      }
      return { success: false, urls: [], error: 'Failed to upload images' };
    }
  },

  uploadProductVideos: async (formData: FormData): Promise<{ success: boolean; urls: string[]; error?: string }> => {
    try {
      const res = await api.post('/admin/products/upload-videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, urls: [], error: (error.response?.data as any)?.error || 'Failed to upload videos' };
      }
      return { success: false, urls: [], error: 'Failed to upload videos' };
    }
  },

  uploadSizeChart: async (formData: FormData): Promise<{ success: boolean; url?: string; urls?: string[]; error?: string }> => {
    try {
      console.log('📊 Uploading size chart...');
      const res = await api.post('/admin/products/upload-size-chart', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      console.log('📊 Size chart upload response:', res.data);
      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('❌ Size chart upload error:', error.response?.data);
        return { success: false, error: (error.response?.data as any)?.error || 'Failed to upload size chart' };
      }
      return { success: false, error: 'Failed to upload size chart' };
    }
  },

  // ==================== ADMIN DASHBOARD ====================
  getDashboardStats: async (): Promise<ApiResponse<{
    totalEmployees: number;
    totalDealers: number;
    walletBalance: number;
    pendingPayouts: number;
    totalSales?: number;
    totalOrders?: number;
    totalProducts?: number;
    totalCategories?: number;
  }>> => {
    try {
      let response;
      try {
        response = await api.get('/admin/dashboard/stats');
        if (response.data && response.data.success) {
          return response.data;
        }
      } catch (e) {
        console.log('Trying alternative endpoint...');
      }
      
      try {
        response = await api.get('/admin/stats/dashboard');
        if (response.data && response.data.success) {
          return response.data;
        }
      } catch (e) {
        console.log('Trying alternative endpoint...');
      }
      
      try {
        response = await api.get('/admin/dashboard');
        if (response.data && response.data.success) {
          return response.data;
        }
      } catch (e) {
        console.log('Trying alternative endpoint...');
      }
      
      console.warn('No dashboard stats endpoint found, using defaults');
      return {
        success: true,
        data: {
          totalEmployees: 0,
          totalDealers: 0,
          walletBalance: 0,
          pendingPayouts: 0
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        success: true,
        data: {
          totalEmployees: 0,
          totalDealers: 0,
          walletBalance: 0,
          pendingPayouts: 0
        }
      };
    }
  },

  getProductStats: async (): Promise<ApiResponse<any>> => {
    try {
      const res = await api.get('/admin/stats/products');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== ADMIN ORDERS ====================
  getAllOrdersAdmin: async (params?: any): Promise<ApiPaginatedResponse<ExtendedApiOrder>> => {
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      let endpoint = '/admin/orders';
      
      if (user?.role === 'employee') {
        endpoint = '/employee/all-orders';
        console.log('📦 Employee user - using /employee/all-orders endpoint');
      } else {
        console.log('📦 Admin user - using /admin/orders endpoint');
      }
      
      const res = await api.get(endpoint, { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching orders:', error);
      throw error;
    }
  },

  getReturnRequests: async (params?: { status?: string; page?: number; page_size?: number }): Promise<ApiPaginatedResponse<ExtendedApiOrder>> => {
    try {
      console.log('🔄 Fetching return requests with params:', params);
      const res = await api.get('/admin/return-requests', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching return requests:', error);
      throw error;
    }
  },

  getOrderAdmin: async (id: number): Promise<ApiResponse<ExtendedApiOrder>> => {
    try {
      const res = await api.get(`/admin/orders/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateOrderStatus: async (id: number, status: string, data?: {
    delivery_start_date?: string;
    delivery_end_date?: string;
    tracking_id?: string;
    courier_name?: string;
    awb_number?: string;
    return_start_date?: string;
    return_end_date?: string;
    return_tracking_id?: string;
    return_courier_name?: string;
    return_awb_number?: string;
    refund_amount?: number;
    refund_method?: string;
    refund_transaction_id?: string;
    return_admin_notes?: string;
    admin_note?: string;
  }): Promise<ApiResponse<ExtendedApiOrder>> => {
    try {
      const payload: any = { status };
      if (data) {
        Object.keys(data).forEach(key => {
          if (data[key as keyof typeof data] !== undefined) {
            payload[key] = data[key as keyof typeof data];
          }
        });
      }
      const res = await api.put(`/admin/orders/${id}/status`, payload);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  approveReturn: async (id: number, data: { return_start_date: string; return_end_date: string; admin_note?: string; }): Promise<ApiResponse<ExtendedApiOrder>> => {
    try {
      console.log('✅ Approving return for order:', id, data);
      const res = await api.post(`/admin/orders/${id}/approve-return`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Approve return error:', error);
      throw error;
    }
  },

  updateReturnStatus: async (id: number, data: {
    status: string;
    return_tracking_id?: string;
    return_courier_name?: string;
    return_awb_number?: string;
    admin_note?: string;
    refund_amount?: number;
    refund_method?: string;
    refund_transaction_id?: string;
  }): Promise<ApiResponse<ExtendedApiOrder>> => {
    try {
      console.log('🔄 Updating return status for order:', id, data);
      const res = await api.put(`/admin/orders/${id}/return-status`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Update return status error:', error);
      throw error;
    }
  },

  getReturnDetails: async (id: number): Promise<ApiResponse<any>> => {
    try {
      console.log('📦 Fetching return details for order:', id);
      const res = await api.get(`/admin/orders/${id}/return-details`);
      return res.data;
    } catch (error) {
      console.error('❌ Get return details error:', error);
      throw error;
    }
  },

  // ==================== INVENTORY PURCHASES ====================
  getPurchases: async (params?: any): Promise<ApiPaginatedResponse<InventoryPurchase>> => {
    try {
      const res = await api.get('/admin/inventory/purchases', { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  createPurchase: async (data: {
    product: number;
    invoiceNumber: string;
    purchaseDate: string;
    purchaseQuantity: number;
    damageQuantity: number;
    notes: string;
  }): Promise<ApiResponse<InventoryPurchase>> => {
    try {
      const payload = {
        product: data.product,
        invoiceNumber: data.invoiceNumber,
        purchaseDate: data.purchaseDate,
        purchaseQuantity: data.purchaseQuantity,
        damageQuantity: data.damageQuantity,
        notes: data.notes || ""
      };
      const res = await api.post('/admin/inventory/purchases', payload);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updatePurchase: async (id: number, data: {
    product: number;
    invoiceNumber: string;
    purchaseDate: string;
    purchaseQuantity: number;
    damageQuantity: number;
    notes: string;
  }): Promise<ApiResponse<InventoryPurchase>> => {
    try {
      const payload = {
        product: data.product,
        invoiceNumber: data.invoiceNumber,
        purchaseDate: data.purchaseDate,
        purchaseQuantity: data.purchaseQuantity,
        damageQuantity: data.damageQuantity,
        notes: data.notes || ""
      };
      const res = await api.put(`/admin/inventory/purchases/${id}`, payload);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deletePurchase: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`/admin/inventory/purchases/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getPurchaseStats: async (): Promise<ApiResponse<PurchaseStats>> => {
    try {
      const res = await api.get('/admin/inventory/purchases/stats');
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getPurchaseById: async (id: number): Promise<ApiResponse<InventoryPurchase>> => {
    try {
      const res = await api.get(`/admin/inventory/purchases/${id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getProductPurchaseHistory: async (productId: number): Promise<ApiResponse<any>> => {
    try {
      const res = await api.get(`/admin/inventory/products/${productId}/history`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== EMPLOYEE INVENTORY PURCHASES ====================
  employeeGetPurchases: async (params?: any): Promise<ApiPaginatedResponse<InventoryPurchase>> => {
    try {
      console.log('👤 Employee fetching purchases...');
      const res = await api.get('/employee/inventory/purchases', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Employee get purchases error:', error);
      throw error;
    }
  },

  employeeCreatePurchase: async (data: {
    product_id: number;
    invoiceNumber: string;
    purchaseDate: string;
    purchaseQuantity: number;
    damageQuantity: number;
    notes: string;
  }): Promise<ApiResponse<InventoryPurchase>> => {
    try {
      console.log('👤 Employee creating purchase:', data);
      const payload = {
        product_id: data.product_id,
        invoice_number: data.invoiceNumber,
        purchase_date: data.purchaseDate,
        purchase_quantity: data.purchaseQuantity,
        damage_quantity: data.damageQuantity,
        notes: data.notes || ""
      };
      const res = await api.post('/employee/inventory/purchases', payload);
      return res.data;
    } catch (error) {
      console.error('❌ Employee create purchase error:', error);
      throw error;
    }
  },

  employeeUpdatePurchase: async (id: number, data: {
    product_id: number;
    invoiceNumber: string;
    purchaseDate: string;
    purchaseQuantity: number;
    damageQuantity: number;
    notes: string;
  }): Promise<ApiResponse<InventoryPurchase>> => {
    try {
      console.log(`👤 Employee updating purchase ${id}:`, data);
      const payload = {
        product_id: data.product_id,
        invoice_number: data.invoiceNumber,
        purchase_date: data.purchaseDate,
        purchase_quantity: data.purchaseQuantity,
        damage_quantity: data.damageQuantity,
        notes: data.notes || ""
      };
      const res = await api.put(`/employee/inventory/purchases/${id}`, payload);
      return res.data;
    } catch (error) {
      console.error('❌ Employee update purchase error:', error);
      throw error;
    }
  },

  employeeDeletePurchase: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`👤 Employee deleting purchase ${id}...`);
      const res = await api.delete(`/employee/inventory/purchases/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Employee delete purchase error:', error);
      throw error;
    }
  },

  employeeGetPurchaseStats: async (): Promise<ApiResponse<PurchaseStats>> => {
    try {
      console.log('👤 Employee fetching purchase stats...');
      const res = await api.get('/employee/inventory/purchases/stats');
      return res.data;
    } catch (error) {
      console.error('❌ Employee get purchase stats error:', error);
      throw error;
    }
  },

  // ==================== ADMIN REPORTS ====================
  getReports: async (period: string = '30'): Promise<ApiResponse<ReportResponse>> => {
    try {
      console.log(`📊 Fetching reports for period: ${period} days`);
      const res = await api.get('/admin/reports', { params: { period } });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching reports:', error);
      throw error;
    }
  },

  // ==================== COD REMITTANCE (admin) ====================
  getCodRemittances: async (params: Record<string, any>): Promise<any> => {
    try {
      const res = await api.get(codRemUrl(''), { params });
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        summary: null,
        pagination: { page: 1, page_size: 20, total: 0, total_pages: 0 },
        error: error.response?.data?.error || 'Failed to load COD remittance',
      };
    }
  },

  exportCodRemittances: async (params: Record<string, any>): Promise<Blob> => {
    const res = await api.get(codRemUrl('/export'), { params, responseType: 'blob' });
    return res.data as Blob;
  },

  importCodRemittanceCSV: async (file: File, scheduledDate?: string): Promise<any> => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (scheduledDate) fd.append('scheduled_date', scheduledDate);
      const res = await api.post(codRemUrl('/import'), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Import failed' };
    }
  },

  syncCodRemittanceFromShiprocket: async (): Promise<any> => {
    try {
      const res = await api.post(codRemUrl('/sync'));
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Sync failed' };
    }
  },

  updateCodRemittanceShiprocket: async (id: number, payload: {
    status: string;
    remittance_scheduled?: string;
    utr?: string;
    remitted_amount?: number;
    remitted_date?: string;
    shiprocket_fees?: number;
  }): Promise<any> => {
    try {
      const res = await api.put(codRemUrl(`/${id}/shiprocket`), payload);
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Could not update' };
    }
  },

  markCodRemittanceReceived: async (id: number, payload: { bank_date: string; amount: number; note?: string }): Promise<any> => {
    try {
      const res = await api.post(codRemUrl(`/${id}/mark-received`), payload);
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Could not update' };
    }
  },

  disputeCodRemittance: async (id: number, note: string): Promise<any> => {
    try {
      const res = await api.post(codRemUrl(`/${id}/dispute`), { note });
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Could not update' };
    }
  },

  resetCodRemittance: async (id: number): Promise<any> => {
    try {
      const res = await api.post(codRemUrl(`/${id}/reset`));
      return res.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Could not update' };
    }
  },

  // ==================== RTO METHODS ====================

  getRTOOrders: async (params?: RtoFilters): Promise<{
    success: boolean;
    data: RtoOrder[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
    stats: RtoStats;
    error?: string;
  }> => {
    try {
      console.log('🚚 Fetching RTO orders with params:', params);
      
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let endpoint = '/admin/rto/orders';
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.role === 'employee') {
            endpoint = '/employee/rto/orders';
            console.log('🚚 Employee user - using /employee/rto/orders endpoint');
          }
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      const res = await api.get(endpoint, { params });
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching RTO orders:', error);
      return {
        success: false,
        data: [],
        pagination: { page: 1, page_size: 10, total: 0, total_pages: 0 },
        stats: { intransit: 0, delivered: 0, disputes: 0 },
        error: error.response?.data?.error || 'Failed to fetch RTO orders'
      };
    }
  },

  getRTOOrderById: async (id: number): Promise<{
    success: boolean;
    data?: RtoOrder;
    error?: string;
  }> => {
    try {
      console.log(`🚚 Fetching RTO order ${id}...`);
      
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let endpoint = `/admin/rto/orders/${id}`;
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.role === 'employee') {
            endpoint = `/employee/rto/orders/${id}`;
            console.log(`🚚 Employee user - using /employee/rto/orders/${id} endpoint`);
          }
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      const res = await api.get(endpoint);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch order'
      };
    }
  },

  updateRTOStatus: async (id: number, data: {
    status?: RtoTab;
    delivery_status?: string;
    admin_notes?: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`🚚 Updating RTO order ${id}:`, data);
      const res = await api.patch(`/admin/rto/orders/${id}/status`, data);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update status'
      };
    }
  },

  disputeRTO: async (id: number, data: {
    dispute_reason: string;
    admin_notes?: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`🚚 Raising dispute for RTO order ${id}:`, data);
      const res = await api.post(`/admin/rto/orders/${id}/dispute`, data);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to raise dispute'
      };
    }
  },

  downloadRTOReport: async (params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
  }): Promise<Blob> => {
    try {
      console.log('📄 Downloading RTO report...');
      const res = await api.get('/admin/rto/report', {
        params,
        responseType: 'blob'
      });
      return res.data;
    } catch (error) {
      console.error('❌ Error downloading report:', error);
      throw error;
    }
  },

  // ==================== NDR METHODS ====================

  getNDROrders: async (params?: NdrFilters): Promise<{
    success: boolean;
    data: NdrOrder[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
    stats: NdrStats;
    error?: string;
  }> => {
    try {
      console.log('📋 Fetching NDR orders with params:', params);
      
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let endpoint = '/admin/ndr/orders';
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.role === 'employee') {
            endpoint = '/employee/ndr/orders';
            console.log('📋 Employee user - using /employee/ndr/orders endpoint');
          }
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      const res = await api.get(endpoint, { params });
      return res.data;
    } catch (error: any) {
      console.error('❌ Error fetching NDR orders:', error);
      return {
        success: false,
        data: [],
        pagination: { page: 1, page_size: 10, total: 0, total_pages: 0 },
        stats: { total: 0, pending: 0, delivered: 0, rto: 0 },
        error: error.response?.data?.error || 'Failed to fetch NDR orders'
      };
    }
  },

  getNDROrderById: async (id: number): Promise<{
    success: boolean;
    data?: NdrOrder;
    error?: string;
  }> => {
    try {
      console.log(`📋 Fetching NDR order ${id}...`);
      
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let endpoint = `/admin/ndr/orders/${id}`;
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.role === 'employee') {
            endpoint = `/employee/ndr/orders/${id}`;
            console.log(`📋 Employee user - using /employee/ndr/orders/${id} endpoint`);
          }
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      const res = await api.get(endpoint);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch order'
      };
    }
  },

  // Pull NDR shipments from Shiprocket right now (admin only; also runs automatically every ~15 min)
  syncNDRFromShiprocket: async (): Promise<{ success: boolean; updated?: number; error?: string }> => {
    try {
      const res = await api.post('/admin/ndr/sync');
      return { success: !!res.data?.success, updated: res.data?.data?.updated ?? 0 };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to sync NDR from Shiprocket'
      };
    }
  },

  updateNDRAction: async (id: number, data: {
    action: 'reattempt' | 'rto';
    admin_notes?: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      console.log(`📋 Updating NDR action for order ${id}:`, data);
      
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let endpoint = `/admin/ndr/orders/${id}/action`;
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.role === 'employee') {
            endpoint = `/employee/ndr/orders/${id}/action`;
            console.log('📋 Employee user - using employee endpoint for action');
          }
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      const res = await api.post(endpoint, data);
      return res.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update action'
      };
    }
  },

  downloadNDRReport: async (params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
  }): Promise<Blob> => {
    try {
      console.log('📄 Downloading NDR report...');
      const res = await api.get('/admin/ndr/report', {
        params,
        responseType: 'blob'
      });
      return res.data;
    } catch (error) {
      console.error('❌ Error downloading report:', error);
      throw error;
    }
  },

  // ==================== WAREHOUSE / PICKUP ADDRESS METHODS ====================
  getPickupAddresses: async (): Promise<ApiResponse<PickupAddress[]>> => {
    try {
      const res = await api.get(`${whBase()}/pickup-addresses`);
      return res.data;
    } catch (error) {
      console.error('❌ Get pickup addresses error:', error);
      throw error;
    }
  },

  getEmployeePickupAddresses: async (): Promise<ApiResponse<PickupAddress[]>> => {
    try {
      console.log('👤 Employee fetching pickup addresses...');
      const res = await api.get('/employee/pickup-addresses');
      return res.data;
    } catch (error) {
      console.error('❌ Employee get pickup addresses error:', error);
      return { success: false, data: [] as PickupAddress[], error: 'Failed to fetch warehouses' };
    }
  },

  getPickupAddress: async (id: string): Promise<ApiResponse<PickupAddress>> => {
    try {
      const res = await api.get(`${whBase()}/pickup-addresses/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Get pickup address error:', error);
      throw error;
    }
  },

  createPickupAddress: async (data: Omit<PickupAddress, 'id' | 'createdAt'>): Promise<ApiResponse<PickupAddress>> => {
    try {
      console.log('🏭 Creating pickup address:', data);
      const res = await api.post(`${whBase()}/pickup-addresses`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Create pickup address error:', error);
      throw error;
    }
  },

  updatePickupAddress: async (id: string, data: Partial<PickupAddress>): Promise<ApiResponse<PickupAddress>> => {
    try {
      console.log(`🏭 Updating pickup address ${id}:`, data);
      const res = await api.put(`${whBase()}/pickup-addresses/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Update pickup address error:', error);
      throw error;
    }
  },

  deletePickupAddress: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      const res = await api.delete(`${whBase()}/pickup-addresses/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Delete pickup address error:', error);
      throw error;
    }
  },

  togglePickupAddressStatus: async (id: string): Promise<ApiResponse<PickupAddress>> => {
    try {
      const res = await api.patch(`${whBase()}/pickup-addresses/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Toggle pickup address status error:', error);
      throw error;
    }
  },

  bulkUpdatePickupAddressStatus: async (ids: string[], isActive: boolean): Promise<ApiResponse<{ updatedCount: number }>> => {
    try {
      const res = await api.patch(`${whBase()}/pickup-addresses/bulk/status`, { ids, isActive });
      return res.data;
    } catch (error) {
      console.error('❌ Bulk update pickup address status error:', error);
      throw error;
    }
  },

  bulkDeletePickupAddresses: async (ids: string[]): Promise<ApiResponse<{ deletedCount: number }>> => {
    try {
      const res = await api.delete(`${whBase()}/pickup-addresses/bulk`, { data: { ids } });
      return res.data;
    } catch (error) {
      console.error('❌ Bulk delete pickup addresses error:', error);
      throw error;
    }
  },

  getWarehousePreferences: async (): Promise<ApiResponse<WarehousePreferences>> => {
    try {
      const res = await api.get(`${whPrefBase()}/preferences`);
      return res.data;
    } catch (error) {
      console.error('❌ Get warehouse preferences error:', error);
      throw error;
    }
  },

  updateWarehousePreferences: async (data: WarehousePreferences): Promise<ApiResponse<WarehousePreferences>> => {
    try {
      console.log('⚙️ Updating warehouse preferences:', data);
      const res = await api.put(`${whPrefBase()}/preferences`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Update warehouse preferences error:', error);
      throw error;
    }
  },

  getRTOSettings: async (): Promise<ApiResponse<RTOSettings>> => {
    try {
      const res = await api.get(whRtoUrl());
      return res.data;
    } catch (error) {
      console.error('❌ Get RTO settings error:', error);
      throw error;
    }
  },

  updateRTOSettings: async (data: RTOSettings): Promise<ApiResponse<RTOSettings>> => {
    try {
      console.log('🚚 Updating RTO settings:', data);
      const res = await api.put(whRtoUrl(), data);
      return res.data;
    } catch (error) {
      console.error('❌ Update RTO settings error:', error);
      throw error;
    }
  },

  getWarehouseStats: async (): Promise<ApiResponse<{
    total: number;
    active: number;
    inactive: number;
    suppliers: number;
    customRTO: number;
    cities: Array<{ city: string; count: number }>;
  }>> => {
    try {
      const res = await api.get(`${whPrefBase()}/stats`);
      return res.data;
    } catch (error) {
      console.error('❌ Get warehouse stats error:', error);
      throw error;
    }
  },

  // ==================== ANNOUNCEMENTS ====================
  
  getAnnouncements: async (params?: { page?: number; page_size?: number; is_active?: boolean }): Promise<ApiPaginatedResponse<Announcement>> => {
    try {
      console.log('📢 Fetching announcements with params:', params);
      const res = await api.get('/admin/announcements', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching announcements:', error);
      throw error;
    }
  },

  getActiveAnnouncements: async (): Promise<ApiResponse<Announcement[]>> => {
    try {
      console.log('📢 Fetching active announcements for frontend...');
      const res = await api.get('/announcements/active');
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching active announcements:', error);
      throw error;
    }
  },

  getAnnouncementById: async (id: number): Promise<ApiResponse<Announcement>> => {
    try {
      console.log(`📢 Fetching announcement ${id}...`);
      const res = await api.get(`/admin/announcements/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching announcement:', error);
      throw error;
    }
  },

  createAnnouncement: async (data: Omit<Announcement, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Announcement>> => {
    try {
      console.log('📢 Creating announcement:', data);
      const res = await api.post('/admin/announcements', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error creating announcement:', error);
      throw error;
    }
  },

  updateAnnouncement: async (id: number, data: Partial<Omit<Announcement, 'id' | 'created_at' | 'updated_at'>>): Promise<ApiResponse<Announcement>> => {
    try {
      console.log(`📢 Updating announcement ${id}:`, data);
      const res = await api.put(`/admin/announcements/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating announcement:', error);
      throw error;
    }
  },

  deleteAnnouncement: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`📢 Deleting announcement ${id}...`);
      const res = await api.delete(`/admin/announcements/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error deleting announcement:', error);
      throw error;
    }
  },

  toggleAnnouncementStatus: async (id: number): Promise<ApiResponse<Announcement>> => {
    try {
      console.log(`📢 Toggling announcement ${id} status...`);
      const res = await api.patch(`/admin/announcements/${id}/toggle`);
      return res.data;
    } catch (error) {
      console.error('❌ Error toggling announcement status:', error);
      throw error;
    }
  },

  reorderAnnouncements: async (announcements: Array<{ id: number; sort_order: number }>): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log('📢 Reordering announcements:', announcements);
      const res = await api.put('/admin/announcements/reorder', { announcements });
      return res.data;
    } catch (error) {
      console.error('❌ Error reordering announcements:', error);
      throw error;
    }
  },

  bulkDeleteAnnouncements: async (ids: number[]): Promise<ApiResponse<{ deletedCount: number }>> => {
    try {
      console.log(`📢 Bulk deleting announcements: ${ids.join(', ')}`);
      const res = await api.delete('/admin/announcements/bulk', { data: { ids } });
      return res.data;
    } catch (error) {
      console.error('❌ Error bulk deleting announcements:', error);
      throw error;
    }
  },

  bulkUpdateAnnouncementStatus: async (ids: number[], isActive: boolean): Promise<ApiResponse<{ updatedCount: number }>> => {
    try {
      console.log(`📢 Bulk updating ${ids.length} announcements status to ${isActive}`);
      const res = await api.patch('/admin/announcements/bulk/status', { ids, is_active: isActive });
      return res.data;
    } catch (error) {
      console.error('❌ Error bulk updating announcements status:', error);
      throw error;
    }
  },

  // ==================== PAYMENT CHARGES ====================

  getPaymentCharges: async (): Promise<{ 
    success: boolean; 
    exists: boolean; 
    data?: PaymentCharges; 
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('💰 Fetching payment charges configuration...');
      const res = await api.get('/admin/payment-charges');
      console.log('💰 Payment charges response:', res.data);
      
      if (res.data.success) {
        return {
          success: true,
          exists: true,
          data: res.data.data
        };
      }
      
      return {
        success: false,
        exists: false,
        message: res.data.message || 'Failed to fetch payment charges'
      };
    } catch (error: any) {
      console.error('❌ Error fetching payment charges:', error);
      
      if (error.response?.status === 404) {
        return {
          success: false,
          exists: false,
          message: error.response?.data?.message || 'Payment charges not configured yet'
        };
      }
      
      return {
        success: false,
        exists: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch payment charges'
      };
    }
  },

  // ==================== PUBLIC PAYMENT CHARGES (NO AUTH) ====================
  getPublicPaymentCharges: async (): Promise<{ 
    success: boolean; 
    exists: boolean; 
    data?: PaymentCharges; 
    message?: string;
    error?: string;
  }> => {
    try {
      console.log('💰 Fetching public payment charges for customer...');
      const res = await api.get('/payment-charges/public');
      console.log('💰 Public payment charges response:', res.data);
      
      if (res.data.success && res.data.exists && res.data.data) {
        return {
          success: true,
          exists: true,
          data: res.data.data
        };
      }
      
      return {
        success: false,
        exists: false,
        message: res.data.message || 'Payment charges not configured yet'
      };
    } catch (error: any) {
      console.error('❌ Error fetching public payment charges:', error);
      return {
        success: false,
        exists: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch payment charges'
      };
    }
  },

  createPaymentCharges: async (data: {
    codCharges: number;
    showCodToast: boolean;
    deliveryCharges: number;
    minCartValue: number;
    handlingCharges: number;
    smallCartCharges: number;
    smallCartLimit: number;
    onlineDiscountType: string;
    onlineDiscountValue: number;
    onlineMaxDiscount: number;
    partialCodDiscountType: string;
    partialCodDiscountValue: number;
    partialCodMaxDiscount: number;
    partialCodEnabled: boolean;
    partialCodAdvanceType: string;
    partialCodAdvanceValue: number;
    partialCodGateway: string;
    razorpayEnabled: boolean;
    payuEnabled: boolean;
    codEnabled: boolean;
  }): Promise<ApiResponse<PaymentCharges>> => {
    try {
      console.log('💰 Creating payment charges configuration:', data);
      const res = await api.post('/admin/payment-charges', data);
      console.log('💰 Create payment charges response:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Error creating payment charges:', error);
      throw error;
    }
  },

  updatePaymentCharges: async (data: {
    codCharges?: number;
    showCodToast?: boolean;
    deliveryCharges?: number;
    minCartValue?: number;
    handlingCharges?: number;
    smallCartCharges?: number;
    smallCartLimit?: number;
    onlineDiscountType?: string;
    onlineDiscountValue?: number;
    onlineMaxDiscount?: number;
    partialCodDiscountType?: string;
    partialCodDiscountValue?: number;
    partialCodMaxDiscount?: number;
    partialCodEnabled?: boolean;
    partialCodAdvanceType?: string;
    partialCodAdvanceValue?: number;
    partialCodGateway?: string;
    razorpayEnabled?: boolean;
    payuEnabled?: boolean;
  phonepeEnabled?: boolean;
    codEnabled?: boolean;
  }): Promise<ApiResponse<PaymentCharges>> => {
    try {
      console.log('💰 Updating payment charges configuration:', data);
      const res = await api.put('/admin/payment-charges', data);
      console.log('💰 Update payment charges response:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating payment charges:', error);
      throw error;
    }
  },

  // ==================== REPEAT USER COUPONS ====================

  createRepeatUserCoupon: async (data: {
    discount_type: string;
    discount_value: number;
    max_discount: number;
    expiry_days: number;
    whatsapp_sent: boolean;
    enabled: boolean;
  }): Promise<ApiResponse<RepeatUserCoupon>> => {
    try {
      console.log('🎫 Creating repeat user coupon:', data);
      const res = await api.post('/admin/repeat-user-coupons', data);
      return res.data;
    } catch (error) {
      console.error('❌ Error creating repeat user coupon:', error);
      throw error;
    }
  },

  getAllRepeatUserCoupons: async (enabled?: boolean): Promise<ApiResponse<RepeatUserCoupon[]>> => {
    try {
      const params = enabled !== undefined ? { enabled } : {};
      console.log('🎫 Fetching all repeat user coupons with params:', params);
      const res = await api.get('/admin/repeat-user-coupons', { params });
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching repeat user coupons:', error);
      throw error;
    }
  },

  getRepeatUserCouponById: async (id: number): Promise<ApiResponse<RepeatUserCoupon>> => {
    try {
      console.log(`🎫 Fetching repeat user coupon ${id}...`);
      const res = await api.get(`/admin/repeat-user-coupons/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error fetching repeat user coupon:', error);
      throw error;
    }
  },

  updateRepeatUserCoupon: async (id: number, data: {
    discount_type?: string;
    discount_value?: number;
    max_discount?: number;
    expiry_days?: number;
    whatsapp_sent?: boolean;
    enabled?: boolean;
  }): Promise<ApiResponse<RepeatUserCoupon>> => {
    try {
      console.log(`🎫 Updating repeat user coupon ${id}:`, data);
      const res = await api.put(`/admin/repeat-user-coupons/${id}`, data);
      return res.data;
    } catch (error) {
      console.error('❌ Error updating repeat user coupon:', error);
      throw error;
    }
  },

  deleteRepeatUserCoupon: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`🎫 Deleting repeat user coupon ${id}...`);
      const res = await api.delete(`/admin/repeat-user-coupons/${id}`);
      return res.data;
    } catch (error) {
      console.error('❌ Error deleting repeat user coupon:', error);
      throw error;
    }
  },

  toggleRepeatUserCouponStatus: async (id: number, enabled: boolean): Promise<ApiResponse<{ enabled: boolean }>> => {
    try {
      console.log(`🎫 Toggling repeat user coupon ${id} status to ${enabled}...`);
      const res = await api.patch(`/admin/repeat-user-coupons/${id}/toggle-status`, { enabled });
      return res.data;
    } catch (error) {
      console.error('❌ Error toggling repeat user coupon status:', error);
      throw error;
    }
  },

  resendCouponWhatsApp: async (couponId: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      console.log(`📱 Resending WhatsApp for coupon ${couponId}...`);
      const res = await api.post(`/admin/repeat-user-coupons/${couponId}/resend-whatsapp`);
      return res.data;
    } catch (error) {
      console.error('❌ Error resending WhatsApp:', error);
      throw error;
    }
  },

  validateCoupon: async (couponCode: string, orderTotal: number, userId: number): Promise<ApiResponse<{
    valid: boolean;
    coupon_code: string;
    discount: number;
    discount_type: string;
    message: string;
  }>> => {
    try {
      console.log(`🎫 Validating coupon ${couponCode} for user ${userId} with total ₹${orderTotal}...`);
      const res = await api.post('/validate-coupon', {
        coupon_code: couponCode,
        order_total: orderTotal,
        user_id: userId,
      });
      return res.data;
    } catch (error) {
      console.error('❌ Error validating coupon:', error);
      throw error;
    }
  },

  // ==================== DEBUG ====================
  debugCheckEndpoints: async () => {
    const endpoints = [
      '/admin/dashboard',
      '/admin/dashboard/stats', 
      '/admin/stats/dashboard',
      '/admin/dashboard'
    ];
    
    const results = [];
    for (const endpoint of endpoints) {
      try {
        const res = await api.get(endpoint);
        results.push({ endpoint, status: res.status, data: res.data });
      } catch (error: any) {
        results.push({ endpoint, error: error.message, status: error.response?.status });
      }
    }
    
    console.table(results);
    return results;
  },
};

// Export generic coupon API separately for convenience
export const genericCouponAPI = {
  create: apiService.createGenericCoupon,
  getAll: apiService.getAllGenericCoupons,
  getById: apiService.getGenericCouponById,
  getByCode: apiService.getGenericCouponByCode,
  update: apiService.updateGenericCoupon,
  delete: apiService.deleteGenericCoupon,
  toggleStatus: apiService.toggleGenericCouponStatus,
  validateAndApply: apiService.validateGenericCoupon,
  recordUsage: apiService.recordGenericCouponUsage,
  getStats: apiService.getGenericCouponStats,
};

// Export free gift coupon API separately for convenience
export const freeGiftCouponAPI = {
  create: apiService.createFreeGiftCoupon,
  getAll: apiService.getAllFreeGiftCoupons,
  getById: apiService.getFreeGiftCouponById,
  update: apiService.updateFreeGiftCoupon,
  delete: apiService.deleteFreeGiftCoupon,
  toggle: apiService.toggleFreeGiftCouponStatus,
  validate: apiService.validateFreeGiftCoupon,
  getActive: apiService.getActiveFreeGiftCoupons,
  recordUsage: apiService.recordFreeGiftCouponUsage,
};

// Export BXGY coupon API separately for convenience
export const bxgyCouponAPI = {
  create: apiService.createBXGYCoupon,
  getAll: apiService.getAllBXGYCoupons,
  getById: apiService.getBXGYCouponById,
  update: apiService.updateBXGYCoupon,
  delete: apiService.deleteBXGYCoupon,
  toggle: apiService.toggleBXGYCouponStatus,
  getStats: apiService.getBXGYCouponStats,
  validate: apiService.validateBXGYCoupon,
  recordUsage: apiService.recordBXGYCouponUsage,
  getActive: apiService.getActiveBXGYCoupons,
  getByCode: apiService.getBXGYCouponByCode,
};

// Export particular customer coupon API
export const particularCustomerCouponAPI = {
  getCustomers: apiService.getCustomers,
  assign: apiService.assignParticularCustomerCoupon,
  getAssignedCoupons: apiService.getParticularCustomerCoupons,
  delete: apiService.deleteParticularCustomerCoupon,
  getStats: apiService.getParticularCustomerCouponStats,
  getMyCoupons: async (): Promise<{ success: boolean; data?: any[]; error?: string }> => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await api.get('/customer/coupons');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error fetching customer coupons:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to fetch coupons' };
    }
  },
  validate: async (data: { coupon_code: string; order_total: number }): Promise<{ 
    success: boolean; 
    data?: { 
      discount_amount: number; 
      final_amount: number; 
      coupon: any;
    }; 
    error?: string 
  }> => {
    try {
      console.log(`🎫 Validating particular customer coupon ${data.coupon_code} for checkout...`);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await api.post('/customer/coupons/validate', {
        coupon_code: data.coupon_code,
        order_total: data.order_total
      });
      return response.data;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid coupon code' 
      };
    }
  },
  redeem: apiService.redeemParticularCustomerCoupon,
};

// Export post checkout offer API
export const postCheckoutOfferAPI = {
  getAll: apiService.getAllPostCheckoutOffers,
  getById: apiService.getPostCheckoutOfferById,
  create: apiService.createPostCheckoutOffer,
  update: apiService.updatePostCheckoutOffer,
  delete: apiService.deletePostCheckoutOffer,
  toggle: apiService.togglePostCheckoutOfferStatus,
  getActiveOffer: apiService.getActivePostCheckoutOffer,
  claimOffer: apiService.claimPostCheckoutOffer,
  trackOfferUsage: apiService.trackPostCheckoutOfferUsage,
  getUserOfferStatus: apiService.getUserPostCheckoutOfferStatus,
  getOfferUsageHistory: apiService.getPostCheckoutOfferUsageHistory,
};

// Export offers API
export const offersAPI = {
  getAll: apiService.getAllOffers,
  getActive: apiService.getActiveOffers,
  validate: apiService.validateOfferCode,
};

// Export customer API
export const customerAPI = {
  getAll: apiService.getAllCustomers,
  getById: apiService.getCustomerById,
  updateStatus: apiService.updateCustomerStatus,
  updateCallStatus: apiService.updateCustomerCallStatus,
  getOrders: apiService.getCustomerOrders,
  getWalletTransactions: apiService.getCustomerWalletTransactions,
};

// Export default apiService
export default apiService;
export { api };